// home.ts — the Home Screen (T1.8): AG-Grid Community releases list.
//
// Spec pins (phase1 plan, WS1 design "Home Screen"):
// - AG-Grid COMMUNITY, bundled locally via esbuild (npm dep — no CDN, no
//   Enterprise).
// - Columns, exactly and only, in order: name, release id (REL-nnn),
//   created, owner, source branch, merged-into branch, status (color pill),
//   fleet, throughput, framework, last commit, last VM activity, last
//   deploy, VM status (dot + No VM/Starting/Verifying/Running/Stopping/
//   Stopped — the release VM's row truth, owner request 2026-09-03), VM
//   uptime (counting upward live at 1 s from the vm_started_at stamp),
//   test-server status (dot + Offline/Deploying/Live, open button
//   when live — item 41), total cost, abort/reopen action cell (owner/admin
//   only) — plus a HIDDEN absolute-datetime variant beside each datetime
//   column, reachable from the header's column picker (item 41).
// - Cost is batch-fetched from WS7's GET /api/releases/cost-summary — the
//   ONE agreed cost read endpoint — and NULL-TOLERANT: "—" while WS7 is
//   unlanded, errors, or has no row — never $0.00. (The acceptance greps
//   this file for the deleted per-release cost-cache table's name: 0 hits —
//   no dead table is ever read.)
// - Slot indicator from GET /api/slots; at-cap reopen surfaces the
//   governor's counted used/total refusal verbatim.
// - Column state persists via ui_state(grid_preset, 'home') — the API is
//   T1.9's; this client degrades silently until it lands (404-tolerant).
//
// Pure decision surfaces (column defs, cost text, action visibility,
// refusal message, test-server link) are EXPORTED so the acceptance
// harness measures them without a browser.

import { mkFcToggle } from './form-controls.js';
import { mkChip } from './feedback-nav.js';
import {
  AllCommunityModule,
  ModuleRegistry,
  createGrid,
  themeQuartz,
  type ColDef,
  type GridApi,
} from 'ag-grid-community';
import { attachGridScrollRails } from 'pi-stack/client/scroll-rails.js';
import { gridThemeParams } from './appearance.js';
import { createRowStore } from './row-state.js';
import { icon } from './generated/icons.js';
import { renderGridToolbar, mountColumnPicker } from './grid-toolbar.js';
import { mkSearchBox } from './search.js';
import { iconBtn, mkIconBtn, setBtnGlyph, setBtnLabel, setBtnState } from './buttons.js';
import { mkFcSelect } from './form-controls.js';
import { registerRichTip } from './tooltip.js';
import { canCreate, mountShell, type Me } from './shell.js';
import { fetchRecents, renderRecents, type CardLive, type RecentRelease } from './recents.js';
import { renderCardList, watchPhone, type CardSpec } from './card-list.js';
import { withFlip } from './motion.js';
import { avatarNode } from './admin-ui.js';
import {
  confirmDialog as dlgConfirm,
  destroyDialog,
  noticeDialog as dlgNotice,
  openDialog,
  showDialogMsg,
} from './dialog.js';
import {
  ec2CostCell,
  totalCostCell,
  type Ec2CostEntry,
  type Ec2CostMap,
  type MoneyCell,
  type TotalComponent,
} from './cost-cell.js';

export type { Ec2CostEntry, Ec2CostMap, MoneyCell } from './cost-cell.js';
import { openCreateWired } from './create-dialog.js';
import { ackBody, ackVm, mountPreflight, type PreflightDto } from './template-preflight.js';
import { attachCtxHandler, copyPlainText, mountCtxMenu, openCtxMenu, type CtxMenuItem } from './ctxmenu.js';
import {
  anyColumnFilterActive,
  columnCopyText,
  displayValueFromDefs,
  effectivePins,
  parseGridSeatBlob,
  togglePin,
  withGridMachinery,
  type ColumnFilterMap,
  type ColumnFilterState,
} from './grid-machinery.js';
import {
  absoluteStamp, formatRelativeLong, formatUptime, formatVmActivity, parseStamp,
  startRelativeTimeTicker, startUptimeTicker,
} from './relative-time.js';
// The SAME sparkline the in-release Bridge overview strip draws, copied
// verbatim at prebuild by scripts/ensure-seismo.mjs (it has zero imports,
// which is what makes it portable). A lookalike would drift; this cannot.
import { seismoSvg, type SeriesPoint } from './generated/seismo.js';
// THE VM VERB TABLE (#1272), read straight from the server route surface —
// never re-guessed here. It is PURE BY CONSTRUCTION (its own file doctrine:
// no DB handle, no clock, no I/O — only an `import type` from
// fork-resume-gate.ts, erased at compile time), which is what makes it safe
// to bundle into the BROWSER: this import pulls in exactly vm-verb-table.js,
// nothing server-only behind it. actionsFor() below calls vmVerbFor() with
// the SAME vmCellOf() the server uses to read a release row, so the grid's
// button and the route that verb hits can never drift apart the way they did
// for REL-096 (`active` + a reaped VM offered only Abort — #1272).
import { vmCellOf, vmVerbFor, type VmCell, type VmVerbName } from '../server/vm-verb-table.js';
// #4101 — TYPE ONLY (erased at build): the offer is COMPUTED on the server
// and serialised in the fleet-versions snapshot. The client renders it and
// never re-derives it, so the label and the install cannot disagree.
import type { UpdateOffer } from '../server/framework-versions.js';
// The VM standing derivation, for this file's OWN call sites (the cell
// renderers below). Same module the block further down re-exports — one
// import, one copy. See that block's comment for why it is not here.
import {
  transitionStanding, vmStatusCell, vmUptimeCell, isCorpusFailure, type TransitionBudgets,
} from '../server/vm-standing.js';

ModuleRegistry.registerModules([AllCommunityModule]);

// ---------------------------------------------------------------------
// Row + context types
// ---------------------------------------------------------------------

/** #4199 P2 — the first-plan kick's standing, as GET /api/releases (every
 * row) and GET /api/releases/:id serve it: DERIVED server-side from the
 * audit rows, never a column. `owed` = the re-arm is armed (N failed, 0
 * kicked); `blocked` = the console STOPPED re-arming (a deterministic
 * refusal, or the ceiling) and `remedy` says what to fix before Retry;
 * `failure_class` null = a failure that predates #4635 (unclassified). */
export interface PlanKickInfo {
  state: 'kicked' | 'owed' | 'blocked' | 'none';
  failure_class: string | null;
  prior_failed_kicks: number;
  bridge_detail: string | null;
  blocked_at: string | null;
  last_failed_at: string | null;
  remedy: string | null;
  failed_since_retry: number;
  ceiling: number;
}

export interface ReleaseGridRow {
  id: string;
  name: string;
  slug: string;
  /** The corpus register id (REL-230). NULL until the branch cutter mints
   * it (ideating pre-cut, or an imported row) — rendered as an em dash. */
  rel_id?: string | null;
  status: 'ideating' | 'active' | 'completed' | 'aborted';
  /** Read-layer: what the release is DOING right now, or null at rest. */
  transition_state?: string | null;
  /** #3288 (migration 202609092150) — WHEN that transition started, stamped
   * in the same statement as the state it dates. Served by GET /api/releases
   * (the list projection is `rel.*`). Without it the grid renders `Starting`
   * forever with nothing to compare: REL-098's row read `Starting` from
   * 16:55Z to a hand SQL clear at 17:08:51Z on 2026-09-14 and the cell said
   * the same thing at second 1 and at minute 13. */
  transition_since?: string | null;
  /** Migration 068: the release is waiting for a VM slot (slot-queue.ts);
   * null when it is not. Renders as 'waiting for slot' / 'Queued'. */
  slot_queued_at?: string | null;
  /** The template that seeded this release (migration 060) — the fork
   * dialog preflights the INHERITED snapshot under it. Absent on a list
   * projection = fetched from GET /api/releases/:id when needed. */
  config_template_id?: string | null;
  /** #4199 P2 — see PlanKickInfo. Absent on a pre-P2 console's list. */
  plan_kick?: PlanKickInfo | null;
  /** prime-carryretry — see CarryHoldInfo. Absent on an older console's list. */
  carry_hold?: CarryHoldInfo | null;
  /** Owner ruling 2026-09-25 — whether the release holds its OWN minted
   * model key (release_secrets.xai_api_key_id). Served by GET /api/releases
   * and GET /api/releases/:id; absent on an older console = no mark. */
  agent_key?: 'minted' | 'missing' | null;
  /** win-boot (#5512): the release's FROZEN OS (release-platform.ts; served by
   * GET /api/releases, `linux` for a row with no frozen platform) and the OS
   * string the box itself reported at its last verified boot (migration
   * 202609282040; NULL until a report carries one — every Linux box today). */
  platform?: 'linux' | 'windows' | 'invalid' | null;
  vm_os?: string | null;
  owner_id: string | null;
  owner_email: string | null;
  owner_name: string | null;
  /** OIDC profile picture (users.picture_url, migration 029) — item 55:
   * the Owner cell and the recents cards paint it; null renders initials. */
  owner_picture_url?: string | null;
  source_branch: string | null;
  merged_into_branch: string | null;
  created_at: string | null;
  last_commit_at: string | null;
  last_vm_active_at: string | null;
  /** Computed server-side (recency.ts): activity of ANY kind. The list's sort
   * key — the server already orders by it, so the grid does not re-sort. */
  last_activity_at: string | null;
  /** Computed server-side (recency.ts): when the release VM last ran (an
   * older row may also carry a turn from the retired RM-sandbox lane, which
   * recency.ts still folds in). NULL when no machine has ever run. */
  last_vm_activity_at: string | null;
  /** The release VM row truth (migrations 003 + 051) — the VM status/uptime
   * columns read THESE, never EC2 (grid doctrine: no API call on the render
   * path). vm_started_at is the console's own RunInstances/StartInstances
   * stamp; NULL on a running VM = the start predates migration 051 and the
   * uptime cell says so by name. */
  vm_instance_id: string | null;
  vm_stopped_at: string | null;
  vm_started_at?: string | null;
  /** Migration 055 — where vm_started_at came from. NULL = the console's
   * own stamp; 'ec2-launch-time' = backfilled from the instance's EC2
   * LaunchTime (EXACT for the current start — LaunchTime advances on every
   * start, measured two-sided 2026-09-10, #1486/#1497/#1487; the older
   * reading, that a stop/start cycle leaves LaunchTime frozen at the
   * original launch, was false and made this shape render '≤' when it
   * deserved no qualifier);
   * 'observed-running' = the VM was already running when a lane marked the
   * row active (a LOWER bound); 'relaunch' = the console's own stamp on a
   * fresh instance the relaunch verb provisioned (exact — the tip says why
   * the clock reset). The uptime cell renders the bounded shapes
   * distinctly — a backfilled number never impersonates a real stamp. */
  vm_started_source?: string | null;
  /** Migration 202609071000 — vm-reconcile's last EC2 observation of the
   * recorded instance; 'gone' = terminated / absent from EC2, stamped once
   * at vm_gone_at. The VM cell renders it BEFORE the two-fact running rule:
   * a gone instance has nobody's stop stamp, so it would read "Running". */
  vm_state?: 'running' | 'stopped' | 'gone' | 'running-unreachable' | null;
  vm_gone_at?: string | null;
  /** Migration 202609091624 (#1107) — served with vm_state
   * 'running-unreachable': EC2 says the instance is running but its status
   * check is `impaired` (reachability failed) or its Bridge has been silent
   * for N fleet sweeps. Nothing auto-reboots or terminates it; the cell
   * names the remedy. */
  vm_unreachable_since?: string | null;
  vm_unreachable_reason?: string | null;
  /** Migration 049 (Replay-VM increment): 'replay' rows are disposable
   * observation envs — REPLAY badge, fire/down verbs, no lifecycle. */
  row_class?: 'release' | 'replay';
  replay_checkpoint_id?: string | null;
  replay_target_sha?: string | null;
  /** Migration 052 (FW5 T5.1, VM-fork program): fork PROVENANCE. A fork is
   * a REAL release (row_class stays 'release', D7) — these two columns are
   * what the FORK badge + lineage line key off ("forked from <release> @
   * <checkpoint short-id>"). NULL on every non-fork row. Display lands in
   * the later FW5 UI task — this is the wire plumbing. */
  fork_of_release_id?: string | null;
  fork_checkpoint_id?: string | null;
  /** Read-layer (migration 048): why the last accepted start failed. */
  transition_error?: string | null;
  /** Migration 065 — the fork autonomy seat: true/false explicit, null =
   * inherit the source fleet's posture at capture. */
  autostaff_on_boot?: boolean | null;
  /** Server decoration (fork.ts decorateForkRows) on FORK rows only:
   * lineage, the named boot-gate refusal + remedy while parked, and the
   * verify instrument's measured summary. Absent on plain rows. */
  fork_provenance?: {
    source_release_id: string | null;
    source_slug: string | null;
    checkpoint_id: string | null;
    captured_at: string | null;
    autostaff_on_boot: boolean | 'inherit';
  } | null;
  fork_boot?: {
    verdict: string;
    summary: string;
    remedy: string;
    boot_log: string | null;
  } | null;
  fork_verify?: {
    verified: boolean;
    passed: number;
    checked: number;
    verdict: string;
    boot_ok: boolean | null;
    /** Whether the marriage gate governs this release at all (derived from
     * the CURRENT fork_mode server-side). False on a `corpus-only` fork,
     * where passed/checked are 0 because no gate was computed — not because
     * one measured zero. Absent from a pre-#1781 server: treated as true,
     * which is what that server meant. */
    gate_applies?: boolean;
  } | null;
  has_successful_deployment: boolean;
  /** Newest SUCCESSFUL deployment's terminal instant (releases.list —
   * finished_at, started_at as the pre-042 fallback). NULL = the test
   * server has never received a bundle. */
  last_deployed_at?: string | null;
  /** The newest NON-TERMINAL deployment's status (queued…swapping), NULL at
   * rest — what turns the test-server dot yellow ("Deploying"). */
  deploy_in_flight_status?: string | null;
  /** The newest failed|exception deployment IF it is newer than the last
   * SUCCESSFUL one (releases.list), else NULL — what turns the test-server
   * dot red with the word "Failed". A failure older than the last success
   * is not a state anyone needs to see; a failure newer than it means the
   * chain is broken RIGHT NOW even though the old bundle keeps serving. */
  deploy_failed_status?: string | null;
  /** deployments.failure_reason for that row — the tooltip's whole content
   * when it exists. */
  deploy_failed_reason?: string | null;
  branches_pending: boolean;
  imported: boolean;
}

// Me / canManage / canCreate MOVED to shell.ts (shared-shell): the shell owns
// the /api/me read now — the account circle and the admin nav entries render
// on every page, not just this one. Re-exported so the headless suite (and
// any older importer) keeps pinning them through this module.
export { canCreate, canManage } from './shell.js';
export type { Me } from './shell.js';

/** One release's cost standing, as GET /api/releases/cost-summary serves it.
 * Mirrors the route's per-release object in server/costs.ts.
 *
 * #927: the map used to be `Map<string, number>`, which threw away the two
 * facts the cell needs to be honest — `measured` (a record EXISTS but carries
 * no dollar figure is not the same as no record at all) and `as_of` (a figure
 * from 40 minutes ago is not the same as a live one). Both were on the wire
 * already and the client discarded them. */
export interface CostEntry {
  /** null = a record exists but carries no dollar figure. */
  total_usd: number | null;
  /** false = nothing summable was found; total_usd is null. */
  measured: boolean;
  /** ISO — the newest fact behind this figure. */
  as_of: string | null;
  // ---- #1805 / #1816. The displayed figure is the whole fork LINEAGE, and
  // a lineage total can be a LOWER BOUND: `cost_snapshots` is a cumulative
  // upsert keyed (release_id, source), so a relaunch resets the VM's ledger
  // and the next push OVERWRITES the prior total. The server has derived and
  // transported that fact since #1805 and this client dropped it on the
  // floor — a number known to be short, rendered identically to a complete
  // one, which is worse than no number at all.
  /** True = `total_usd` is a LOWER BOUND, not a total. Absent (an older
   * console) is read as false — silence is not a claim of completeness, but
   * a marker cannot be invented out of it either. */
  truncated?: boolean;
  /** WHICH lineage members made it one, in lineage order. **EMPTY IS NOT
   * "NONE"**, and that is why this stays optional: #1844 added the field to
   * the grid's `GET /api/releases/cost-summary`, which sends it ONLY when
   * `truncated` is true and never sends `[]`; `GET /api/releases/:id/cost`
   * has carried `lineage.truncated_release_ids` since #1805. A payload that
   * carries the flag with no ids is still reachable — a console that
   * predates #1844, or a response cached across that deploy — so the cell
   * says "unnamed by this payload" rather than claiming a count it does not
   * hold. */
  truncated_release_ids?: string[];
  /** #4360 — the vendor-billed figure, basis `vendor`. When present it
   * REPLACES `total_usd` as the displayed figure (owner's 2026-09-17 replace
   * ruling, quoted in #4391 at 717e5ffc861b, tool-cards-workflow.ts:849-851);
   * never added to it. null/absent = nothing was billed: a non-zero catalog
   * `total_usd` is an ESTIMATE and renders as `Unmeasured`, never as a
   * figure and never `$0.00 billed` (W01; owner ruling n134, "only ever use
   * the authoritative figure"). */
  vendor_usd?: number | null;
  /** #4371 — the coverage mark on `vendor_usd`. ABSENT beside a vendor
   * figure reads as `unknown`: a bill is shown as complete only when the
   * payload SAYS it is. */
  vendor_coverage?: CostCoverage;
  /** #4371 — Σ vendor_tok / Σ tok in [0, 1]; null when it cannot be said. */
  vendor_coverage_share?: number | null;
}

/** #4371 — the server's `VendorCoverage` spelling (costs.ts). */
export type CostCoverage = 'complete' | 'partial' | 'unknown' | 'none';

/** #4360 — the vendor figure's basis: the FOURTH `WorkflowCostBasis` literal
 * (#4391 adds it to pi-stack; restated here, not imported, so this surface
 * does not wait on that PR). */
export type VendorCostBasis = 'vendor';

/** #4360/#4371 — the vendor figure a cost cell displays in place of the
 * catalog one. */
export interface VendorFigure {
  usd: number;
  basis: VendorCostBasis;
  /** `$X billed` — #4391's `fmtSpend(usd, 'vendor')` wording; the cell's
   * label when billed. */
  label: string;
  coverage: CostCoverage;
  share: number | null;
  /** The coverage mark; EMPTY when coverage is complete (a mark on every
   * figure teaches people to stop reading marks). */
  coverageLabel: string;
}

/** releaseId -> that release's cost standing; null = the endpoint has not
 * answered (unlanded, erroring, or not fetched yet). ABSENT FROM THE MAP is a
 * third state and a different one: the release exists and has no cost record.
 * `costCell` is the one place those three are told apart. */
export type CostMap = Map<string, CostEntry> | null;

/** One release's framework standing, as GET /api/releases/fleet-versions
 * reports it. Mirrors server/framework-versions.ts. */
export interface ReleaseVersion {
  releaseId: string;
  slug: string;
  installedSha: string | null;
  pinSha: string | null;
  /** The VM's baked base identity (server: `/opt/aisdlc/.base-input-hash`).
   * Absent = an older server. Null = unreadable. */
  baseInputHash?: string | null;
  /** The sweep's PRE-CLICK base-compat verdict (server framework-versions.ts
   * baseCompat): 'incompatible' means no published bundle can install on
   * this VM in place — the pill says so and names the remedy instead of
   * offering an update that every click would be refused. Absent = an
   * older server (rendered as the plain drift pill, as before). */
  compat?: { verdict: 'compatible' | 'incompatible' | 'unknown'; line: string; remedy?: string | null } | null;
  /** #1641 — the same verdict computed against THIS release's OWN referent
   * (its `framework_pin_sha` when pinned, `latest.json` only when not), with
   * `referent` naming which bundle it compared against. `compat` above is
   * always about latest.json; on a pinned release the two differ by
   * construction. Absent = an older server. A surface that renders the
   * verdict without the referent re-creates the REL-096 false MISMATCH. */
  referentCompat?: {
    verdict: 'compatible' | 'incompatible' | 'unknown';
    line: string;
    remedy?: string | null;
    referent?: { kind: 'pin' | 'latest'; sha: string | null; baseInputHash: string | null } | null;
  } | null;
  drift: boolean;
  /** R19: publishes separating installed from latest (S3 manifest order,
   * approximate by design). Null = unknown — rendered as unknown, never 0. */
  behind: number | null;
  error: string | null;
  checkedAt: string;
  /** installedSha through the release-notes manifest (server-resolved;
   * label is ALWAYS printable — the minted YYYY.MM.DD.N or the 12-char
   * sha with a named fallbackReason). Absent = an older server. */
  version?: ResolvedVersion | null;
  // ---- #889. All optional: a console deployed before this wave serves
  // none of them, and every reader below treats absent as "not stated"
  // rather than as a value.
  /** False = nothing was read off a box this sweep. */
  vmRunning?: boolean;
  /** 'live' = read off the running VM; 'pin' = the row's last-known
   * framework_pin_sha (also what the VM boots back into). */
  source?: 'live' | 'pin' | null;
  /** Commits of main between this release's bundle and latest's. */
  commitsBehind?: number | null;
  /** Why commitsBehind is null — null exactly when it is a number. */
  commitsBehindReason?: string | null;
  installedMergedThroughAt?: string | null;
  installedBuiltAt?: string | null;
  // `baseInputHash` is declared once, above, beside `compat` — #889 and the
  // pre-click verdict read the SAME field off the same sweep.
  /** Non-null = an update to latest is refused by the base gate, in words. */
  baseBlock?: string | null;
  /** Non-null exactly when installedSha is null: WHY we have nothing. */
  unknownReason?: string | null;
}
/** Mirrors server/framework-versions.ts ResolvedVersion. */
export interface ResolvedVersion {
  label: string;
  seq: number | null;
  fallbackReason: string | null;
}

/** One release's live update-lane standing (migration 056), folded into the
 * fleet-versions answer per GET. Mirrors server/framework-versions.ts. */
export interface FrameworkUpdateRow {
  state: 'updating' | 'failed' | 'refused' | 'scheduled' | 'resuming' | 'deferred';
  /** #828 — the facts behind a 'refused' state (server: UpdateRefusal).
   * Absent/null = a refusal with no structured cause (older row): rendered
   * as a refusal with its sentence and a plain retry, never as a failure. */
  cause?: {
    cause: string; vm_base: string | null; bundle_base: string;
    bundle_sha: string; remedy: string;
    /** #4179 P2 — the verb's class and the table's why (server:
     * UpdateRefusal.remedy_class / remedy_why); absent on a row stamped
     * before P2, which the chip renders exactly as before. */
    remedy_class?: string; remedy_why?: string;
  } | null;
  /** The stored measured sentence (R17's click-for-details). */
  error: string | null;
  /** ISO — when the state was stamped. */
  at: string;
  /** ISO — the server-computed expiry (R18): ignored at render time past
   * this, and mirrored into the row store's TTL so the badge repaints away
   * exactly then, with no client timer arithmetic of its own. */
  until: string;
  /** The RETIRED hot lane's phase record (server framework_update_resume):
   * the tooltip's counts come from here, never derived client-side. HISTORY
   * ONLY (#3972) — nothing has written one since #2414 / #3402, so a cell
   * that has one is rendering a row stamped before that retirement. */
  resume?: Record<string, unknown> | null;
}

export interface FleetVersionSnapshot {
  latestSha: string | null;
  /** The base the newest bundle was built against (server-side). */
  latestBaseInputHash?: string | null;
  /** latestSha through the release-notes manifest (see ResolvedVersion). */
  latestVersion?: ResolvedVersion | null;
  latestBuiltAt: string | null;
  latestError: string | null;
  releases: Record<string, ReleaseVersion>;
  /** Keyed by release id — the migration-056 update lane (see above). */
  updates: Record<string, FrameworkUpdateRow>;
  /** Keyed by release id — the LAST hot-update outcome per release
   * (framework_update_resume), served whether or not an update is in
   * flight. HISTORY ONLY (#3972): the hot lane is RETIRED (#2414 / #3402)
   * and nothing writes a new outcome, so every entry here predates it.
   * Absent on an older server. */
  resume?: Record<string, Record<string, unknown>>;
  sweptAt: string | null;
  /** #4101 — which sha Update Framework may install, and why. Absent on an
   * OLDER SERVER, and that absence is load-bearing: `frameworkCell` then
   * behaves exactly as it did before this field existed, rather than
   * inventing a verdict a server that cannot measure one never sent. */
  updateOffer?: UpdateOffer;
  measured: string;
}

/** null = the endpoint has not answered; we claim NOTHING in that state. */
export type FleetVersions = FleetVersionSnapshot | null;

/** What the Framework cell shows.
 *  none     — RETIRED 2026-09-08 (#889). It rendered a bare em dash, and
 *             `frameworkCell` can no longer return it: the value is kept in
 *             the union ONLY so `frameworkPillClass` and any stored column
 *             state from an older tab still typecheck. Every path that used
 *             to produce it now produces 'unknown' WITH A REASON.
 *             WHY: measured on prod 2026-09-08, five of the owner's seven
 *             releases showed '—'. One of them (REL-097) was 347 commits
 *             behind. A cell that renders a 347-commit gap identically to a
 *             release with nothing to say is not an absence of information,
 *             it is a false statement of one — the same class as a check
 *             that reports success while doing nothing.
 *  current  — measured and equal to latest: a positive 'Current' pill.
 *             RE-AUTHORED 2026-09-04: this REVERSES the earlier pinned
 *             ruling ("a current VM shows NO badge — '—'"; the old text
 *             called a green tick on every healthy row noise). The owner's
 *             Bridge-UI ruling of 2026-09-04 (R11) asks for current shown
 *             positively; the earlier ruling is recorded here, not deleted.
 *  drift    — measured and BEHIND latest: 'Update Available', which IS the
 *             action (R15) — clicking the pill is the update.
 *  updating — an accepted install is running (migration 056's durable row,
 *             or this tab's own click an instant before the 202 lands).
 *  failed   — the last update failed; the pill opens the stored measured
 *             detail (R16/R17) and clears itself (R18).
 *  unknown  — we could not measure. Deliberately NOT 'current': a drift
 *             badge that reads "up to date" when the read failed is the
 *             exact silent-success failure this framework refuses to ship. */
export type FrameworkState = 'none' | 'current' | 'drift' | 'unknown' | 'updating' | 'failed' | 'refused' | 'scheduled' | 'resuming' | 'deferred';

export interface FrameworkCell {
  state: FrameworkState;
  label: string;
  /** A DRIFT row whose VM cannot take the newest bundle at all (the sweep's
   * base-compat verdict is 'incompatible'): the pill reads 'Update blocked',
   * offers no install, and the click opens this — the measured refusal
   * (both base hashes) and the remedy — instead of spending a round trip
   * that the VM's gate would refuse by the same name. */
  blocked?: { reason: string; remedy: string } | null;
  tip: string;
  /** Whether THIS cell offers the INSTALL action (#889: 'drift' with a
   * running VM and no base objection). A failed or refused row's RETRY rides
   * `canRetry` (#828), never this flag — the two verbs are told apart. */
  canUpdate: boolean;
  /** #828 — the chip is clickable and RE-RUNS the update: a failed row, and
   * a refusal a retry could actually clear (it re-resolves the newest bundle
   * and re-runs the gate). False on a refusal only a different verb can
   * clear — `remedyAction` names that verb instead. */
  canRetry?: boolean;
  /** #828 — the operator verb that WOULD clear this state when retrying
   * cannot: 'vm-relaunch' for a base mismatch (the machine is replaced on
   * the current AMI + bundle). The chip offers exactly this action. */
  remedyAction?: ReleaseAction;
  /** R13's settle: true during the brief animated return to Current. */
  justUpdated?: boolean;
  /** R17: the stored measured failure sentence behind click-for-details. */
  detail?: string | null;
  /** #828 — the structured refusal facts, when the server recorded them. */
  cause?: FrameworkUpdateRow['cause'] | null;
  // ---- #889 ------------------------------------------------------------
  /** WHY there is no action on a row that otherwise looks actionable — a
   * stopped VM, a base-AMI mismatch, an install already running. Non-null
   * exactly when the state is one a person would expect to click and
   * `canUpdate` is false. A dead control with no explanation is the same
   * defect as the blank cell: it looks like a fact and carries none. */
  refusal?: string | null;
  /** True when `label`/`tip` describe a machine nobody just looked at — the
   * row's last-known framework rather than a live reading. The label says
   * so too; this flag is for the renderer's styling and for the tests. */
  lastKnown?: boolean;
  /** Commits of main between this release and latest, when known. */
  commitsBehind?: number | null;
  /** #4101 — the server's verified-bundle offer, verbatim, when it sent one.
   * This cell's label, tip and `canUpdate` are all DERIVED from it, so a
   * reader (or a test) can see the input the decision was made from. */
  offer?: UpdateOffer | null;
}

/** The transient, THIS-TAB half of the column's state. It rides the
 * foundation's row-state.ts store — built precisely because AG Grid's
 * refreshCells destroys renderer DOM, so a cell cannot keep "I am mid-
 * update" on its own element — and this seam consumes that store rather
 * than building a second one.
 *   'updating' — from the CLICK until the durable server stamp is read
 *                (R12's immediate feedback);
 *   'failed'   — a refusal the server never stored (network error, 4xx):
 *                column-initiated, so column-rendered (R16), self-clearing;
 *   'updated'  — the brief settle animating Updating -> Current (R13). */
export type FrameworkLocalState =
  | { state: 'updating'; at?: number; detail?: string | null }
  | { state: 'resuming'; at?: number; detail?: string | null }
  | { state: 'failed'; at?: number; detail?: string | null }
  | { state: 'refused'; at?: number; detail?: string | null }
  | { state: 'scheduled'; at?: number; detail?: string | null }
  /** #1089: the auto lane's stored note, folded in from the durable lane. */
  | { state: 'deferred'; at?: number; detail?: string | null }
  | { state: 'updated' };

/** What the POST /vm/relaunch response carries that the notice renders. */
export interface RelaunchNoticeData {
  measured?: string;
  snapshot_ids?: string[];
  old_instance_id?: string;
  /** The carry leg the server took: pre-leg / taken / no-seam / refused.
   * Only its `line` is rendered here, verbatim. */
  carry?: { line?: unknown } | null;
  /** `carryNote(...)` — what survives the relaunch, in the server's words. */
  note?: unknown;
}

/** The post-relaunch notice, DERIVED from the relaunch response. #4449 — the
 * server has answered every relaunch with the carry leg (`carry.line`) and the
 * survive sentence (`note`: "corpus branch + S3 checkpoints survive; on-VM
 * session state is in the snapshots only" on a pre-leg box), and the notice
 * rendered only the instance, the snapshot ids and the measured line — so the
 * one honest sentence about what the relaunch LOSES was computed on every
 * relaunch and shown on none. Both now follow the snapshot sentence VERBATIM;
 * an older or refused response that carries neither prints nothing for them,
 * never `undefined`. Pure so the mapping is testable without a DOM. */
export function relaunchNoticeText(status: number, d: RelaunchNoticeData | null | undefined): string {
  const carryLine = typeof d?.carry?.line === 'string' && d.carry.line ? d.carry.line : '';
  const note = typeof d?.note === 'string' && d.note ? d.note : '';
  return (
    `Relaunch VM: old instance ${d?.old_instance_id ?? '?'} terminated after ` +
    `${d?.snapshot_ids?.length ?? 0} forensic snapshot(s) [${(d?.snapshot_ids ?? []).join(', ')}]; ` +
    `${status === 202 ? 'the fresh VM is booting behind the gate — the row moves when it lands' : 'fresh VM recorded'}. ${d?.measured ?? ''}` +
    (carryLine ? ` ${carryLine}.` : '') +
    (note ? ` What survives: ${note}.` : '')
  );
}

/** Relative age, coarse and honest. Empty string for an unusable timestamp —
 * never "0m ago", which would assert a measurement we do not have. */
export function fmtAgo(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

const short = (sha: string | null | undefined): string => (sha ? sha.slice(0, 12) : 'unknown');

/** The Update Available tooltip's FIVE lines — the owner's exact shape
 * (bridge-ui-polish follow-up, replacing the 7-line sha dump):
 *   Running Version: <minted version, or the 12-char sha fallback>
 *   Latest Version:  <same resolution>
 *   X versions behind (singular at 1; seq delta when both sides are
 *     minted, else the swept publish count, else 'unknown')
 *   Checked: Xm ago  (fmtAgo — seconds only under a minute)
 *   Click to install the newest bundle  (the html twin bolds this)
 * Pure and exported: the suite pins the contract line by line, and the
 * hover-time provider rebuilds from the SAME function. */
export function frameworkDriftTipLines(
  entry: ReleaseVersion,
  fv: FleetVersionSnapshot,
  now = Date.now(),
): string[] {
  const running = entry.version?.label ?? short(entry.installedSha);
  const latest = fv.latestVersion?.label ?? short(fv.latestSha);
  const rSeq = entry.version?.seq ?? null;
  const lSeq = fv.latestVersion?.seq ?? null;
  const n = rSeq !== null && lSeq !== null && lSeq > rSeq ? lSeq - rSeq : entry.behind ?? null;
  // #889: the DISTANCE line. "347 commits behind" is what an operator judges
  // urgency by; the version count answers a different question (how many
  // bundles were published) and was the only figure here. When the ordinal
  // cannot be computed the line SAYS WHY — the one thing it may never do is
  // go quiet, because a silent distance reads as a small one.
  const distance =
    typeof entry.commitsBehind === 'number'
      ? `${entry.commitsBehind} commit${entry.commitsBehind === 1 ? '' : 's'} behind main` +
        (entry.installedMergedThroughAt ? ` (its tree is from ${fmtAgo(entry.installedMergedThroughAt, now)})` : '')
      : entry.commitsBehindReason
        ? `Commits behind: unknown — ${entry.commitsBehindReason}`
        : null;
  const live = entry.source === 'pin'
    ? [`Reading: LAST KNOWN — the VM is not running; this is the pin it boots back into`]
    : [];
  return [
    `Running Version: ${running}`,
    `Latest Version: ${latest}`,
    n === null ? 'unknown versions behind' : `${n} version${n === 1 ? '' : 's'} behind`,
    ...(distance ? [distance] : []),
    ...live,
    `Checked: ${fmtAgo(entry.checkedAt, now)}`,
    'Click to install the newest bundle',
  ];
}

/** The 'Resuming…' tooltip of a LEGACY row, from the phase record the
 * RETIRED hot update lane left behind (server framework_update_resume) — the
 * age and the counts, each the server's own number.
 *
 * #3972 — this used to serve the 'Updating…' tooltip too, through a
 * `rec.mode === 'hot'` branch in `updatingCell`. That branch was DEAD: the
 * update route refuses `mode:'hot'` 400 by name and the completion writer no
 * longer produces a phase record, so no live `updating` row can carry one.
 * Only the resuming arm is left, it is reachable only from rows stamped
 * before #2414 / #3402, and its LAST LINE says so — a reader must be able to
 * tell a historical cell from an update in flight, which is the whole defect
 * this function used to hide. Pure and exported. */
export function legacyResumeTipLines(
  rec: Record<string, unknown> | null | undefined,
  atIso: string | null,
  now = Date.now(),
): string[] {
  const n = (k: string): number | null => (typeof rec?.[k] === 'number' ? (rec![k] as number) : null);
  const age = atIso ? fmtAgo(atIso, now) : '';
  const sessions = n('preparedSessions') ?? n('prepared_sessions');
  const mid = n('preparedMidTurn') ?? n('prepared_mid_turn');
  return [
    `Resuming${sessions === null ? ' the checkpointed sessions' : ` ${sessions} session${sessions === 1 ? '' : 's'}`}` +
      `${mid === null ? '' : ` (${mid} mid-turn)`} after the hot update${age ? ` — restarted ${age}` : ''}.`,
    'Every tab, pane and registry row comes back under its own id; a session that was mid-turn gets one machine turn to continue where it was.',
    'HISTORY, not an update in flight: the hot update lane was retired with the Bridge\u2019s parked-fleet resume lane (#2414, console half #3402). Nothing writes this state any more, so this row was stamped before that retirement and no further verdict is coming.',
  ];
}

const escHtml = (s: string): string =>
  s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;');

/** The rich twin: same five lines, last one bolded. Server-fed labels are
 * escaped here — the provider owns escaping (tooltip layer contract). The
 * .bridge-tip layer is white-space:pre-line, so \n stays a line break. */
export function frameworkDriftTipHtml(
  entry: ReleaseVersion,
  fv: FleetVersionSnapshot,
  now = Date.now(),
): string {
  const lines = frameworkDriftTipLines(entry, fv, now);
  const last = lines[lines.length - 1]!;
  return `${lines.slice(0, -1).map(escHtml).join('\n')}\n<strong>${escHtml(last)}</strong>`;
}

/** The Framework column's whole decision, as one pure function.
 *
 * WHICH ROWS GET A CELL IS NOT DECIDED HERE. This function used to open with
 * `row.status !== 'active'` — a second, client-side copy of "which releases
 * have a VM", and it drifted from the action's copy the moment the RM-on-VM
 * flip gave IDEATING releases real VMs (2026-09-02). Measured on the live
 * console 2026-09-04: an ideating release with a running VM rendered '—' with
 * no Update action, while the server would happily have installed onto it.
 *
 * The sweep's ENTRY is now the whole answer: fleet-versions selects rows by
 * `vmRunning` server-side (releases.ts — the same predicate the update route
 * gates on), so a row with an entry has a running VM by construction and a
 * row without one has nothing to install onto. One predicate, one place,
 * display and action reading the identical truth. */
export function frameworkCell(
  row: ReleaseGridRow | undefined,
  fv: FleetVersions,
  now = Date.now(),
  local: FrameworkLocalState | null = null,
): FrameworkCell {
  // #889: THE INVARIANT OF THIS FUNCTION — every return below carries a
  // state a person can act on or a reason they cannot. `unknownCell` is the
  // only exit for "we have nothing", and it cannot be called without saying
  // why. There is no path back to a bare em dash.
  const unknownCell = (reason: string, refusal?: string | null): FrameworkCell => ({
    state: 'unknown',
    label: 'version unknown',
    tip: ['This release\u2019s framework could not be determined.', `reason:  ${reason}`].join('\n'),
    canUpdate: false,
    refusal: refusal ?? null,
  });
  if (!row) {
    return unknownCell('no release row was passed to the cell renderer');
  }
  // #3972 — this used to fork on `rec?.mode === 'hot'` and serve the hot
  // lane's tooltip. Dead: the route refuses `mode:'hot'` 400 by name and the
  // completion writer no longer produces a phase record, so no live
  // 'updating' row can carry one. One tip, and the `rec` parameter is gone
  // with the branch that was its only reader.
  const updatingCell = (startedAtMs: number | null): FrameworkCell => ({
    state: 'updating',
    label: 'Updating…',
    tip: [
      'Installing the newest published bundle on this VM' +
        (startedAtMs ? ` — started ${fmtAgo(new Date(startedAtMs).toISOString(), now)}` : '') +
        '.',
      'The install typically takes a few minutes; this row reports its own verdict, in every tab.',
      `Stuck threshold: ${FW_UPDATING_STUCK_MIN} min — past it with no verdict the row is shown as failed, with that sentence, not as a spinner.`,
    ].join('\n'),
    canUpdate: false,
  });
  /** LEGACY (#3972): a row the RETIRED hot lane stamped before #2414 /
   * #3402. Nothing produces this state any more; it is still rendered
   * because the rows are real, and the tip's last line says which it is. */
  const resumingCell = (atIso: string | null, rec: Record<string, unknown> | null = null): FrameworkCell => ({
    state: 'resuming',
    label: 'Resuming…',
    tip: legacyResumeTipLines(rec, atIso, now).join('\n'),
    canUpdate: false,
  });
  const failedCell = (detail: string | null, atIso: string | null): FrameworkCell => ({
    state: 'failed',
    label: 'Update failed',
    tip: [
      `The last framework update FAILED${atIso ? ` ${fmtAgo(atIso, now)}` : ''}.`,
      detail ? `reason:  ${detail}` : 'No measured detail was recorded.',
      // #828: the chip is the lane. It used to be inert, and the owner was
      // told to click something that did nothing.
      // #889 (owner request 2026-09-08) asked for exactly this — click
      // RETRIES and the stored sentence stays one hover away. It is safe to
      // offer because `updateStateBlocks` (server, #871/#886) does not hold
      // a 'failed' row at any age — a failure is a finished attempt, not a
      // running one. The retry rides `canRetry`; `canUpdate` stays the
      // INSTALL verb and is false here.
      'Click to RETRY the update. This badge also clears itself after a few minutes (R18).',
    ].join('\n'),
    canUpdate: false,
    canRetry: true,
    detail,
  });
  /** #828 — A REFUSAL IS NOT A FAILURE. The base-compat gate refusing an
   * incompatible bundle is the system working: nothing was fetched,
   * restarted or pinned, and the release is exactly as it was. It reads as
   * REFUSED, names the reason, and offers the verb that would actually
   * clear it — for a base mismatch that is Relaunch VM (the VM's baked base
   * predates every published bundle, so no retry can succeed until the
   * machine itself is replaced), never an endless red retry. */
  const refusedCell = (
    detail: string | null,
    atIso: string | null,
    cause: FrameworkUpdateRow['cause'] | null,
  ): FrameworkCell => {
    // #4179 P2: the remedy is the verb the release's STATE permits (server
    // reopen-remedy.ts, P1's decision table), not a constant. 'relaunch' is
    // still the one verb with a button here — it is permitted exactly on an
    // open, unprotected release whose bases differ. Every other verb is
    // PROSE: 'retry' keeps the retry click; the reopen / re-pin / wait-reaper
    // verbs name their route; and the OWNER DECISION verb names the owner's
    // writer for the protect list (reaper-protect.ts, audited
    // reaper.protect_changed) — no session and no button touches that list
    // (orchestrator seq 894), so there is deliberately no button for it.
    const remedy = cause?.remedy ?? 'retry';
    const relaunch = remedy === 'relaunch';
    const retry = remedy === 'retry';
    const ownerDecision = remedy.startsWith('OWNER DECISION');
    const why = cause?.cause === 'base-mismatch'
      ? `This VM was baked on base ${short(cause.vm_base ?? 'unreadable')}; the published bundle ` +
        `${short(cause.bundle_sha)} is built for base ${short(cause.bundle_base)}. Installing it would ` +
        `break the VM, so the gate refused it.`
      : (detail ?? 'A gate refused the update.');
    const remedyLine = relaunch
      ? 'Retrying cannot clear this: no published bundle fits this VM. Relaunch VM replaces the ' +
        'machine with a fresh one on the current AMI + bundle — click to start it. (The console ' +
        'also re-tries by itself the moment a compatible bundle is published.)'
      : retry
        ? 'Click to retry — the update re-resolves the newest bundle and re-runs the gate.'
        : ownerDecision
          ? `remedy: ${remedy}. Retrying cannot clear this and no button here can: this VM is on the ` +
            'operator\'s protect list, so relaunch and stop both refuse it by name. The owner decides ' +
            'between un-protecting the VM (its one writer is the protect-list seat, reaper-protect.ts, ' +
            'audited as reaper.protect_changed) and re-pinning the release to the newest base ' +
            '(POST /api/releases/:id/framework-pin). Nothing here touches the VM or the list.'
          : `remedy: ${remedy}. Retrying cannot clear this. ` +
            (remedy.startsWith('re-pin')
              ? 'Re-pin the release first (POST /api/releases/:id/framework-pin to the current pointer), then '
              : '') +
            (remedy.includes('wait-reaper')
              ? 'wait for the reaper to clear the stopped VM, then reopen provisions fresh.'
              : remedy.startsWith('reopen(ec2-start)')
                ? 'reopen starts the machine that exists — its base already matches the pin.'
                : 'reopen provisions a fresh VM on the pinned base.');
    return {
      state: 'refused',
      label: 'Update refused',
      tip: [
        `The last framework update was REFUSED by a gate${atIso ? ` ${fmtAgo(atIso, now)}` : ''} — ` +
          'nothing was fetched, restarted or pinned. The release is untouched.',
        `reason:  ${why}`,
        remedyLine,
        ...(cause?.remedy_why ? [`state:   ${cause.remedy_why}`] : []),
      ].join('\n'),
      canUpdate: false,
      canRetry: retry,
      ...(relaunch ? { remedyAction: 'vm-relaunch' as const } : {}),
      detail,
      cause: cause ?? null,
    };
  };
  /** #1089: the auto lane REACHED this release, found it behind, and did not
   * act — `detail` is its own sentence saying why. Distinct from 'scheduled'
   * on purpose: nothing is queued and no window is being waited for, so a
   * 'Scheduled' pill here would promise an install that is not coming. The
   * manual update stays available — the deferral is the AUTO lane's, and a
   * person clicking is a different lane with different rules. */
  const deferredCell = (detail: string | null, atIso: string | null): FrameworkCell => ({
    state: 'deferred',
    label: 'Update deferred',
    tip: [
      detail ?? 'the automatic framework-update lane skipped this release',
      `The automatic lane looked at this release${atIso ? ` ${fmtAgo(atIso, now)}` : ''} and did NOT ` +
        'install: nothing was fetched, pinned or restarted. It re-decides every tick, and this note ' +
        'clears itself when it stops applying.',
      'Click Update to run it by hand — the manual lane is not deferred by this.',
    ].join('\n'),
    canUpdate: true,
    detail,
  });
  /** The quiescent lane's wait (framework-auto-update): the update is
   * scheduled for the VM's next quiet window; `detail` is the server's
   * sentence naming the live-turn count. No turn is ever ended. */
  const scheduledCell = (detail: string | null, atIso: string | null): FrameworkCell => ({
    state: 'scheduled',
    label: 'Scheduled',
    tip: [
      detail ?? 'scheduled: will apply at the next quiet window',
      `Waiting for a moment with no agent turn live${atIso ? ` — since ${fmtAgo(atIso, now)}` : ''}; ` +
        'the install then runs at that boundary. A live turn is never ended.',
    ].join('\n'),
    canUpdate: false,
    detail,
  });
  // THIS TAB's transient first (row-state.ts): the click paints 'Updating…'
  // before the server has answered (R12), and a refused click paints its
  // failure with no durable state existing anywhere (R16's column arm).
  if (local?.state === 'updating') return updatingCell(local.at ?? null);
  if (local?.state === 'resuming') {
    return resumingCell(local.at ? new Date(local.at).toISOString() : null, fv?.updates?.[row.id]?.resume ?? null);
  }
  if (local?.state === 'scheduled') {
    return scheduledCell(local.detail ?? null, local.at ? new Date(local.at).toISOString() : null);
  }
  if (local?.state === 'deferred') {
    return deferredCell(local.detail ?? null, local.at ? new Date(local.at).toISOString() : null);
  }
  if (local?.state === 'failed') {
    return failedCell(local.detail ?? null, local.at ? new Date(local.at).toISOString() : null);
  }
  if (local?.state === 'refused') {
    return refusedCell(local.detail ?? null, local.at ? new Date(local.at).toISOString() : null, null);
  }
  // The durable lane (migration 056) — what EVERY tab sees. `until` is the
  // server-computed expiry (R18): an entry past it is ignored at RENDER
  // time, never left standing until a poll happens to remove it.
  const upd = fv?.updates?.[row.id];
  if (upd && now < new Date(upd.until).getTime()) {
    if (upd.state === 'updating') return updatingCell(new Date(upd.at).getTime());
    if (upd.state === 'resuming') return resumingCell(upd.at, upd.resume ?? null);
    if (upd.state === 'scheduled') return scheduledCell(upd.error, upd.at);
    // #1089 BEFORE the failedCell fall-through below: an unrecognised state
    // rendering as 'Update failed' would turn a note about a non-action into
    // a verdict about an install that never ran.
    if (upd.state === 'deferred') return deferredCell(upd.error, upd.at);
    if (upd.state === 'refused') return refusedCell(upd.error, upd.at, upd.cause ?? null);
    return failedCell(upd.error, upd.at);
  }
  if (!fv || !fv.sweptAt) {
    return unknownCell(
      fv
        ? 'the fleet-versions sweep has not completed a pass yet — this cell fills in on its first answer'
        : 'the console has not answered GET /api/releases/fleet-versions yet',
    );
  }
  const entry = fv.releases[row.id];
  if (!entry) {
    // #889: the sweep scopes itself to rows with a recorded VM instance and
    // row_class 'release'. Say WHICH of those this row failed, from the row's
    // own fields — the operator learns "there has never been a machine here"
    // instead of reading an em dash that could mean anything.
    if (row.row_class === 'replay') {
      return unknownCell(
        'this is a REPLAY row — a replay VM boots the framework baked into its checkpoint ' +
          'and is never framework-updated, so no framework standing is tracked for it',
        'Replay environments are not framework-update targets.',
      );
    }
    if (!row.vm_instance_id) {
      return unknownCell(
        `no VM has ever been recorded for this release (status '${row.status}'), so there is no ` +
          'machine whose framework could be read or pinned',
        'There is no VM to install onto.',
      );
    }
    return unknownCell(
      `the fleet-versions sweep has no entry for this release (VM ${row.vm_instance_id} is recorded) — ` +
        'it was created or its VM was recorded after the last sweep pass',
    );
  }
  const latestLine = fv.latestSha
    ? `latest:  ${short(fv.latestSha)}${fv.latestBuiltAt ? ` (published ${fmtAgo(fv.latestBuiltAt, now)})` : ''}`
    : `latest:  UNREADABLE — ${fv.latestError ?? 'no pointer'}`;
  const pinLine = entry.pinSha
    ? `pin:     operator pin ${short(entry.pinSha)} — survives reboots`
    : 'pin:     none — this VM converges to its launch pin on reboot';
  const checked = `checked: ${fmtAgo(entry.checkedAt, now)}`;

  if (!entry.installedSha) {
    // The server states its own reason (#889: `unknownReason` is non-null
    // exactly when installedSha is). `error` is the older field and remains
    // the fallback for a console deployed before this wave.
    const why = entry.unknownReason ?? entry.error ?? 'unstated';
    // Owner report 2026-09-08: a release read 'Version Unknown — reason: ssm
    // status=Failed' for hours with NO option to re-read or update — a dead
    // end on the one row that needed an action. Unknown now OFFERS both:
    // the pill click re-reads (a fresh sweep), and the row menu's Update
    // Framework stays enabled whenever a latest bundle exists — the server
    // re-measures the VM itself and refuses BY NAME if it cannot install
    // (not SSM-online, unreadable base hash), so offering it costs nothing
    // and hides nothing.
    // #889 narrows that offer by ONE measured fact: when the sweep itself
    // says the VM is stopped (`vmRunning === false`) there is no machine to
    // install onto, so the row carries the refusal instead of a control that
    // can only fail. Absent the field (an older server) the offer stands.
    const stopped = entry.vmRunning === false;
    const refusal = stopped ? 'This VM is stopped — start it to read or update its framework.' : null;
    const canUpdate = !!fv.latestSha && !stopped;
    return {
      state: 'unknown',
      label: 'version unknown',
      tip: [
        `This release\u2019s framework could not be determined.`,
        `reason:  ${why}`,
        latestLine,
        checked,
        stopped
          ? `action:  ${refusal}`
          : 'action:  click to re-read now' +
            (canUpdate
              ? '; the row menu\u2019s Update Framework installs latest anyway (the VM refuses by name if it cannot)'
              : ''),
      ].join('\n'),
      canUpdate,
      refusal,
    };
  }
  const lastKnown = entry.source === 'pin';
  const running = lastKnown
    ? `last known: ${short(entry.installedSha)} (the row\u2019s pin — the VM is not running)`
    : `running: ${short(entry.installedSha)}`;
  // #1022: `drift` is three-valued, and `null` means the comparison could not
  // be MADE (no readable latest pointer). It must render as UNKNOWN — a bare
  // `!entry.drift` sent it here, to a pill that says "Current" and a tip that
  // names a `latest` line reading UNREADABLE two lines above the word.
  if (entry.drift === null) {
    return {
      state: 'unknown',
      label: lastKnown ? 'Standing unknown (last known)' : 'Standing unknown',
      tip: [
        `This release’s framework was read, but its STANDING could not be judged.`,
        running,
        `reason:  ${entry.unknownReason ?? entry.error ?? 'the latest-bundle pointer is unreadable'}`,
        latestLine,
        pinLine,
        checked,
        'action:  click to re-read now — this is not "up to date"; nothing was compared.',
      ].join('\n'),
      // Nothing to install TO: the pointer that names latest is the thing that
      // could not be read, so an update click could only fail the same way.
      canUpdate: false,
      lastKnown,
      refusal: 'The latest-bundle pointer is unreadable, so there is no bundle to install.',
    };
  }
  if (entry.drift === false) {
    return {
      state: 'current',
      // R11 (owner ruling 2026-09-04): current shows POSITIVELY. The
      // pre-ruling '—' is recorded in the FrameworkState doc above.
      // #889: a STOPPED VM pinned to latest is current in the only sense
      // that matters — it boots into latest — but the label must not imply
      // anyone just looked at it.
      label: lastKnown ? 'Current (last known)' : 'Current',
      tip: [
        running,
        latestLine,
        pinLine,
        ...(lastKnown
          ? ['note:    the VM is not running; this is the pin it will boot back into, not a live reading']
          : []),
        checked,
      ].join('\n'),
      canUpdate: false,
      lastKnown,
      commitsBehind: 0,
      // R13: the brief settle after a completed update animates the pill's
      // return to Current instead of popping a dialog (R14).
      ...(local?.state === 'updated' ? { justUpdated: true } : {}),
    };
  }
  // #889 — BEHIND IS A STATE, ACTIONABLE IS A SEPARATE QUESTION. Three rows
  // are all behind and only one can be clicked, so the label carries the
  // distance and `refusal` carries the reason there is no button:
  //   running, base OK      -> 'Update Available', the click installs;
  //   running, base MISMATCH-> 'Behind — blocked', because the base gate
  //                            will refuse the install (measured REL-096,
  //                            2026-09-08: the stored failure was exactly
  //                            `base mismatch (vm=e5f5db79 bundle=26a9fb52)`,
  //                            so a retry button there is a dead control);
  //   stopped               -> 'Behind (last known)', because there is no
  //                            machine up to install onto — and this is the
  //                            case that used to render as a bare em dash on
  //                            a release 347 commits behind.
  // THE DISTANCE IN THE LABEL. Commits is the figure the owner asked for and
  // the one that reads as urgency. When the installed bundle predates
  // publish-bundle.sh's ordinal stamp (measured: a4d04c21da8b and
  // f55b41061f14, both published 2026-09-07 before the field existed) the
  // count is genuinely unavailable — so the label falls back to the publish
  // count, which IS available for those, and the tooltip states why the
  // commit figure is missing. Silence is the one option not on the table.
  const dist =
    typeof entry.commitsBehind === 'number' && entry.commitsBehind > 0
      ? `${entry.commitsBehind} commit${entry.commitsBehind === 1 ? '' : 's'} behind`
      : typeof entry.behind === 'number' && entry.behind > 0
        ? `${entry.behind} version${entry.behind === 1 ? '' : 's'} behind`
        : null;
  const tipLines = frameworkDriftTipLines(entry, fv, now);
  if (lastKnown || entry.vmRunning === false) {
    const stoppedRefusal =
      'This VM is stopped, so there is nothing to install onto. It will boot back into this ' +
      'same pinned framework — start the VM, then update.';
    return {
      state: 'drift',
      label: dist ? `Behind — ${dist} (last known)` : 'Behind (last known)',
      tip: [...tipLines.slice(0, -1), stoppedRefusal].join('\n'),
      canUpdate: false,
      refusal: stoppedRefusal,
      lastKnown: true,
      commitsBehind: entry.commitsBehind ?? null,
    };
  }
  // THE PRE-CLICK REFUSAL (2026-09-09, daag-full-run): the VM was baked on a
  // base no published bundle is built for. Offering 'Update Available' here
  // produced one audited refusal per click (8 in 11 h) and a red 'Update
  // failed' chip for a gate that was WORKING. The verdict is known before
  // anyone clicks, so the pill says so and names the remedy — and it still
  // carries the DISTANCE (#889's rule: behind is a state, actionable is a
  // separate question), so a blocked row is never less informative than a
  // clickable one.
  //
  // This branch and the `entry.baseBlock` one below are the two halves of the
  // same fact: `compat` is the STRUCTURED verdict (what this client branches
  // on) and `baseBlock` is the SENTENCE (#889). A server that serves both
  // lands here; the baseBlock branch stays as the fallback for one that
  // serves only the sentence, and both refuse the install.
  if (entry.compat?.verdict === 'incompatible') {
    const remedy = entry.compat.remedy === 'relaunch'
      ? 'Relaunch the VM on the current AMI (the row menu\'s Relaunch VM where this console offers it; POST /api/releases/:id/vm/relaunch) — or publish a bundle on this VM\'s base.'
      : 'Re-measure the VM (the next sweep) — the install gate refuses an unreadable base.';
    // Both sentences when the server has both: the structured line names the
    // two hashes, #889's prose names WHICH gate refused and why a rebake
    // cannot clear a live baked base.
    const reason = entry.baseBlock ? `${entry.compat.line}\n\n${entry.baseBlock}` : entry.compat.line;
    return {
      state: 'drift',
      label: dist ? `Update blocked — ${dist}` : 'Update blocked',
      tip: [
        ...tipLines.slice(0, 3),
        `Base: INCOMPATIBLE — VM ${short(entry.baseInputHash ?? 'unreadable')}, bundle ${short(fv.latestBaseInputHash ?? 'unknown')}`,
        `Checked: ${fmtAgo(entry.checkedAt, now)}`,
        'No in-place update can succeed on this VM. Click for the measured reason and the remedy.',
      ].join('\n'),
      canUpdate: false,
      refusal: reason,
      lastKnown: false,
      commitsBehind: entry.commitsBehind ?? null,
      blocked: { reason, remedy },
    };
  }
  if (entry.baseBlock) {
    return {
      state: 'drift',
      label: dist ? `Behind — ${dist} (blocked)` : 'Behind — update blocked',
      tip: [...tipLines.slice(0, -1), entry.baseBlock].join('\n'),
      canUpdate: false,
      refusal: entry.baseBlock,
      lastKnown: false,
      commitsBehind: entry.commitsBehind ?? null,
    };
  }
  // Owner follow-up (bridge-ui-polish): the drift tip is the version ledger —
  // frameworkDriftTipLines is the ONE builder (this plain twin feeds the menu
  // tip and the no-provider fallback; the pill's rich hover bolds the last
  // line via frameworkDriftTipHtml). The pin line left THIS state's tooltip
  // by owner ruling — for a drifting row it was the only pin surface, stated
  // in the PR body; Current keeps its pin line (pinLine above still feeds it).
  // #4101 — THE VERIFIED OFFER. Until this branch existed, a drifting row
  // read 'Update Available' and the click installed whatever `latest.json`
  // pointed at, WHICH IS NOT THE SAME THING AS A BUNDLE MAIN HAS PROVED.
  // `latest.json` moves at publish time, which can be while main's own ci run
  // is still in flight, so the control could silently install an unverified
  // bundle onto a release VM and never say that a verified one existed.
  //
  // The server decides (one `updateOffer`, read here verbatim and by the
  // install lane); this cell only RENDERS the decision. Both shas are named
  // whenever they differ — that naming is the operator half of the ask.
  const offer = fv.updateOffer ?? null;
  if (offer && offer.kind === 'last-verified') {
    return {
      state: 'drift',
      // The distance still leads (#889's rule: behind is a state, actionable
      // is a separate question) — a verified-offer row is never LESS
      // informative than a plain one.
      label: dist ? `Update Available — ${dist} (verified)` : 'Update Available (verified)',
      tip: [
        ...tipLines,
        '',
        offer.line,
        `Verified:   ${short(offer.offeredSha ?? 'unknown')} — this is what Update Framework installs`,
        `Pointer:    ${short(offer.pointerSha ?? 'unknown')} — published, unverified; not offered until it verifies`,
      ].join('\n'),
      canUpdate: true,
      refusal: null,
      lastKnown: false,
      commitsBehind: entry.commitsBehind ?? null,
      offer,
    };
  }
  if (offer && offer.kind === 'none') {
    // Nothing VERIFIED has ever been published and main proved the pointer
    // RED. A dead control that says why (#889's invariant), never a silent
    // install of a bundle main refused.
    return {
      state: 'drift',
      label: dist ? `Update blocked — ${dist}` : 'Update blocked',
      tip: [...tipLines.slice(0, -1), offer.line].join('\n'),
      canUpdate: false,
      refusal: offer.line,
      lastKnown: false,
      commitsBehind: entry.commitsBehind ?? null,
      offer,
    };
  }
  return {
    state: 'drift',
    label: dist ? `Update Available — ${dist}` : 'Update Available',
    // 'pointer' (main VERIFIED it) and 'unmeasured' (no verdict has ever been
    // stamped — #4100 is open, so this is every row today) both install the
    // pointer exactly as before. The sentence still SAYS which of the two it
    // is: an unmeasured verdict is not a verified one, and a tip that stayed
    // silent about it would be the same silence this issue is about.
    tip: offer ? [...tipLines, '', offer.line].join('\n') : tipLines.join('\n'),
    canUpdate: true,
    refusal: null,
    lastKnown: false,
    commitsBehind: entry.commitsBehind ?? null,
    offer,
  };
}

/** The served 'updating' window (server UPDATING_STALE_MS, 15 min): past it
 * with no verdict the row is served as failed. Stated in the tip so a person
 * watching a spinner knows when it stops being one. */
export const FW_UPDATING_STUCK_MIN = 15;

/** One release's live fleet standing, as GET /api/releases/fleet-metrics
 * reports it. Mirrors server/fleet-metrics.ts. */
export interface ReleaseFleet {
  releaseId: string;
  slug: string;
  /** null = NOT measured. Never conflate with 0, which means "running none". */
  sessions: number | null;
  panes: number | null;
  /** Running dynamic-workflow agents on this VM (SDK sessions inside
   * workflow runs — not Bridge sessions, not panes). null = the Bridge did
   * not report the figure: UNMEASURED, never 0. */
  wfAgents: number | null;
  /** Live workflow runs behind that count. */
  wfRuns: number | null;
  /** DEAD-IDLE (REL-096): the Bridge's own verdict — null = not dead-idle
   * (when `deadIdleMeasured`) or unmeasured. The `line` is the Bridge's
   * measured line, rendered verbatim; the console never recomputes it. */
  deadIdle: ReleaseDeadIdle | null;
  deadIdleMeasured: boolean;
  points: SeriesPoint[];
  windowMs: number | null;
  error: string | null;
  checkedAt: string;
  completionPct: number | null;
  pointsTotal: number | null;
  pointsDelivered: number | null;
  completionAt: string | null;
  /** Corpus commits that exist only on the VM (migration 067; pushed by the
   * Bridge's corpus publisher). null = UNMEASURED, never 0. */
  corpusAheadOfRemote?: number | null;
  corpusPublicationAt?: string | null;
  corpusPublicationNote?: string | null;
  /** #2324 — the publisher's verdict status beside the count, and the
   * stamp's standing AT THE STOP ('missing' / 'stale' / 'stamped'; null while
   * running). Absent on an older console snapshot: unknown, rendered as such. */
  corpusPublicationStatus?: string | null;
  corpusPublicationAtStop?: 'missing' | 'stale' | 'stamped' | null;
  /** The release's run-position line (aisdlc-docs § 30), verbatim from the
   * VM Bridge; null = UNMEASURED (unreachable / older bundle / first compute
   * in flight — `runPositionError` names which). Absent on an older console
   * snapshot: the same null. */
  runPosition?: RunPositionLine | null;
  runPositionError?: string | null;
  /** Free space on the VM (REL-096). null = UNMEASURED — an unreachable
   * Bridge, a bundle predating the fact, or a filesystem with no inode
   * table. Absent on an older console snapshot: the same null. */
  diskFreeGb?: number | null;
  diskFreeInodes?: number | null;
  diskPct?: number | null;
  inodePct?: number | null;
}

/** Mirrors server/fleet-metrics.ts RunPositionLine (the Bridge's shape). */
export interface RunPositionLine {
  release: string;
  step: string;
  label: string;
  state: string;
  blocked: boolean;
  line: string;
  position: string;
  blocker: string;
  lever: string;
  liveTurns: number | null;
  computedAt: string;
}

/** Mirrors server/fleet-metrics.ts ReleaseDeadIdle (helm-bridge dead-idle.ts). */
export interface ReleaseDeadIdle {
  since: string;
  minutes: number;
  heldStories: number;
  freeKeys: string[];
  keyClasses: Record<string, string[]>;
  openEsc: number;
  liveTurns: number;
  line: string;
  signature: string;
  woke: boolean;
  secondWindowDone: boolean;
  outcome: string | null;
  reverify: string | null;
}

export interface FleetMetricsSnapshot {
  releases: Record<string, ReleaseFleet>;
  sweptAt: string | null;
  measured: string;
}

/** null = the endpoint has not answered; we claim NOTHING in that state. */
export type FleetMetricsMap = FleetMetricsSnapshot | null;

// ---------------------------------------------------------------------
// Escalation scorecard (REL-096/097) — GET /api/scorecard
// ---------------------------------------------------------------------

/** One release's escalation scorecard, as GET /api/scorecard reports it
 * (mirrors server/escalation-scorecard.ts). The goal's metric: product
 * DECISIONS waiting on a human are the positive outcome; MACHINERY records
 * are platform defects a human should never read — one that reached the
 * human is a routing defect, not workload. */
export interface ReleaseScorecard {
  decision: number;
  machinery: number;
  blocking: number;
  open: number;
  decisions_for_user: number;
  machinery_at_user: number;
  /** Machinery at the platform rung (U1); absent from an older Bridge. */
  machinery_at_platform?: number;
  joined: number;
  /** REL-097 — turns whose summary named none of the refusals the same turn
   * produced. A machinery finding; it paints the badge even when the
   * escalation register is empty, because a laundered REFUSE raises no
   * escalation at all — that is exactly how it stayed invisible. */
  turns_with_hidden_refusals: number;
  measured: string;
}
export interface ReleaseScorecardEntry {
  releaseId: string;
  /** null = NOT measured (unreachable Bridge / older bundle) — `error` says why. */
  scorecard: ReleaseScorecard | null;
  error: string | null;
  checkedAt: string;
}
export interface ScorecardSnapshot {
  releases: Record<string, ReleaseScorecardEntry>;
  sweptAt: string | null;
  measured: string;
}
/** null = the endpoint has not answered; no badge is painted in that state. */
export type ScorecardMap = ScorecardSnapshot | null;

/** The row/card badge for one release's scorecard. Null when NOT measured
 * or when nothing is open — an unreachable Bridge and a clean register both
 * paint nothing, and the tip on a painted badge carries the class counts.
 * `loud` = a human is being asked ("N decisions waiting"). Pure; exported
 * so scripts/home.test.mjs measures the exact text. */
export function escalationBadge(
  entry: ReleaseScorecardEntry | null | undefined,
): { text: string; tip: string; loud: boolean } | null {
  const sc = entry?.scorecard;
  const hidden = sc?.turns_with_hidden_refusals ?? 0;
  // A hidden refusal raises NO escalation record — a turn that says "done"
  // over a REFUSE is silent by construction — so `open === 0` must not be
  // allowed to paint nothing when the ledger found one (REL-097).
  if (!sc || (sc.open === 0 && hidden === 0)) return null;
  const n = sc.decisions_for_user;
  const plural = (k: number, w: string): string => `${k} ${w}${k === 1 ? '' : 's'}`;
  const text = n > 0 ? `${plural(n, 'decision')} waiting` : sc.open > 0 ? `${sc.open} open` : `${plural(hidden, 'hidden refusal')}`;
  const tip =
    `Open escalations: ${plural(sc.decision, 'decision')} · ${sc.machinery} machinery · ${sc.blocking} blocking` +
    (n > 0 ? ` — ${n} waiting on a human.` : ' — none waiting on a human.') +
    (sc.machinery_at_user > 0
      ? ` ${sc.machinery_at_user} machinery record${sc.machinery_at_user === 1 ? '' : 's'} reached the human — a routing defect the platform owes.`
      : '') +
    ((sc.machinery_at_platform ?? 0) > 0
      ? ` ${sc.machinery_at_platform} machinery record${sc.machinery_at_platform === 1 ? '' : 's'} at the platform rung — filed into the framework’s intake, nothing for a human.`
      : '') +
    (sc.joined > 0 ? ` ${sc.joined} joined raiser${sc.joined === 1 ? '' : 's'} folded into open records.` : '') +
    (hidden > 0
      ? ` ${plural(hidden, 'turn')} summarised as done while the same turn refused something — the reply carries the machine-written footer.`
      : '') +
    ` (${sc.measured})`;
  return { text, tip, loud: n > 0 || hidden > 0 };
}

// ---------------------------------------------------------------------
// Active users (R37-R40) — GET /api/releases/fleet-presence
// ---------------------------------------------------------------------

/** One user actively in a release, as GET /api/releases/fleet-presence
 * reports it. Mirrors server/fleet-presence.ts. */
export interface PresenceUser {
  id: string;
  name: string | null;
  email: string | null;
  picture_url: string | null;
  /** 'socket' = connected right now; 'visit' = opened within the window. */
  lane: 'socket' | 'visit';
  last_visited_at: string | null;
}

export interface FleetPresenceSnapshot {
  /** releaseId -> active users. ABSENT release = measured empty (the sweep
   * ran and found nobody), distinguishable from a null snapshot. */
  releases: Record<string, PresenceUser[]>;
  windowMs: number;
  sweptAt: string | null;
  measured: string;
}

/** null = the endpoint has not answered; the cell renders an em dash and
 * the filter offers no names — never an invented "empty release". */
export type FleetPresenceMap = FleetPresenceSnapshot | null;

/** How many avatars stack before the '+N more' chip (R40: "past three"). */
export const AVATAR_STACK_MAX = 3;

export function activeUsersFor(
  row: ReleaseGridRow | undefined,
  pm: FleetPresenceMap,
): PresenceUser[] {
  if (!row || !pm) return [];
  return pm.releases[row.id] ?? [];
}

/** The display identity the filter, the tooltip and the copy lane all use —
 * ONE vocabulary (R38/R39 hover-name and filter-name must agree). */
export function activeUserName(u: PresenceUser): string {
  return u.name || u.email || `${u.id.slice(0, 8)}…`;
}

/** Per-entity display strings — what the machinery's multi-value lane
 * offers as filter checkboxes. NAMES, never picture URLs and never the
 * joined string. */
export function activeUsersDisplays(
  row: ReleaseGridRow | undefined,
  pm: FleetPresenceMap,
): string[] {
  return activeUsersFor(row, pm).map(activeUserName);
}

/** The stack decision, pure: which users get avatars, which fold into the
 * '+N more' chip. Exactly AVATAR_STACK_MAX users = no chip. */
export function activeUsersCellModel(
  users: PresenceUser[],
): { shown: PresenceUser[]; more: PresenceUser[] } {
  if (users.length <= AVATAR_STACK_MAX) return { shown: users, more: [] };
  return { shown: users.slice(0, AVATAR_STACK_MAX), more: users.slice(AVATAR_STACK_MAX) };
}

/** The hover text (R38): the NAME, plus which lane put them here — a
 * live-socket viewer and a recent visitor are different facts and the
 * tooltip says which one it is claiming. */
export function presenceTip(u: PresenceUser): string {
  return `${activeUserName(u)} · ${u.lane === 'socket' ? 'viewing now' : 'opened recently'}`;
}

export interface FleetCell {
  /** 'none' = no VM / no data. 'unknown' = there IS a VM we could not read. */
  state: 'none' | 'unknown' | 'live';
  sessions: number | null;
  panes: number | null;
  /** Running workflow agents — rendered as a THIRD chip, never folded into
   * sessions or panes: a reader reconciling the cell against the sessions
   * list must not find a phantom. null = unmeasured -> no chip; 0 = measured
   * idle -> no chip either (the cell reads exactly as before). */
  wfAgents: number | null;
  wfRuns: number | null;
  /** DEAD-IDLE badge: the Bridge's episode, or null (not dead-idle /
   * unmeasured — the chip is absent either way; the tip says which). */
  deadIdle: ReleaseDeadIdle | null;
  points: SeriesPoint[];
  windowMs: number;
  tip: string;
  /** 0..1, or null when UNMEASURED. Never 0 as a stand-in for "unknown":
   * ringHtml omits the arc at 0, so a missing number and a real 0% would be
   * pixel-identical. */
  completion: number | null;
  completionTip: string;
  /** #4950 — true when `completion` is a reading older than
   * COMPLETION_STALE_MS. The age lived only in completionTip, so a stale or
   * deliberately-monotonic high-water reading read as current progress; the
   * gauge now carries a marker on the VISIBLE surface. false when unmeasured
   * (completion === null), when the timestamp is absent, or when fresh. */
  completionStale: boolean;
  /** #4950 — the reading's age as fmtAgo text ('' when unmeasured or the
   * timestamp is absent), rendered in the visible marker when completionStale. */
  completionAgeText: string;
  /** Corpus commits that exist ONLY on the release VM (ahead of the corpus
   * remote) — rendered as a warning chip `corpus unpublished: N commits`
   * exactly when > 0. null = unmeasured (no chip); 0 = published (no chip).
   * Measured 2026-09-07: 255 unpublished commits sat invisible for 16 hours
   * until a fork refused at the cut. */
  corpusUnpublished: number | null;
  corpusTip: string;
  /** #2324 — the publication's standing AT THE STOP: a stopped VM whose
   * stamp is 'missing' or 'stale' renders a chip that says so, because an
   * absent measurement must never read as 0 (0 means "safe to delete"). */
  corpusAtStop: 'missing' | 'stale' | 'stamped' | null;
  /** ⛔ RED = the VM's volume is at or past DISK_RED_PCT on bytes or inodes.
   * Measured on i-05e1c229103ea54f0 (2026-09-07): `df -h /` 100% and
   * `df -i /` 100%, seventeen wedged processes, and the row rendered as an
   * ordinary quiet release — 0 sessions, 0 panes, nothing to look at. The
   * one state a reader most needs to see was the one the cell could not
   * show. null = UNMEASURED -> no chip (never a green one). */
  diskRed: boolean;
  diskTip: string;
}

/** The chip text for an unpublished corpus — one copy, so the grid cell,
 * the Jump Back In card and the test agree to the character. */
export function corpusUnpublishedText(n: number): string {
  return `corpus unpublished: ${n} commit${n === 1 ? '' : 's'}`;
}

/** ⛔ MIRRORED, NOT IMPORTED, from server/boot-report.ts DISK_RED_PCT. The
 * client bundle must not pull the server tree, and the two runtimes already
 * carry independent copies of this number (the python side declares it in
 * claude-shared/scripts/lib/_disk.py). The config-reference fragment
 * `disk-floor-and-parked-gc` is where all three are reconciled. */
export const DISK_RED_PCT_CLIENT = 95;

/** The Bridge's own live ring is 30 minutes — the window the owner asked the
 * home-screen graph to cover, and the same axis the in-release strip uses. */
export const FLEET_WINDOW_MS = 30 * 60_000;

/** #4950 — a completion reading is pushed on the release VM Bridge's
 * 15-minute recorder tick, so a reading older than three missed ticks is the
 * VM having STOPPED pushing, not a fresh gauge. Past this age the gauge carries
 * a visible staleness marker (ringHtml) so a stale or deliberately-monotonic
 * high-water percentage is legible WITHOUT hovering; until then the age lived
 * only in completionTip. */
export const COMPLETION_STALE_MS = 45 * 60_000;

/** The fleet columns' whole decision, as one pure function.
 *
 * WHICH ROWS GET A CELL IS NOT DECIDED HERE — the same removal frameworkCell
 * took above, for the same reason. This opened with `row.status !== 'active'`
 * until 2026-09-04: a second, client-side copy of "which releases have a VM",
 * which stopped agreeing with the server the moment the RM-on-VM flip gave
 * ideating releases real VMs. Measured on the owner's screen the same day:
 * workflow-trial showed Fleet '—' with an RM session live on its VM.
 *
 * fleet-metrics selects rows by `vmRunning` server-side, so an entry means a
 * machine is up and no entry means there is nothing to ask. The client keeps
 * NO copy: one predicate, one place, and the Fleet column cannot disagree
 * with the Framework column beside it about whether the same VM exists. */
export function fleetCell(
  row: ReleaseGridRow | undefined,
  fm: FleetMetricsMap,
  now = Date.now(),
): FleetCell {
  const empty: FleetCell = {
    state: 'none', sessions: null, panes: null, wfAgents: null, wfRuns: null, deadIdle: null,
    points: [], windowMs: FLEET_WINDOW_MS, tip: '', completion: null, completionTip: '',
    completionStale: false, completionAgeText: '',
    corpusUnpublished: null, corpusTip: '', corpusAtStop: null,
    diskRed: false, diskTip: '',
  };
  if (!row) return empty;
  if (!fm || !fm.sweptAt) return empty;
  const e = fm.releases[row.id];
  if (!e) return empty;
  const checked = `checked: ${fmtAgo(e.checkedAt, now)}`;
  // Corpus publication comes off the release ROW too (pushed by the VM
  // Bridge's corpus publisher), so a stale corpus remote is visible even
  // when the Bridge itself cannot be read.
  const corpusUnpublished =
    typeof e.corpusAheadOfRemote === 'number' && Number.isFinite(e.corpusAheadOfRemote)
      ? Math.max(0, Math.round(e.corpusAheadOfRemote))
      : null;
  // #2324 — the stamp's standing AT THE STOP, and the status beside a 0:
  // 0 ahead is emitted for BOTH an up-to-date tree and a dirty one, so a
  // bare 0 never reads "published" here without the status that earns it.
  const corpusAtStop = e.corpusPublicationAtStop ?? null;
  const corpusStatus = e.corpusPublicationStatus ?? null;
  const atStopLine =
    corpusAtStop === 'missing'
      ? 'AT STOP: UNMEASURED — no corpus publication was ever recorded for this release; this is not 0'
      : corpusAtStop === 'stale'
        ? 'AT STOP: STALE — the last stamp predates the stop by more than 6 h; the stopped volume is unmeasured'
        : '';
  const corpusTip =
    corpusUnpublished === null
      ? atStopLine
      : [
          corpusUnpublished > 0
            ? `${corpusUnpublishedText(corpusUnpublished)} — these commits exist only on the release VM; a fork cut from a checkpoint at this history refuses until they are published`
            : corpusStatus === 'up-to-date'
              ? 'corpus: published (0 commits ahead of the corpus remote, status up-to-date)'
              : `corpus: 0 commits ahead but publication status ${corpusStatus ?? 'UNRECORDED'} — NOT verified published (0 ahead is emitted for an up-to-date tree and for a dirty one)`,
          atStopLine,
          e.corpusPublicationNote ? `last verdict: ${e.corpusPublicationNote}` : '',
          `as of: ${fmtAgo(e.corpusPublicationAt ?? null, now) || 'unknown'}`,
        ].filter(Boolean).join('\n');
  // Completion comes off the release ROW (pushed by the VM Bridge on its own
  // 15-minute recorder tick), so it survives an unreachable Bridge and is
  // computed before the reachability branch below.
  const completion =
    typeof e.completionPct === 'number'
      ? Math.max(0, Math.min(1, e.completionPct / 100))
      : null;
  const completionTip =
    completion === null
      ? 'completion: unmeasured (no push from this release’s Helm server yet)'
      : [
          `completion: ${e.completionPct}%`,
          e.pointsTotal !== null && e.pointsDelivered !== null
            ? `points:     ${e.pointsDelivered} / ${e.pointsTotal}`
            : '',
          `as of:      ${fmtAgo(e.completionAt, now) || 'unknown'}`,
        ].filter(Boolean).join('\n');
  // #4950 — staleness of the reading itself, so the age reaches the VISIBLE
  // surface and not only completionTip. A present reading with an absent or
  // unparseable timestamp is NOT marked stale (we cannot measure its age).
  const completionAtMs =
    completion !== null && e.completionAt ? Date.parse(e.completionAt) : NaN;
  const completionStale =
    Number.isFinite(completionAtMs) && now - completionAtMs > COMPLETION_STALE_MS;
  const completionAgeText = completion !== null ? fmtAgo(e.completionAt, now) : '';
  if (e.sessions === null) {
    return {
      ...empty,
      state: 'unknown',
      completion,
      completionTip,
      completionStale,
      completionAgeText,
      corpusUnpublished,
      corpusTip,
      corpusAtStop,
      tip: [
        "Could not read this release's Helm server.",
        `reason:  ${e.error ?? 'unstated'}`,
        ...(corpusUnpublished !== null && corpusUnpublished > 0 ? [corpusUnpublishedText(corpusUnpublished)] : []),
        checked,
      ].join('\n'),
    };
  }
  const diskKnown = typeof e.diskPct === 'number' || typeof e.inodePct === 'number';
  const diskRed =
    (typeof e.diskPct === 'number' && e.diskPct >= DISK_RED_PCT_CLIENT) ||
    (typeof e.inodePct === 'number' && e.inodePct >= DISK_RED_PCT_CLIENT);
  const diskTip = !diskKnown
    ? 'disk: unmeasured'
    : [
        `disk:   ${e.diskPct ?? 'n/a'}% used, ${e.diskFreeGb ?? '?'}G free`,
        `inodes: ${e.inodePct ?? 'n/a'}% used, ${e.diskFreeInodes ?? '?'} free`,
        ...(diskRed
          ? ['⛔ the volume is effectively FULL — this release is NOT idle, it is wedged. '
             + 'Reclaim on the box: gc-parked-lanes.sh --apply, checkpoint-fleet.py prune',
             // 2026-09-08: the chip IS the button (owner/admin) — it runs the
             // framework\u2019s own sweep over SSM and shows the measured line.
             // On a box at 100% SSM itself is blind; the refusal names that.
             'action:  click the chip (or the row menu\u2019s Reclaim Disk) to run the reclaim now — '
             + 'refused by name if the box cannot be reached']
          : []),
      ].join('\n');
  const peak = e.points.reduce((m, p) => (p.v > m ? p.v : m), 0);
  const deadIdle = e.deadIdle ?? null;
  return {
    state: 'live',
    diskRed,
    diskTip,
    sessions: e.sessions,
    panes: e.panes,
    wfAgents: e.wfAgents ?? null,
    wfRuns: e.wfRuns ?? null,
    deadIdle,
    points: e.points,
    windowMs: e.windowMs ?? FLEET_WINDOW_MS,
    completion,
    completionTip,
    completionStale,
    completionAgeText,
    corpusUnpublished,
    corpusTip,
    corpusAtStop,
    tip: [
      `${e.sessions} live session(s), ${e.panes ?? 0} agent pane(s)`,
      ...(corpusUnpublished !== null && corpusUnpublished > 0 ? [corpusUnpublishedText(corpusUnpublished)] : []),
      // The workflow line names WHAT the third chip counts, so "8" is
      // attributable to the workflow rather than a session count nobody
      // can reconcile against the sessions list.
      ...((e.wfAgents ?? 0) > 0
        ? [`${e.wfAgents} workflow agent(s) across ${e.wfRuns ?? 0} live run(s) — SDK agents inside dynamic-workflow runs, not Bridge panes`]
        : []),
      // The dead-idle line is the Bridge's own measured line, verbatim —
      // the badge is a pointer to it, never a second computation.
      ...(deadIdle
        ? [
            `DEAD-IDLE: ${deadIdle.line}`,
            `  ${deadIdle.woke ? 'project-lead woken once' : 'wake pending'}; ${deadIdle.secondWindowDone ? `second window: ${deadIdle.outcome ?? 'done'}` : 'second window pending'}`,
          ]
        : e.deadIdleMeasured === false
          ? ['dead-idle: unmeasured (this release’s Helm server predates the detector)']
          // THE THIRD STATE, WHICH USED TO RENDER AS NOTHING.
          // `deadIdleMeasured: true` with `deadIdle: null` is a MEASURED
          // ALL-CLEAR, and it fell through the two branches above into `[]`
          // — byte-identical output to the unmeasured case once the branch
          // above is not taken. Acceptance case REL-103: measured true,
          // verdict null, and NO second surface on which to cross-check, so
          // a reader had no way to tell "the sweep looked and this release
          // is fine" from "nothing looked". Two states, one rendering, no
          // discriminator — which is exactly what the pair was built to
          // prevent one field earlier.
          : ['dead-idle: no — measured on the last sweep (this is an all-clear, not an absence of measurement)']),
      `throughput: ${e.points.length} sample(s) in the last ${Math.round((e.windowMs ?? FLEET_WINDOW_MS) / 60000)}m, peak ${Math.round(peak)} tok/s`,
      checked,
    ].join('\n'),
  };
}

/** The Jump-Back-In card's run-position line (aisdlc-docs § 30) as a PURE
 * decision over the fleet sweep: the step chip, the state word + tone, and
 * ONE text cell — the blocker while blocked, else the lever — with the whole
 * verbatim line in the tip. null = nothing painted: no sweep yet, the
 * release not swept, an unreachable/older Bridge, or its first compute in
 * flight — the card never guesses a step. The tone map is the Helm strip's
 * (helm-bridge run-position-strip.ts stateTone): an unknown state word
 * paints NEUTRAL, never red or green by accident. Exported so
 * home.test.mjs measures exactly what the card paints. */
export function runPositionCard(
  fm: FleetMetricsMap,
  releaseId: string,
  now = Date.now(),
): { step: string; state: string; tone: 'live' | 'dead' | 'stale' | 'done' | 'neutral'; text: string; tip: string } | null {
  if (!fm || !fm.sweptAt) return null;
  const e = fm.releases[releaseId];
  const rp = e?.runPosition;
  if (!rp || typeof rp.line !== 'string' || !rp.step) return null;
  const tone =
    rp.state === 'LIVE' ? 'live'
    : rp.state === 'DEAD-IDLE' ? 'dead'
    : rp.state === 'STALE' ? 'stale'
    : rp.state === 'COMPLETE' ? 'done'
    : 'neutral';
  const text = rp.blocked && rp.blocker ? rp.blocker : rp.lever ? `next: ${rp.lever}` : rp.position;
  return {
    step: `${rp.step} ${rp.label}`.trim(),
    state: rp.state,
    tone,
    text,
    tip: [rp.line, '', `computed on the release's Helm server ${fmtAgo(rp.computedAt, now)}`,
      rp.liveTurns === null ? 'live turns: unmeasured' : `live turns: ${rp.liveTurns}`].join('\n'),
  };
}

/** Attribute-safe text for the innerHTML-built grid chips. home.ts builds
 * these cells as markup strings, so any value that reaches an attribute has
 * to be escaped HERE — account-menu.ts has a private twin, and importing a
 * menu module into the grid for a string helper is the wrong dependency. */
function escapeAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** The fleet chips' four glyphs, as SPRITE REFERENCES.
 *
 * They used to be hand-inlined `<svg><path…>` art with a comment claiming
 * "the console has no sprite sheet". That claim stopped being true when
 * ensure-icons.mjs started copying the Bridge sprite into
 * `generated/icons-sprite.ts` — and the residue was invisible: the style
 * axis swaps a sprite by replacing SYMBOL BODIES, so art that is not a
 * `<use href="#i-…">` can never be re-cut per style. Under MAINFRAME '84
 * these chips kept Blueprint's smooth lucide strokes beside the rest of the
 * row's 8-bit glyphs, which is exactly the mixed set the owner saw on
 * 2026-09-07. Measured on origin/main: 3 hand-inlined slots on the Bridge
 * home, 0 elsewhere in either client — and the RED-disk chip landed as a
 * FOURTH one while this branch was open (the residue class regrows unless
 * the sprite is the only place art lives).
 *
 * The ids are the sprite's own (`square-terminal`, `crew`, `workflow`,
 * `database` — the same lucide artwork these strings were tracing, verified
 * glyph by glyph), and they are pinned in ensure-icons.mjs's REQUIRED_IDS so
 * the console build refuses if the Bridge ever drops one. `hc-ic` keeps the
 * 13px box the chips were laid out for. */
const FLEET_ICON = {
  terminal: 'square-terminal',
  crew: 'crew',
  flow: 'workflow',
  disk: 'database',
} as const;
function fleetIcon(kind: keyof typeof FLEET_ICON): string {
  return icon(FLEET_ICON[kind], 'hc-ic');
}

/** One Jump Back In card's LIVE half (R44/R45) as a pure decision: the
 * fleet/throughput cell markup and the cost text, resolved from the SAME
 * stores the grid columns read — fleetCell over the fleet-metrics sweep
 * (post-#363/#367 that sweep includes ideating rows and RM sessions, and
 * fleetCell's status guard is gone, so the cards inherit both fixes for
 * free) and costCellText over the batch cost map. `gridRow` is the card's
 * row resolved by id from the loaded list; undefined (a card whose release
 * is not in the list) degrades to the honest em dash, never an invented
 * zero. Exported so home.test.mjs measures the exact cell the cards paint. */
export function recentsCardLive(
  gridRow: ReleaseGridRow | undefined,
  fm: FleetMetricsMap,
  costs: CostMap,
  releaseId: string,
  now = Date.now(),
): Pick<CardLive, 'html' | 'tip' | 'costText' | 'costTip'> {
  // #927: the card reads the SAME decision as the column, so a card and a
  // grid row can never disagree about the same release — and the card's tip
  // is the cell's own reason rather than the flat "not measured yet" it used
  // to invent for every non-figure.
  const costCellNow = costCell(gridRow, costs, now);
  const costText = costCellNow.label;
  const costTip = costCellNow.tip;
  const c = fleetCell(gridRow, fm, now);
  if (c.state !== 'live') {
    // The corpus chip rides the card even when the Bridge is unreadable —
    // the fact is off the row (see corpusChipHtml).
    const chip = corpusChipHtml(c);
    return { html: chip || null, tip: c.tip, costText, costTip };
  }
  return {
    html:
      `<span class="hc-count">${fleetIcon('terminal')}${c.sessions}</span>` +
      `<span class="hc-count${(c.panes ?? 0) > 0 ? ' on' : ''}">${fleetIcon('crew')}${c.panes ?? 0}</span>` +
      // .hc-wave is the SAME holder the grid's Throughput cell uses — the
      // seismo-* internals are scoped under it, so the card waveform wears
      // the exact styles the column does.
      `<span class="hc-wave">${seismoSvg(c.points, { windowMs: c.windowMs, now, width: 96, height: 20, cls: 'hc-seismo', channel: 'release-throughput' })}</span>` +
      corpusChipHtml(c),
    tip: c.tip,
    costText,
    costTip,
  };
}

/** The `corpus unpublished: N commits` warning chip — rendered exactly when
 * the fact is measured AND > 0 (2026-09-07 incident). Exported so the test
 * measures the same markup the grid and the cards paint. */
export function corpusChipHtml(c: Pick<FleetCell, 'corpusUnpublished' | 'corpusTip' | 'corpusAtStop'>): string {
  // #2324 — a STOPPED VM whose publication stamp is missing or stale gets a
  // chip of its own: the absence is the fact, and it must be visible rather
  // than render as nothing (which reads like "published, nothing to show").
  if ((c.corpusAtStop === 'missing' || c.corpusAtStop === 'stale') && !(c.corpusUnpublished !== null && c.corpusUnpublished > 0)) {
    const tipS = escapeAttr(c.corpusTip);
    return `<span class="hc-count hc-corpus-unpublished hc-corpus-unmeasured" data-tip="${tipS}" `
      + `aria-label="${tipS}">corpus: unmeasured at stop</span>`;
  }
  if (c.corpusUnpublished === null || c.corpusUnpublished <= 0) return '';
  // ⛔ `data-tip` + `aria-label`, never a native `title=` — and escaped through
  // the file's ONE escapeAttr, not a hand-rolled replace chain. The
  // primitive-catalog ratchet (arm 9) pins the count of live `title=` sites per
  // file and refused this chip by name: `GREW helm-console/src/client/home.ts 8
  // site(s), pinned 7`. A browser default tooltip is not the console's tooltip:
  // installTooltips (pi-stack/src/client/tooltip.ts) owns the styled, themed,
  // screen-reader-visible one, which is exactly what a warning chip needs.
  const tip = escapeAttr(c.corpusTip);
  return `<span class="hc-count hc-corpus-unpublished" data-tip="${tip}" `
    + `aria-label="${tip}">${corpusUnpublishedText(c.corpusUnpublished)}</span>`;
}

/** Merge a SAVED grid preset with the columns that exist NOW.
 *
 * THE DEFECT THIS FIXES (measured on production 2026-08-27). The home grid
 * restores each user's saved column preset with
 * `applyColumnState({ state, applyOrder: true })`. AG Grid's ordering routine
 * (orderLiveColsLikeState, ag-grid-community main.cjs.js:1932) walks the saved
 * state, then APPENDS every column not mentioned in it to the END of the grid.
 * A preset is a snapshot of the columns that existed the day it was saved, so
 * every column shipped afterwards is exiled to the far right — and because the
 * 14-column grid needs ~1830px of minWidth against a ~1600px viewport, the
 * exiled columns land past the right edge, where AG Grid's column
 * virtualisation keeps them out of the DOM entirely. The result is a new
 * column that is fully built, fully deployed, and INVISIBLE to every existing
 * user, while looking perfect to anyone with no saved preset (a fresh browser,
 * or the developer who just built it).
 *
 * The fix keeps the user's customisation and re-homes only what they never
 * expressed an opinion about: a column absent from the preset is inserted
 * directly after its canonical predecessor, i.e. where the column list says it
 * belongs. Stale ids (columns since removed) are dropped rather than carried
 * forward as phantom entries.
 *
 * Pure and exported so scripts/home.test.mjs can measure it without a browser
 * — the layout half of this bug needs pixels, but the ORDER half is exactly
 * what this function decides, and it is the half that hid the columns. */
export function reconcileColumnState(
  saved: Array<{ colId?: string }> | null | undefined,
  canonical: readonly string[] = HOME_COLUMN_IDS,
): Array<{ colId?: string }> {
  const known = new Set(canonical);
  // Drop entries for columns that no longer exist, and any duplicate ids.
  const seen = new Set<string>();
  const kept: Array<{ colId?: string }> = [];
  for (const e of saved ?? []) {
    const id = e?.colId;
    if (typeof id !== 'string' || !known.has(id) || seen.has(id)) continue;
    seen.add(id);
    kept.push(e);
  }
  // Nothing usable survived: let the declared order stand untouched.
  if (kept.length === 0) return [];

  const out = [...kept];
  for (let i = 0; i < canonical.length; i++) {
    const id = canonical[i]!;
    if (seen.has(id)) continue;
    // Insert after the nearest canonical predecessor the preset DOES know,
    // so a new column appears where it was designed to sit.
    let at = 0;
    for (let j = i - 1; j >= 0; j--) {
      const prev = canonical[j]!;
      const idx = out.findIndex((e) => e.colId === prev);
      if (idx >= 0) { at = idx + 1; break; }
    }
    out.splice(at, 0, { colId: id });
    seen.add(id);
  }
  return out;
}

/** The column picker's rows (item 41): one entry per column, labeled by its
 * header, with visibility resolved live-state-first (a user's toggle or
 * saved preset wins) and ColDef `hide` as the default (how the absolute
 * datetime variants ship hidden until picked). Pure so home.test.mjs
 * measures the resolution without AG Grid. */
export function columnPickerEntries(
  defs: ReadonlyArray<{ colId?: string; headerName?: string; hide?: boolean }>,
  state: ReadonlyArray<{ colId?: string; hide?: boolean | null }> | null | undefined,
): Array<{ colId: string; label: string; visible: boolean }> {
  const hiddenByState = new Map<string, boolean>();
  for (const s of state ?? []) {
    if (typeof s?.colId === 'string') hiddenByState.set(s.colId, s.hide === true);
  }
  const out: Array<{ colId: string; label: string; visible: boolean }> = [];
  for (const d of defs) {
    if (typeof d.colId !== 'string') continue;
    out.push({
      colId: d.colId,
      label: d.headerName || (d.colId === 'actions' ? 'Actions' : d.colId),
      visible: hiddenByState.has(d.colId) ? !hiddenByState.get(d.colId) : d.hide !== true,
    });
  }
  return out;
}

/** The fleet-level drift alert, and the default/terminal split of the list.
 *
 * WHY A HEADER PILL EXISTS AT ALL. The per-row badge satisfies "an option to
 * update right there", but on 2026-08-27 the live list carried 32 releases and
 * the one drifted release sat below the fold — an alert an operator has to
 * scroll to find is not an alert. This is the fleet-level half of the same
 * requirement.
 *
 * Returns null when nothing is drifted, so a current fleet renders NOTHING.
 * A permanent "0 need update" is a reassuring zero: it looks identical whether
 * the sweep proved everything current or never measured anything, and it
 * trains the eye to ignore the one place drift is supposed to shout. */
export interface DriftSummary {
  count: number;
  releaseIds: string[];
  slugs: string[];
  label: string;
  tip: string;
}

export function driftSummary(fv: FleetVersions, now = Date.now()): DriftSummary | null {
  if (!fv || !fv.sweptAt) return null;
  const entries = Object.values(fv.releases ?? {});
  const drifted = entries.filter((e) => e.drift === true && e.installedSha);
  // #1022 — a row whose sha we READ and whose standing we cannot judge. This
  // class did not exist before: an unreadable latest pointer made every such
  // row `drift:false`, i.e. CURRENT, and the header stayed empty. It is NOT
  // the same class as a row with no installedSha, which has always had its own
  // 'version unknown' cell and is deliberately left out of this header.
  const unjudged = entries.filter((e) => e.drift === null && e.installedSha);
  // The tip's count is the honest union of both unmeasured classes.
  const unknown = entries.filter((e) => !e.installedSha || e.drift === null).length;
  if (drifted.length === 0) {
    // Silence when nothing was READ is the shipped contract (each such row
    // carries its own cell). Silence when the whole fleet was read and NONE of
    // it could be judged is the #1022 defect, so that case speaks.
    if (unjudged.length === 0) return null;
    return {
      count: 0,
      releaseIds: unjudged.map((e) => e.releaseId),
      slugs: unjudged.map((e) => e.slug),
      label: `${unjudged.length} unknown`,
      tip: [
        `${unjudged.length} running release${unjudged.length === 1 ? '' : 's'} could not be judged against the newest published framework.`,
        ...unjudged.map((e) => `  ${e.slug}: ${e.installedSha!.slice(0, 12)} — standing unknown`),
        `latest:  ${fv.latestSha ? fv.latestSha.slice(0, 12) : `UNREADABLE (${fv.latestError ?? 'no pointer'})`}`,
        'This is NOT "everything is up to date" — nothing was compared.',
        `checked: ${fmtAgo(fv.sweptAt, now)}`,
      ].join('\n'),
    };
  }
  const blocked = drifted.filter((e) => e.compat?.verdict === 'incompatible');
  return {
    count: drifted.length,
    releaseIds: drifted.map((e) => e.releaseId),
    slugs: drifted.map((e) => e.slug),
    label: `${drifted.length} need${drifted.length === 1 ? 's' : ''} update`,
    tip: [
      `${drifted.length} running release${drifted.length === 1 ? ' is' : 's are'} behind the newest published framework.`,
      ...drifted.map((e) => `  ${e.slug}: ${e.installedSha!.slice(0, 12)}${e.compat?.verdict === 'incompatible' ? ' — BLOCKED: incompatible base, relaunch required' : ''}`),
      ...(blocked.length > 0 ? [`${blocked.length} of these cannot take the bundle in place (base mismatch) — an update click would be refused; relaunch instead.`] : []),
      `latest:  ${fv.latestSha ? fv.latestSha.slice(0, 12) : `UNREADABLE (${fv.latestError ?? 'no pointer'})`}`,
      ...(unknown > 0 ? [`${unknown} release(s) could not be measured — see their own cells.`] : []),
      `checked: ${fmtAgo(fv.sweptAt, now)}`,
      'Click to jump to the first one.',
    ].join('\n'),
  };
}

/** Releases you can still work in, and the finished tail behind "Show more".
 *
 * The same split the release switcher already makes (release-chrome.ts
 * partitionSwitcher): active + ideating by default, completed/aborted one
 * click away. Nothing is deleted or hidden permanently — a disposed e2e run
 * and a shipped release are both still there, just not in the way.
 *
 * Incoming order is PRESERVED (the server sorts by last activity); this only
 * partitions. Note the interlock with the drift pill above: drift is computed
 * only for releases with a RUNNING VM, and a machine only stays up on an
 * active or ideating release — both of which are in the default set below —
 * so a drifted release can never be hiding behind "Show more". (Before
 * 2026-09-04 that read "only for ACTIVE releases"; the interlock survived the
 * widening to ideating because this partition already keeps ideating in the
 * default set. A future status that both keeps a VM up and lands in `rest`
 * would break it, which is why the reason is spelled out and not asserted.) */
export function partitionReleases(
  rows: ReleaseGridRow[],
): { primary: ReleaseGridRow[]; rest: ReleaseGridRow[] } {
  const primary: ReleaseGridRow[] = [];
  const rest: ReleaseGridRow[] = [];
  for (const r of rows ?? []) {
    if (r.status === 'active' || r.status === 'ideating') primary.push(r);
    else rest.push(r);
  }
  return { primary, rest };
}

/** Radial completion gauge — the console twin of the Bridge dashboard's
 * ringHtml. At 0 the arc is OMITTED (a round-capped zero-length dash still
 * paints a dot), which is also why `frac === null` must render no ring at all
 * rather than a ring at zero. */
export function ringHtml(frac: number | null, title = '', channel = '', stale = false, staleText = ''): string {
  if (frac === null) return '';
  const f = Math.max(0, Math.min(1, frac));
  const r = 7;
  const c = 2 * Math.PI * r;
  const arc =
    f > 0
      ? `<circle class="ring-fg${f >= 1 ? ' full' : ''}" cx="9" cy="9" r="${r}" ` +
        `stroke-dasharray="${(c * f).toFixed(2)} ${c.toFixed(2)}" transform="rotate(-90 9 9)"/>`
      : '';
  const esc = (t: string): string =>
    t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  // The mark stamp — the console twin of the Bridge ring's. `data-mark-set` is
  // the literal "blueprint" until the mark registry lands; that IS the
  // measurement. No tooltip attribute is added or removed (arm 9 is a ratchet;
  // #1654 T2 moved it from native `title` onto `data-tip`), and no selector
  // moves: e2e/specs/fleet-columns.spec.mjs reads `.hc-ringwrap` and
  // its innerText, both untouched by an attribute on the inner <svg>.
  const chan = channel ? ` data-mark-channel="${esc(channel)}"` : '';
  // #4950 — a stale (or deliberately-monotonic high-water) reading carries a
  // marker on the VISIBLE surface, so the age no longer lives only in the
  // tooltip. A FRESH gauge (stale === false) is byte-identical to the pre-#4950
  // output, and `frac === null` returns '' above these lines untouched.
  const staleAria = stale ? ` — STALE (${staleText || 'reading is old'})` : '';
  const staleMark = stale
    ? `<span class="hc-ringstale" data-mark-stale="1">\u26A0${staleText ? ' ' + esc(staleText) : ''}</span>`
    : '';
  return (
    `<span class="hc-ringwrap"${title ? ` data-tip="${esc(title)}"` : ''}>` +
    `<svg class="hc-ring" data-mark="ring" data-mark-set="blueprint"${chan} viewBox="0 0 18 18" role="img" ` +
    `aria-label="${Math.round(f * 100)}% complete${staleAria}">` +
    `<circle class="ring-bg" cx="9" cy="9" r="${r}"/>${arc}</svg>` +
    `<span class="hc-ringn">${Math.round(f * 100)}%</span>${staleMark}</span>`
  );
}

/** Pill class per framework state; '' only where no pill is painted (a
 * no-VM row). 'current' gained a pill with R11's 2026-09-04 ruling. */
export function frameworkPillClass(state: FrameworkState): string {
  if (state === 'drift') return 'hc-pill hc-pill-drift';
  if (state === 'unknown') return 'hc-pill hc-pill-unknown';
  if (state === 'current') return 'hc-pill hc-pill-current';
  if (state === 'updating' || state === 'resuming') return 'hc-pill hc-pill-updating';
  if (state === 'scheduled' || state === 'deferred') return 'hc-pill hc-pill-scheduled';
  if (state === 'failed') return 'hc-pill hc-pill-failed';
  return '';
}

// ---------------------------------------------------------------------
// Pure decision surfaces (measured by scripts/home.test.mjs)
// ---------------------------------------------------------------------

/** win-boot (#5512): the grid's OS cell — the release's FROZEN platform as
 * the word (it is what the cut decided and what every later launch reuses),
 * the box's own reported OS string in the tip when one has arrived. Pure so
 * scripts/home.test.mjs measures it. An unreadable frozen value is named,
 * never shown as Linux. */
export function vmOsCell(row: Pick<ReleaseGridRow, 'platform' | 'vm_os'>): { text: string; tip: string } {
  const p = row.platform ?? 'linux';
  const text = p === 'windows' ? 'Windows' : p === 'linux' ? 'Linux' : 'Unknown';
  const reported = (row.vm_os ?? '').trim();
  const tip = p === 'invalid'
    ? 'The release\u2019s frozen platform is not linux or windows \u2014 the console refuses to launch it by name.'
    : reported
      ? `Frozen at cut: ${p}. The box reported ${reported} at its last verified boot.`
      : `Frozen at cut: ${p}. No OS string reported yet (a Linux box reports none; a Windows box reports it at its first verified boot).`;
  return { text, tip };
}

export const HOME_COLUMN_IDS = [
  'name',
  'relId',
  'forkedFrom',
  'forkedInto',
  'created',
  'createdAbs',
  'owner',
  'activeUsers',
  'sourceBranch',
  'mergedIntoBranch',
  'status',
  'fleet',
  'throughput',
  'framework',
  'lastCommit',
  'lastCommitAbs',
  'lastVmActive',
  'lastVmActiveAbs',
  'lastDeploy',
  'lastDeployAbs',
  'vmStatus',
  'vmOs',
  'vmUptime',
  'vmStartedAbs',
  'testServer',
  // #4267 — THREE money columns where there was one. `totalCost` KEEPS its
  // colId (a saved column-state or a picker entry that named it still
  // resolves) and changes what it means: it is now the composed total. The
  // column that shows what `totalCost` used to show is `tokenCost`, and it
  // renders the SAME `costCell` decision, unchanged — #4360's vendor swap,
  // #1816's truncation marker and #927's five states all ride on exactly as
  // before. Nothing about the token figure is rebuilt here.
  'ec2Cost',
  'tokenCost',
  'totalCost',
  'actions',
] as const;

/** Item 41: every datetime column ships TWO variants — the relative one
 * ("5 min ago", the default) and the absolute one (the exact stamp), with
 * the absolute variant HIDDEN by default and reachable from the column
 * picker. One list so the ColDefs, the picker, and the tests agree on
 * which ids are the hidden-by-default set. */
export const ABS_VARIANT_IDS = [
  'createdAbs', 'lastCommitAbs', 'lastVmActiveAbs', 'lastDeployAbs', 'vmStartedAbs',
] as const;

export function fmtWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toISOString().slice(0, 16).replace('T', ' ');
}

/** "—" while WS7 is unlanded (null map), errors, or has no row — NEVER
 * "$0.00" for missing data. A real 0 spend row still renders as a dollar
 * figure only when WS7 actually reported one. */
export type CostState = 'measured' | 'zero' | 'pending' | 'replay' | 'unknown' | 'unmeasured';

export interface CostCell {
  state: CostState;
  /** NEVER a bare em dash. #927/#911: a money cell is a figure, or a word
   * that sends you to the reason. */
  label: string;
  /** Always non-empty. The reason, or the provenance of the figure. */
  tip: string;
  /** The figure when there is one, else null. **A truncated figure is a
   * LOWER BOUND** — read `truncated` beside it, never `usd` alone. */
  usd: number | null;
  /** #1816: true = the figure above is known to be SHORT. The label carries
   * the marker and the tip names who; this is the machine-readable twin so
   * no reader has to parse a label to find out. */
  truncated: boolean;
  /** #4360 — the vendor figure the cell displays; absent exactly when the
   * payload carried no vendor bill (the label is then NOT a figure — W01:
   * a catalog estimate renders `Unmeasured`). */
  vendor?: VendorFigure;
  /** The members that truncated it, as the payload named them. Empty when
   * the payload carries the flag without the ids — which both live routes
   * now avoid (#1844), but an older console still produces. */
  truncatedIds: string[];
}

/** The LOWER-BOUND marker (#1816). A SUFFIX, deliberately: the Total-cost
 * column's sort and export value IS the label, as text, so a prefix
 * ('>= $12.50') would sweep every truncated row to one end of the sort.
 * '$12.50+' leaves the digits where they were and still cannot be read as a
 * complete figure. */
export const COST_TRUNCATED_SUFFIX = '+';

/** #4360 — the vendor figure's text: #4391's `fmtSpend(usd, 'vendor')`.
 * `mark` (the truncation suffix) rides on the amount. */
export function fmtBilled(usd: number, mark = ''): string {
  return `$${usd.toFixed(2)}${mark} billed`;
}

/** #4371 — the coverage mark beside a vendor figure: empty when complete,
 * the token share when partial (`98% of tokens`), `coverage ?` when unknown.
 * A partial share never rounds up to read as 100%. */
export function coverageMark(coverage: CostCoverage, share: number | null): string {
  if (coverage === 'complete') return '';
  if (coverage === 'partial') {
    if (typeof share !== 'number' || !Number.isFinite(share)) return 'part of tokens';
    const pc = Math.round(share * 100);
    return pc >= 100 ? '>99% of tokens' : `${pc}% of tokens`;
  }
  return 'coverage ?';
}

/** #4360 — THE vendor figure of a cost entry, mirroring #4391's
 * `authoritativeSpend` (owner's 2026-09-17 replace ruling, quoted in #4391 at
 * 717e5ffc861b, tool-cards-workflow.ts:849-851):
 * (1) ABSENT IS NOT ZERO — undefined when nothing was billed, and the cell
 *     then shows NO figure (W01: the catalog estimate renders `Unmeasured`);
 * (2) REPLACE, NEVER SUM — when billed, this figure ALONE is displayed, and
 *     it says `billed` so the swap is visible;
 * (3) A PARTIAL BILL SAYS SO — a coverage mark unless coverage is complete. */
export function vendorFigure(e: CostEntry | undefined): VendorFigure | undefined {
  const usd = e?.vendor_usd;
  if (typeof usd !== 'number' || !Number.isFinite(usd)) return undefined;
  // A vendor figure with no (or a `none`) coverage beside it cannot be
  // complete by default.
  const coverage: CostCoverage =
    e?.vendor_coverage && e.vendor_coverage !== 'none' ? e.vendor_coverage : 'unknown';
  const share = coverage === 'unknown'
    ? null
    : typeof e?.vendor_coverage_share === 'number' && Number.isFinite(e.vendor_coverage_share)
      ? e.vendor_coverage_share
      : null;
  return { usd, basis: 'vendor', label: fmtBilled(usd), coverage, share, coverageLabel: coverageMark(coverage, share) };
}

/** The tip's vendor paragraph. Empty without a vendor figure. */
function vendorNote(v: VendorFigure | undefined): string {
  if (!v) return '';
  const cov = v.coverage === 'complete'
    ? 'the bill covers every token'
    : v.coverage === 'partial'
      ? `the bill covers ${v.share !== null ? `${(v.share * 100).toFixed(1)}%` : 'only part'} of the tokens`
      : 'nothing says how many of the tokens the bill covers';
  return `\nbasis:   VENDOR-BILLED — what the model vendor invoiced (basis vendor). It REPLACES the ` +
    `catalog price-table estimate, which is not shown and never added to it; ${cov}`;
}

/** The tip's truncation paragraph. Separated so the renders-arm and the
 * absent-arm in home.test.mjs drive the SAME text the grid shows. */
function truncationNote(ids: string[]): string {
  const who = ids.length
    ? `\ntruncated by: ${ids.join(', ')} (${ids.length} release${ids.length === 1 ? '' : 's'} in this lineage)`
    : '\ntruncated by: unnamed by this payload — the grid\'s cost-summary route sends the flag ' +
      'without the ids; GET /api/releases/<id>/cost names them';
  return (
    `\nINCOMPLETE — this figure is a LOWER BOUND, not a total. A relaunch reset the cumulative ` +
    `ledger of a release in this fork lineage, so everything it spent BEFORE that relaunch is ` +
    `missing from the number above.${who}`
  );
}

/** THE Total-cost column's whole decision, as one pure function — the twin of
 * `frameworkCell`, and deliberately the same shape.
 *
 * WHY (#927, measured on prod 2026-09-09). The owner watched daag-plan-first
 * (REL-098) render a bare em dash for TOTAL COST while its VM had been up 55
 * minutes with a detached spec-decompose run in flight. It was genuinely
 * spending: replaying the route's own query on the box returned
 * `{total_usd: 41.84, measured: true}` for that release, and the three
 * figures the owner COULD see (176.98 / 86.71 / 778.82) reproduced to the
 * cent — so the server was right and the cell was wrong.
 *
 * TWO causes, both of the blank-that-looks-like-a-fact class:
 *   1. The map is fetched ONCE at mount and had no poller at all, while
 *      slots/fleet/scorecard/presence/versions each had one. REL-098's first
 *      billable record reached the console at 05:05:25Z, 34m38s after its VM
 *      started at 04:30:47Z. A tab opened inside that window kept a map with
 *      no REL-098 key in it, and the cell stayed blank for the rest of the
 *      session while every other cell on the row went on updating.
 *   2. `costCellText` collapsed THREE different states onto one em dash: the
 *      endpoint had not answered, the release was absent from the payload,
 *      and the release was present with a null figure. None of them could be
 *      told apart, by a person or by a test.
 *
 * The states, and what each one is allowed to claim:
 *   measured — a figure the server summed. Says how fresh it is.
 *   zero     — $0.00. A MEASURED zero, or a release that has never had a VM
 *              (nothing ran, so nothing was billed) — the tip names WHICH
 *              basis, because those are different kinds of zero.
 *   pending  — a VM exists or existed and no billable record has arrived.
 *              THIS IS REL-098's CASE and the one the owner reported.
 *   replay   — a replay row. It states what is measured (no replay row has
 *              ever pushed a cost record) and stops there: `costs.ts` has no
 *              row_class filter, so there is no designed billing exemption to
 *              cite, and inventing one would be its own false fact.
 *   unknown  — we cannot say. Never $0.00: an unmeasured cost that renders as
 *              zero is the silent-success failure this file keeps refusing.
 *   unmeasured — W01: the console holds only a catalog price-table ESTIMATE
 *              for this release and no vendor bill. The estimate is NOT shown
 *              (owner ruling n134, "only ever use the authoritative figure";
 *              the 2026-09-17 replace ruling) and `usd` is null, so it never
 *              sums into a Total. Before W01 this rendered the estimate as
 *              `$X` with a tip reading `$X billed to this release`. */
export function costCell(
  row: ReleaseGridRow | undefined,
  costs: CostMap,
  now = Date.now(),
): CostCell {
  const unknown = (reason: string): CostCell => ({
    state: 'unknown', label: 'Unknown', tip: `Total cost could not be determined.\nreason:  ${reason}`, usd: null,
    truncated: false, truncatedIds: [],
  });
  if (!row) return unknown('no release row was passed to the cell renderer');
  if (!costs) {
    return unknown('the console has not answered GET /api/releases/cost-summary yet');
  }
  const e = costs.get(row.id);
  // #4360: a BILLED release displays the vendor figure alone (the owner's
  // 2026-09-17 replace ruling). Everything below it is the unbilled path,
  // unchanged from before #4360.
  const billed = vendorFigure(e);
  if (e && billed) {
    const fresh = e.as_of ? `\nas of:   ${fmtAgo(e.as_of, now)}` : '\nas of:   unstated';
    const cut = e.truncated === true;
    const ids = cut && Array.isArray(e.truncated_release_ids)
      ? e.truncated_release_ids.filter((x) => typeof x === 'string')
      : [];
    const mark = cut ? COST_TRUNCATED_SUFFIX : '';
    const label = fmtBilled(billed.usd, mark);
    return {
      state: billed.usd === 0 ? 'zero' : 'measured', label, usd: billed.usd,
      truncated: cut, truncatedIds: ids, vendor: billed,
      tip:
        `${label} to this release so far by the model vendor — the VM's cumulative token spend as ` +
        `the vendor invoiced it.${fresh}${cut ? truncationNote(ids) : ''}${vendorNote(billed)}`,
    };
  }
  if (e && e.measured && typeof e.total_usd === 'number') {
    const fresh = e.as_of ? `\nas of:   ${fmtAgo(e.as_of, now)}` : '\nas of:   unstated';
    // #1816. Everything below this line is a NO-OP when `truncated` is false
    // or absent: `mark` and `note` are both the empty string, so an
    // untruncated cell's label and tip are byte-identical to what they were
    // before this wave (home.test.mjs pins that against the pinned pre-fix
    // renderer, at the same dollar figure as the truncated fixture).
    const cut = e.truncated === true;
    const ids = cut && Array.isArray(e.truncated_release_ids)
      ? e.truncated_release_ids.filter((x) => typeof x === 'string')
      : [];
    const mark = cut ? COST_TRUNCATED_SUFFIX : '';
    const note = cut ? truncationNote(ids) : '';
    if (e.total_usd === 0) {
      return {
        state: 'zero', label: `$0.00${mark}`, usd: 0, truncated: cut, truncatedIds: ids,
        tip: `A MEASURED zero: this release has billable records and they sum to nothing.${fresh}${note}`,
      };
    }
    // W01. A NON-ZERO catalog figure with no vendor bill beside it is a
    // price-table ESTIMATE. It used to render here as `$X` with a tip saying
    // `$X billed to this release` — a billed claim nobody billed. Owner
    // ruling n134 ("only ever use the authoritative figure") and the
    // 2026-09-17 replace ruling: no figure, `usd` null (so Token and Total
    // never sum it), and the tip says why. The zero branch above stays: a
    // catalog sum of 0 is zero tokens, and nothing can be billed for them.
    return {
      state: 'unmeasured', label: 'Unmeasured', usd: null, truncated: false, truncatedIds: [],
      tip:
        'No vendor bill for this release has reached this console, so there is no billed figure to show.\n' +
        'basis:   UNMEASURED — the catalog price-table ESTIMATE this console holds for the release is ' +
        'deliberately NOT shown and NOT summed into any total (owner ruling 2026-09-17: only ever use ' +
        'the authoritative, vendor-billed figure). The token cost appears here once the vendor bill does.',
    };
  }
  if (e && !e.measured) {
    return unknown(
      'the console holds a cost record for this release but it carries no dollar figure ' +
      '(a snapshot pushed with a null tokens_usd, or events with no usd)',
    );
  }
  // ---- ABSENT FROM THE PAYLOAD. The route emits a release only once it has
  // something to sum, so absence is a real and common state — and it is
  // several different states wearing one face until they are named.
  if (row.row_class === 'replay') {
    return {
      state: 'replay', label: 'Not reported', usd: null, truncated: false, truncatedIds: [],
      tip:
        'This is a REPLAY row, and no replay environment has ever pushed a cost record to this ' +
        'console (measured 2026-09-09: 0 billable records and 0 snapshots across every replay row). ' +
        'That is an absence of reporting, not a measured zero.',
    };
  }
  if (!row.vm_instance_id) {
    return {
      state: 'zero', label: '$0.00', usd: 0, truncated: false, truncatedIds: [],
      tip:
        `No VM has ever been recorded for this release (status '${row.status}'), so nothing has run ` +
        'and nothing has been billed to it. This zero is the ABSENCE of a machine, not a summed figure.',
    };
  }
  const running = !row.vm_stopped_at;
  return {
    state: 'pending', label: 'Awaiting first report', usd: null, truncated: false, truncatedIds: [],
    tip: [
      running
        ? `This release's VM is running and has not yet pushed a cost record.`
        : `This release had a VM (stopped ${fmtAgo(row.vm_stopped_at, now) || 'at an unrecorded time'}) that never pushed a cost record.`,
      'The VM reports its cumulative spend on its own cadence, so a release that has just started ' +
        'spending shows nothing here until its first report lands — measured at 34m38s for REL-098 ' +
        'on 2026-09-09. Spend during that window is NOT lost; it arrives with the first report.',
    ].join('\n'),
  };
}

/** The release's model-key mark, beside its token spend (owner ruling
 * 2026-09-25: a release runs on its own minted key or on nothing). PURE.
 * null = the console did not answer the field (an older console) — no mark,
 * never an invented state. */
export function agentKeyMark(
  row: Pick<ReleaseGridRow, 'agent_key'> | undefined,
): { text: string; cls: string; tip: string } | null {
  const s = row?.agent_key;
  if (s === 'minted') {
    return {
      text: 'key: minted', cls: 'hc-agent-key',
      tip: 'Model key: this release holds its OWN minted key (release_secrets.xai_api_key_id is set). ' +
        'Its token spend is billed to that key alone.',
    };
  }
  if (s === 'missing') {
    return {
      text: 'key: missing', cls: 'hc-agent-key hc-agent-key-missing',
      tip: 'Model key: MISSING \u2014 this release has no key of its own (release_secrets.xai_api_key_id is NULL). ' +
        'There is no shared fallback: the next launch, reopen, framework update or seats refresh mints one, ' +
        'or refuses by name if the mint lane is not seated.',
    };
  }
  return null;
}

/** The label only — the grid's sort/export value and the cards' text.
 * Kept as the thin twin of `costCell` so there is ONE decision and two
 * readers, never two decisions. */
export function costCellText(
  row: ReleaseGridRow | undefined,
  costs: CostMap,
  now = Date.now(),
): string {
  return costCell(row, costs, now).label;
}

/** #4267 — the TOKEN half of the total, as a component. PURE, and it reads
 * the SHIPPED `costCell` rather than re-deriving anything: the token figure
 * is not rebuilt by this wave, and a second derivation of it is exactly how
 * two numbers for one noun appear on one screen.
 *
 * `measured` is the cell's own verdict, not `usd !== null`: `costCell`'s
 * `zero` state IS measured (a release that never had a VM genuinely cost
 * nothing), and its `pending`/`replay`/`unknown` states are not. */
export function tokenTotalComponent(
  row: ReleaseGridRow | undefined,
  costs: CostMap,
  now = Date.now(),
): TotalComponent {
  const c = costCell(row, costs, now);
  const measured = c.state === 'measured' || c.state === 'zero';
  return {
    name: 'Token cost',
    usd: measured ? c.usd : null,
    measured,
    reason: measured ? null : `${c.label} - ${c.tip.split('\n')[0]}`,
    truncated: c.truncated,
    truncatedIds: c.truncatedIds,
  };
}

/** #4267 — the EC2 half, as a component. */
export function ec2TotalComponent(
  row: ReleaseGridRow | undefined,
  ec2: Ec2CostMap,
): TotalComponent {
  const c = ec2CostCell(row?.id, ec2);
  const measured = c.state === 'measured' || c.state === 'zero';
  return {
    name: 'EC2 cost',
    usd: measured ? c.usd : null,
    measured,
    reason: measured ? null : `${c.label} - ${c.tip.split('\n').slice(1).join(' ').trim() || c.tip}`,
    approximate: c.approximate,
    truncated: c.truncated,
    truncatedIds: c.truncatedIds,
  };
}

/** #4267 — THE Total-cost column's whole decision. Both halves, composed by
 * `totalCostCell`, which refuses to sum an unmeasured one. */
export function releaseTotalCostCell(
  row: ReleaseGridRow | undefined,
  costs: CostMap,
  ec2: Ec2CostMap,
  now = Date.now(),
): MoneyCell {
  return totalCostCell([ec2TotalComponent(row, ec2), tokenTotalComponent(row, costs, now)]);
}

/** The shared painter for the two NEW money columns (EC2 and Total). The
 * Token column keeps its own renderer verbatim, because #4360's vendor
 * coverage chip rides on it and this wave does not touch that. */
export function paintMoneyCell(cell: MoneyCell, extraClass = ''): HTMLElement {
  const holder = document.createElement('span');
  holder.className = extraClass ? `hc-cost ${extraClass}` : 'hc-cost';
  const s = document.createElement('span');
  s.textContent = cell.label;
  if (cell.state !== 'measured') s.classList.add(`hc-cost-${cell.state}`);
  if (cell.truncated) s.classList.add('hc-cost-truncated');
  if (cell.approximate) s.classList.add('hc-cost-approximate');
  // #4267 — the em dash rides as DATA beside the word, so a reader sees the
  // dash the issue asked for and still has something to interrogate (#927).
  if (cell.dash) {
    s.dataset.costDash = cell.dashGlyph;
    s.dataset.costMeasured = 'false';
  } else {
    s.dataset.costMeasured = 'true';
  }
  s.dataset.tip = cell.tip;
  // Lane prime-ec2cost2: what the figure counts rides as data beside it.
  if (cell.basis) s.dataset.costBasis = cell.basis;
  holder.appendChild(s);
  if (cell.mark) {
    const m = document.createElement('span');
    m.className = 'hc-cost-mark';
    m.textContent = cell.mark;
    m.dataset.tip = cell.tip;
    holder.appendChild(m);
  }
  return holder;
}

/** Rendered only when the VM is RUNNING and a deployment has succeeded.
 *
 * #4593 — the release STATUS is gone from this predicate. It used to open
 * `row.status !== 'active' || …`, which is a governance field deciding
 * whether a machine is reachable. The link's real precondition is the
 * proxy's: `forwardHttp` (`src/server/proxy.ts:998`) resolves
 * `release.vm_instance_id` and forwards to `cfg.testServerPort` on that
 * instance's private IP, so a test server exists exactly when that VM is up
 * — no VM, no host to forward to, whatever the release's governance state
 * says. */
export function testServerHref(row: ReleaseGridRow, apex: string | null): string | null {
  if (vmStatusCell(row).state !== 'running') return null;
  if (!row.has_successful_deployment || !apex) return null;
  return `https://${row.slug}-test.${apex}`;
}

/** The Test-server column's whole decision (item 41), as one pure function.
 *
 * FOUR states, mapped from the row's own deployment facts — never invented:
 *   deploying  a NON-TERMINAL deployment exists (deploy_in_flight_status:
 *              queued…swapping). Wins over everything on purpose: the column
 *              reports what is happening NOW, and the tooltip says the
 *              previous deployment keeps serving until the swap.
 *   failed     the newest deployment is a failure NEWER than the last
 *              success (deploy_failed_status). Wins over live, which is the
 *              whole reason this state exists — see below.
 *   live       a RUNNING VM with a successful deployment, nothing in
 *              flight and no newer failure — the one state that gets the
 *              open-in-new-tab button (href; null on an apex-less dev host,
 *              where there is no URL scheme to open).
 *   offline    everything else, with the tooltip naming WHY (which VM
 *              state, or nothing ever deployed) — never a bare red dot.
 *
 * #4593 — THE RELEASE STATUS IS NOT AN INPUT. It was, in all three
 * predicates, and REL-107 read `Offline — Release is ideating` while its
 * test server answered HTTP 200. Widening the accept-list to admit
 * `ideating` was REFUSED by the owner: it keeps a machine column reading
 * a governance field and merely moves the boundary. The gate is now
 * `vmStatusCell(row).state === 'running'` — the same derivation the VM
 * column renders, which is what the proxy actually requires.
 *
 * WHY `failed` EXISTS (2026-09-09). The column had THREE states and none of
 * them was failure, so a release with a prior success and nothing in flight
 * rendered "Live" unconditionally. The release-side auto-deploy then failed
 * four times in a row in production from 17:39:29Z and the console's main
 * screen said "Live" the entire time. The old bundle really was still
 * serving — that is true and the tooltip says so — but "Live" answered a
 * question nobody was asking while hiding the one they were.
 *
 * `failed` KEEPS THE OPEN BUTTON when there is a prior success, because the
 * stale bundle is genuinely reachable and an operator comparing what is
 * served against what was pushed needs to open it. The tooltip is what
 * carries the honesty: it names the failure AND that the server is serving
 * OLD code. */
export interface TestServerCellModel {
  state: 'live' | 'deploying' | 'failed' | 'offline';
  text: 'Live' | 'Deploying' | 'Failed' | 'Offline';
  cls: string;
  /** The open button's target — live state only, and only under an apex. */
  href: string | null;
  tip: string;
}

/** #4593 — WHY the test-server column is DERIVED FROM THE VM, not the row's
 * governance status. The reason a machine is or is not serving is a fact
 * about the MACHINE; `status` is a workflow field an operator moves by hand.
 * `vmStatusCell` is that fact and this column is a SUBSET of it — the same
 * pure derivation the VM column renders, so the two can never disagree. */
const VM_NOT_SERVING: Record<string, string> = {
  stopped: 'the VM is STOPPED',
  gone: 'the VM is GONE',
  none: 'no VM is recorded for this release',
  provisioning: 'the VM is still coming up',
  queued: 'the VM is waiting for a slot',
  unreachable: 'the VM is RUNNING but NOT ANSWERING',
};

export function testServerCell(row: ReleaseGridRow, apex: string | null): TestServerCellModel {
  // THE VM GATE, FIRST. #4593: this used to be `row.status === 'active'`
  // spelled three times, and REL-107 — VM running, test server answering
  // HTTP 200 / 83828 bytes over the proxy's own port — rendered "Offline —
  // Release is ideating — no test server runs for it". A confident false
  // statement about a machine this function never looked at.
  const vm = vmStatusCell(row);
  const inFlight = row.deploy_in_flight_status ?? null;
  if (vm.state !== 'running') {
    const why = VM_NOT_SERVING[vm.state] ?? `the VM is ${vm.text}`;
    return {
      state: 'offline', text: 'Offline', cls: 'hc-ts hc-ts-offline', href: null,
      // The VM cell's own tip is carried through verbatim: it names the
      // instance and the remedy, and one derivation must not grow a second
      // wording of the same fact.
      tip: `No test server is serving — ${why}. ${vm.tip}` +
        (inFlight ? ` A deployment is ${inFlight}, and it has no running host to land on yet.` : ''),
    };
  }
  // From here the VM is RUNNING. The deployment facts decide, in the
  // precedence this column has always had: in flight wins, then a failure
  // newer than the last success, then a success.
  if (inFlight) {
    return {
      state: 'deploying', text: 'Deploying', cls: 'hc-ts hc-ts-deploying', href: null,
      tip: `A deployment is ${inFlight} right now` +
        (row.has_successful_deployment ? ' — the previous deployment keeps serving until the swap.' : '.'),
    };
  }
  const failed = row.deploy_failed_status ?? null;
  if (failed) {
    // Only the LIVE branch may hand out an href, so the stale-bundle link is
    // built from the same predicate rather than a second spelling of it.
    const href = testServerHref(row, apex);
    const why = row.deploy_failed_reason ? `: ${row.deploy_failed_reason}` : '';
    return {
      state: 'failed', text: 'Failed', cls: 'hc-ts hc-ts-failed', href,
      tip: row.has_successful_deployment
        ? `The last deployment ${failed}${why}. The PREVIOUS bundle is still serving` +
          (href ? ` — open ${href} (it is running OLD code)` : ' (it is running OLD code)')
        : `The last deployment ${failed}${why}, and nothing has ever deployed successfully — ` +
          'no test server is serving',
    };
  }
  if (row.has_successful_deployment) {
    const href = testServerHref(row, apex);
    return {
      state: 'live', text: 'Live', cls: 'hc-ts hc-ts-live', href,
      tip: href
        ? `Test server serving — open ${href}`
        : 'Test server serving (no apex domain on this host, so there is no URL to open)',
    };
  }
  return {
    state: 'offline', text: 'Offline', cls: 'hc-ts hc-ts-offline', href: null,
    tip: 'Nothing has deployed successfully yet — no test server is serving',
  };
}

// ---------------------------------------------------------------------
// THE VM STANDING DERIVATION LIVES IN ../server/vm-standing.ts (#4309).
//
// It used to live HERE, and that is precisely why the no-VM interstitial
// (#4278 P4, #4312) could not render "Starting, 0:42": this file is 6262
// lines with 70 `document.`/`window.` references, so a server render path
// cannot import it — the only suite that reaches these functions has to
// bundle this file with esbuild first.
//
// THIS GRID IS NOW A CONSUMER, NOT A SECOND COPY. Nothing below
// re-implements a single one of those derivations; the imports are the same
// code the server renders from, so the grid and the interstitial cannot
// drift. The re-exports keep every existing caller — and every existing arm
// in scripts/home.test.mjs — resolving exactly the names it always did.
// ---------------------------------------------------------------------
export {
  transitionStanding, vmStatusCell, vmUptimeCell, isCorpusFailure, CORPUS_FAILURE_PREFIX,
} from '../server/vm-standing.js';
export type {
  VmCellModel, TransitionBudgets, VmStandingFacts,
} from '../server/vm-standing.js';

/** Abort/reopen cell: owner/admin only (spec). */
export function actionsVisible(row: ReleaseGridRow, me: Me | null): boolean {
  if (!me) return false;
  return me.role === 'admin' || row.owner_id === me.id;
}

/** The row-action union. Two families, and the tooltips say which:
 *
 *   RUN verbs act on the RELEASE — abort (ends the run; the VM is stopped,
 *   the reaper terminates it later), reopen; and for REPLAY rows fire (the
 *   deliberate re-fire of the captured delivery) and down (terminate + row
 *   closed) — never the release lifecycle's.
 *
 *   VM verbs act on the MACHINE and leave the run alone — vm-start
 *   (POST …/vm/resume, StartInstances), vm-stop (POST …/vm/stop,
 *   StopInstances) and vm-provision (POST …/vm/provision — #1272: give an
 *   ACTIVE release a machine back when it records none, after vm-reconcile
 *   found the recorded one GONE, or both). Owner report 2026-09-08: every
 *   stopped release on the grid (REL-081/095/097 …) offered ONE button, the
 *   abort slash — "I have no option to start them again". The console owned
 *   a StartInstances lane the whole time (vm/resume; migration 055's
 *   vm_started_source); the grid simply never derived its buttons from the
 *   VM's state. REL-096 (2026-09-09) was the same defect one cell over: an
 *   Active release whose VM vm-reconcile marked `gone` also offered only
 *   abort, because vm/provision — the verb `vm-verb-table.ts` names for
 *   exactly that cell — did not exist as a route yet when this grid was
 *   written, so nothing here could have called it. */
export type ReleaseAction =
  'abort' | 'reopen' | 'fire' | 'down' | 'vm-start' | 'vm-stop' | 'vm-relaunch' | 'vm-provision';

/** The verbs that act on the machine, not the run. */
export const VM_ACTIONS: ReadonlySet<ReleaseAction> =
  new Set<ReleaseAction>(['vm-start', 'vm-stop', 'vm-relaunch', 'vm-provision']);

/** vm-verb-table.ts's verb name -> this grid's button. Deliberately partial:
 *
 *   'start'       is the FIRST activation (POST …/start) — the release's OWN
 *                 in-Helm UI calls it (helm-bridge/src/client/release-chrome.ts,
 *                 POST /__console/api/releases/:id/start), never this
 *                 fleet-management grid. Mapping it to a button here would be
 *                 a SECOND caller of a route this grid does not own the
 *                 workflow for — so this map has no entry for it, and
 *                 actionsFor()'s `tableAction` is `undefined` on that cell:
 *                 named, not silently dropped (asserted by
 *                 vm-verb-wiring.test.mjs, which greps this tree for the
 *                 route to prove the delegation is a measured fact, not an
 *                 assumption).
 *   'replay/down' is replay's own teardown verb — the replay branch below
 *                 already renders it as `down` through its own rules, never
 *                 through this map (rowClass is never passed to vmVerbFor
 *                 here, so this key is never produced by that call). */
const VM_VERB_TO_ACTION: Partial<Record<VmVerbName, ReleaseAction>> = {
  'vm/provision': 'vm-provision',
  'vm/resume': 'vm-start',
  'vm/relaunch': 'vm-relaunch',
  reopen: 'reopen',
};

/** THE row-action decision: row class × lifecycle status × the MEASURED VM
 * state. The row's PRIMARY verb — which one of start / resume / relaunch /
 * provision / reopen owns getting this release a machine or closing it out —
 * is READ from vm-verb-table.ts (vmCellOf + vmVerbFor, the same functions the
 * server's routes call), never re-decided here; that is the whole fix for
 * #1272 (the row must not keep its own guess). vm-stop and the STANDING
 * vm-relaunch override are NOT in that table's domain (it answers "who
 * provisions the machine", never "can I stop or replace the one that's
 * there") and stay driven by the same VM-presence facts they always were —
 * see their own comments below. Order is the render order: VM verb(s) first,
 * run verb last.
 *
 *   release, live (ideating|active), settled (no transition, no health-check
 *   red — those pre-empt the table: a pipeline already owns the row, or a
 *   human must look before the console offers anything sharper than abort):
 *     VM live    → [vm-stop, <table verb>, abort]
 *     VM stopped → [<table verb>, vm-relaunch-override, abort]
 *     VM none/gone → [<table verb if any>, abort]
 *       table verb is 'vm/relaunch' on active+live (folded into the vm-stop
 *       row, not doubled), 'vm/resume' on any status+stopped, 'vm/provision'
 *       on active+{none,gone} (#1272 — the REL-096 cell), 'start' (no button
 *       here — see VM_VERB_TO_ACTION) on ideating+{live,none,gone}.
 *   release, terminal → [reopen] (table names 'reopen' for EVERY vm cell
 *     here; the vm state changes only what `does`/`boots` say, never the verb)
 *   replay, active:
 *     VM running  → [fire, vm-stop, down]
 *     VM stopped  → [vm-start, down]
 *     gone / in transition → [down]  (down closes the row; 0 EC2 writes on a gone instance)
 *   replay, ideating → [down]
 *   replay, terminal → []  (a closed replay is never reopened — recreate from the checkpoint) */
export function actionsFor(row: ReleaseGridRow): ReleaseAction[] {
  const vmDisplay = vmStatusCell(row).state;
  if (row.row_class === 'replay') {
    if (row.status === 'active') {
      if (vmDisplay === 'running') return ['fire', 'vm-stop', 'down'];
      if (vmDisplay === 'stopped') return ['vm-start', 'down'];
      return ['down'];
    }
    if (row.status === 'ideating') return ['down'];
    return [];
  }
  if (row.status === 'ideating' || row.status === 'active') {
    // A transition already owns the row (a pipeline is mid-boot/mid-stop),
    // and the health-check axis (running-but-unreachable) is orthogonal to
    // vm-verb-table.ts entirely — neither is a cell of that table, so both
    // pre-empt it exactly as they always did: RED with no auto-remedy button
    // beyond abort, never a guess at which verb to offer mid-flight.
    if (vmDisplay === 'queued' || vmDisplay === 'provisioning' || vmDisplay === 'unreachable') {
      return ['abort'];
    }
    // The remaining vmStatusCell states map 1:1 onto vm-verb-table.ts's own
    // vocabulary — vmCellOf(row) would answer the same thing from the raw
    // row facts; reusing vmDisplay here just avoids computing it twice.
    //
    // #4246 P1 — 'corpus-failure' is a DISPLAY state on transition_error and
    // is NOT a cell of vm-verb-table.ts (its four cells are facts about the
    // MACHINE). The verbs a corpus failure leaves the user are the EXISTING
    // ones — abort, and a Start that this failure never refuses — so the
    // cell is read from the raw row facts through vmCellOf, exactly as the
    // comment above says it could always have been. Offering fewer verbs
    // here would be the read layer taking a lever away over a display state.
    const vm: VmCell = vmDisplay === 'corpus-failure'
      ? vmCellOf(row)
      : vmDisplay === 'running' ? 'live' : vmDisplay;
    const { verb } = vmVerbFor({ status: row.status, vm });
    const tableAction = VM_VERB_TO_ACTION[verb];
    const stop: ReleaseAction[] = vm === 'live' ? ['vm-stop'] : [];
    // vm-relaunch is a STANDING operator override, not this table's verb: the
    // server's own gate (releases.ts relaunchVm) accepts it whenever status
    // is ideating|active, a vm_instance_id is recorded and vm-reconcile has
    // not marked it gone — live OR stopped, regardless of which verb this
    // cell names — so a machine that IS the AMI's superseded base can be
    // force-replaced even while resume (or, on ideating+live, nothing) is
    // the table's own answer. Skipped when the table verb already IS
    // relaunch, so active+live never renders it twice.
    const relaunchOverride: ReleaseAction[] =
      (vm === 'live' || vm === 'stopped') && tableAction !== 'vm-relaunch' ? ['vm-relaunch'] : [];
    return [...stop, ...(tableAction ? [tableAction] : []), ...relaunchOverride, 'abort'];
  }
  return ['reopen'];
}

/** The verb's destructive class — it drives the button intent, the confirm
 * dialog's danger emphasis and the menu's danger row, from ONE place. abort
 * ends a run; down TERMINATES a machine; fire SPENDS (real agent turns);
 * vm-stop ends every live agent turn on the VM mid-flight (the run survives,
 * the turns do not). vm-start, vm-provision and reopen build, never destroy —
 * vm-provision launches into a cell that already has NOTHING to snapshot or
 * terminate (that is exactly why it, not vm-relaunch, owns the cell). */
export function actionIsDestructive(a: ReleaseAction): boolean {
  return a === 'abort' || a === 'down' || a === 'fire' || a === 'vm-stop' || a === 'vm-relaunch';
}

/** The named failure card for a lifecycle verb's refusal. The server's
 * lifecycle routes (replay/down, vm/stop) classify an EC2 refusal
 * (ec2-refusal.ts: `cause`, `action`, `principal`, `remedy`); this renders
 * that shape as a TITLED notice — "The console's role lacks
 * ec2:TerminateInstances" — and falls back to the route's own `error` line
 * for every other body. It never returns the raw AWS CLI dump as a title.
 * Measured 2026-09-08: the owner's Down click on REL-091/092 surfaced
 * `aws ec2 terminate-instances: aws: [ERROR]: An error occurred
 * (UnauthorizedOperation) …` verbatim in the notice dialog. */
export function actionFailureNotice(
  action: ReleaseAction,
  status: number,
  body: { error?: string; cause?: string; action?: string | null; principal?: string | null; remedy?: string; instance_id?: string } | null,
): { title: string; body: string } {
  const verb = ACTION_VERB[action];
  const b = body ?? {};
  if (b.cause === 'iam-denied') {
    const role = (b.principal ?? '').replace(/^arn:aws:sts::\d+:assumed-role\//, '').split('/')[0] || 'the console';
    return {
      title: `${verb} refused — the console's role lacks ${b.action ?? 'the EC2 grant'}`,
      body: `EC2 refused ${b.action ?? 'the call'} for ${b.instance_id ?? 'the recorded instance'} as role ${role}. ` +
        `${b.remedy ?? 'Check the console role policy.'} Nothing changed on the row.`,
    };
  }
  if (b.cause === 'not-found') {
    return {
      title: `${verb} refused — the recorded VM is gone from EC2`,
      body: `${b.instance_id ?? 'The recorded instance'} no longer exists in EC2. ${b.remedy ?? ''}`.trim(),
    };
  }
  return {
    title: `${verb} refused (${status || 'no answer'})`,
    body: b.error ?? (status === 0 ? 'The console did not answer (network).' : `refused (${status})`),
  };
}

/** The verb, as the confirm primary and the failure card name it. */
export const ACTION_VERB: Record<ReleaseAction, string> = {
  abort: 'Abort release',
  reopen: 'Reopen release',
  fire: 'Fire replay',
  down: 'Down replay',
  'vm-start': 'Start VM',
  'vm-stop': 'Stop VM',
  'vm-relaunch': 'Relaunch VM',
  'vm-provision': 'Provision VM',
};

/** The at-cap 409 body renders VERBATIM — used/total + derivation, the
 * governor's counted refusal (never a bare "at capacity"). */
export function reopenRefusalMessage(status: number, body: { used?: number; total?: number; detail?: string; error?: string }): string {
  if (status === 409 && typeof body.used === 'number' && typeof body.total === 'number') {
    return `Reopen refused at capacity: ${body.used}/${body.total}${body.detail ? ` — ${body.detail}` : ''}`;
  }
  return body.error ?? `refused (${status})`;
}

// ---------------------------------------------------------------------
// Item 55 — the row/header context menus, pins and display vocabulary.
// PURE decision surfaces, exported so scripts/home.test.mjs measures the
// enable/disable matrix without a browser (the module's standing pattern).
// ---------------------------------------------------------------------

/** Shipped default pins (owner 55.8): the name column + the release number.
 * The seat's `pins` field overrides (absent = these; [] = unpinned). */
export const DEFAULT_HOME_PINS = ['name', 'relId'] as const;

/** The apex every release link hangs under (releaseHref, cardHref,
 * testServerHref, the post-create redirect). It is the SERVER'S configured
 * domains[0], handed over on /api/me — never a guess from location.hostname.
 * The guess it replaces took the last two labels of the hostname, which is
 * the apex only when the console sits on a two-label domain: a console at
 * qa.example.dev linked every release to {slug}.example.dev — another
 * console, which answered "unknown release host". On a plain-IP / localhost
 * dev host there is no subdomain scheme (null: links degrade to '—'/'#'),
 * and a server that names no apex gets null, not a guess. */
export function consoleApex(me: Pick<Me, 'apex'> | null, hostname: string): string | null {
  if (hostname === 'localhost' || /^[0-9.]+$/.test(hostname)) return null;
  const a = me?.apex;
  return typeof a === 'string' && a.length > 0 ? a : null;
}

/** The URL the name cell links to — reused by Open Release so the menu and
 * the anchor can never open different places. Null on a dev host with no
 * subdomain scheme. */
export function releaseHref(row: Pick<ReleaseGridRow, 'slug'>, apex: string | null): string | null {
  return apex ? `https://${row.slug}.${apex}/` : null;
}

/** Fork Release — LIVE (FW5 T5.6; the 55.3 stub flipped). One capability
 * seat, every disabled state named: replay rows never fork (sealed
 * observation chambers), and the create verb is the release-owner bar
 * (admin or the row's owner — the fork endpoint enforces role server-side
 * regardless). No row (the recents ghost card) = disabled with the reason. */
export function forkCapability(me: Me | null, row?: ReleaseGridRow | null): { enabled: boolean; tip: string } {
  if (!row) return { enabled: false, tip: 'Release not in the loaded list' };
  if (row.row_class === 'replay') {
    return { enabled: false, tip: 'Replay envs never fork — fork a RELEASE row\'s checkpoint instead' };
  }
  if (!actionsVisible(row, me)) return { enabled: false, tip: 'Release owner or admin only' };
  return {
    enabled: true,
    tip: 'Fork from a checkpoint: an exact clone of this release at that moment — quiet at birth',
  };
}

/** Snapshot This Moment (owner rename 2026-09-05 — the old pushpin-verb
 * label is deliberately not quoted here: a comment carrying it would
 * false-positive every absence sweep, the admin-ui.ts deletion doctrine;
 * the rename exists because two pushpins in one menu read as one feature)
 * — the lane-2 deliberate fork
 * point (design §5): quiesce the fleet at a settled boundary, capture
 * fork-grade, release. Active releases only; every disabled state names
 * its why. The function keeps its pin* code identity: the action id, the
 * /pin-moment routes and the fork library's pinned-checkpoint state are
 * API/vocabulary contracts, not user-facing copy. */
export function pinCapability(me: Me | null, row?: ReleaseGridRow | null): { enabled: boolean; tip: string } {
  if (!row) return { enabled: false, tip: 'Release not in the loaded list' };
  if (row.row_class === 'replay') return { enabled: false, tip: 'Replay envs are not capture sources' };
  if (!actionsVisible(row, me)) return { enabled: false, tip: 'Release owner or admin only' };
  if (row.status !== 'active') {
    return { enabled: false, tip: `A snapshot quiesces a LIVE fleet — release is '${row.status}'` };
  }
  return {
    enabled: true,
    tip: 'Hold the fleet at a settled boundary, capture a fork-grade checkpoint (pinned in the fork library), release — ~30s',
  };
}

export type RowMenuAction =
  | 'open' | 'pin-recent' | 'update-framework' | 'update-framework-quiescent' | 'reread-framework' | 'reclaim-disk' | 'fork' | 'pin-moment'
  | 'abort' | 'reopen' | 'fire' | 'down' | 'vm-start' | 'vm-stop' | 'vm-relaunch' | 'vm-provision'
  | 'deploy-test-server';

export interface RowMenuEntry {
  action: RowMenuAction;
  label: string;
  icon: string;
  enabled: boolean;
  /** Inline annotation on a disabled item ("Up to date"). */
  note?: string;
  /** Hover title carrying the WHY ("Fork is coming", the drift facts). */
  tip?: string;
  danger?: boolean;
}

/** The whole row-menu decision (owner 55.1/55.2/55.3).
 *
 * UPDATE FRAMEWORK ENABLE RULE (stated in the PR body): enabled exactly when
 * the framework cell measures DRIFT — a running VM whose installed sha is
 * behind the newest published bundle (fleet-versions lane) — AND the user
 * can act on the cell's own pill (owner/admin; since the R15 merge the
 * pill IS the update — the separate button this sentence used to name is
 * gone). Every other state is DISABLED WITH ITS WHY: current -> "Up to
 * date" (the owner's words); unknown -> the unreadable-VM reason (never
 * conflated with current); none -> no running VM to update; updating ->
 * an install is already running (migration 056); failed -> the column
 * badge carries the details; drift-but-not-owner -> owner/admin only. */
export function buildRowMenuSpec(
  row: ReleaseGridRow,
  me: Me | null,
  fv: FleetVersions,
  apex: string | null,
  pinnedRecent = false,
  now = Date.now(),
  /** The fleet-metrics sweep, for the disk-red gate on Reclaim Disk.
   * Optional so older callers and tests keep compiling; absent = the
   * reclaim item is offered disabled ("disk not red"), never hidden. */
  fleet: FleetMetricsMap | null = null,
): RowMenuEntry[] {
  const out: RowMenuEntry[] = [];
  const href = releaseHref(row, apex);
  out.push({
    action: 'open', label: 'Open Release', icon: 'arrow-up-right', enabled: !!href,
    ...(href ? {} : { tip: 'No subdomain scheme on this host (dev install)' }),
  });

  // R46 — pin to Jump Back In, right from the grid row (or a card: both
  // surfaces serve THIS one spec). Per-user, never owner-gated: the pin is a
  // private shelf arrangement, not an action on the release. Distinct from
  // 'Snapshot This Moment' below, which quiesces a fleet — this one moves a card.
  out.push({
    action: 'pin-recent',
    label: pinnedRecent ? 'Unpin from Jump Back In' : 'Pin to Jump Back In',
    icon: 'pin',
    enabled: row.status !== 'aborted',
    ...(row.status === 'aborted'
      ? { tip: 'Aborted releases never show in Jump Back In' }
      : { tip: pinnedRecent
          ? 'Return this card to plain recency ordering'
          : 'Keep this release on the Jump Back In shelf (leftmost, until unpinned)' }),
  });

  const cell = frameworkCell(row, fv, now);
  const mayAct = actionsVisible(row, me);
  const updEnabled = cell.canUpdate && mayAct;
  out.push({
    action: 'update-framework', label: 'Update Framework', icon: 'circle-arrow-up',
    enabled: updEnabled,
    ...(cell.state === 'current' ? { note: 'Up to date' } : {}),
    ...(cell.state === 'unknown' ? { note: 'version unknown' } : {}),
    ...(cell.state === 'updating' ? { note: 'Updating…' } : {}),
    ...(cell.state === 'resuming' ? { note: 'Resuming…' } : {}),
    ...(cell.state === 'scheduled' ? { note: 'scheduled: next quiet window' } : {}),
    // #889: 'failed' is now RETRYABLE (owner request) and 'drift' can be
    // behind-but-blocked, so the note names which — and when the sweep
    // measured WHY (the pre-click base verdict), the more specific note wins.
    ...(cell.state === 'failed' ? { note: 'last update failed — retry' } : {}),
    ...(cell.state === 'drift' && !cell.canUpdate ? { note: 'behind, but blocked' } : {}),
    // #4101 — the menu row itself says the target is the VERIFIED bundle and
    // not the pointer. The tip beside it names both shas.
    ...(cell.offer?.kind === 'last-verified' ? { note: 'installs the last VERIFIED bundle' } : {}),
    ...(cell.blocked ? { note: 'blocked: incompatible base' } : {}),
    ...(updEnabled
      ? { tip: cell.tip }
      : {
          tip:
            cell.state === 'drift' && !mayAct
              ? 'Release owner or admin only'
              // #889: a disabled entry names its OWN reason. It used to fall
              // to the flat 'No running VM to update' for every 'none' row,
              // which was a guess — the cell now carries the measured why.
              : cell.refusal ?? cell.tip ?? undefined,
        }),
  });

  // 2026-09-08: RE-READ — a fresh fleet-versions sweep on click. Offered on
  // every measured row (a running VM exists by construction of the entry),
  // and it is the way out of 'version unknown' without waiting for the
  // cadence sweep.
  const hasEntry = !!fv?.releases?.[row.id];
  out.push({
    action: 'reread-framework', label: 'Re-read Framework Version', icon: 'list-restart',
    enabled: hasEntry && cell.state !== 'updating' && cell.state !== 'resuming',
    tip: hasEntry
      ? 'Ask the VM what it runs right now (one fleet-versions sweep) — the way out of "version unknown"'
      : 'No running VM to read',
  });

  // 2026-09-08: RECLAIM — the RED disk chip's action, in the menu too. Only
  // offered when the fleet sweep measured this row RED; the chip on the row
  // is the same verb.
  const fc = fleet ? fleetCell(row, fleet, now) : null;
  const diskRed = !!fc?.diskRed;
  out.push({
    action: 'reclaim-disk', label: 'Reclaim Disk on the VM', icon: 'server',
    enabled: diskRed && mayAct,
    ...(diskRed ? {} : { note: 'disk not red' }),
    tip: diskRed
      ? (mayAct
          ? 'Run the framework\u2019s reclaim on the box now (gc-parked-lanes.sh --apply, checkpoint-fleet.py prune) and show the measured line'
          : 'Release owner or admin only')
      : 'Offered only when the fleet sweep measured this VM\u2019s root volume RED (>= 95%)',
  });

  // The quiescent lane: same drift gate, never ends a live turn — the VM's
  // Bridge opens a quiet window and the install runs inside it.
  out.push({
    action: 'update-framework-quiescent', label: 'Update at Quiet Window', icon: 'circle-arrow-up',
    enabled: updEnabled,
    ...(cell.state === 'scheduled' ? { note: 'already scheduled' } : {}),
    tip: updEnabled
      ? 'Schedule the update for the next moment with no agent turn live — nothing is ended mid-turn'
      : cell.state === 'scheduled'
        ? cell.tip
        : cell.state === 'drift' && !mayAct
          ? 'Release owner or admin only'
          : 'Nothing to schedule',
  });

  const fork = forkCapability(me, row);
  out.push({ action: 'fork', label: 'Fork Release', icon: 'git-branch', enabled: fork.enabled, tip: fork.tip });
  const pin = pinCapability(me, row);
  // Owner rename 2026-09-05: the label says SNAPSHOT and the glyph is the
  // camera — 'Pin to Jump Back In' above keeps the one pushpin in this menu.
  out.push({ action: 'pin-moment', label: 'Snapshot This Moment', icon: 'camera', enabled: pin.enabled, tip: pin.tip });

  // Lifecycle verbs — the SAME decision the action buttons render from
  // (actionsFor × actionsVisible), invoked through the same onAction path.
  if (mayAct) {
    for (const a of actionsFor(row)) {
      out.push({
        action: a,
        label: ACTION_VERB[a],
        // The same glyph the row button wears (ACTION_ICON) — 'fire' rides
        // the spend glyph, not the innocuous play triangle: it starts real,
        // billed agent turns. 'vm-start' is the one that gets the triangle.
        icon: ACTION_ICON[a],
        enabled: true,
        tip: ACTION_TITLE[a],
        danger: actionIsDestructive(a),
      });
    }
  } else {
    out.push({ action: 'abort', label: 'Abort', icon: 'x', enabled: false, tip: 'Release owner or admin only', danger: true });
  }

  // Deploy Test Server — the existing deploy-now lane. The server's
  // mayRedeploy gate is BROADER than owner/admin (stakeholders deploy too),
  // so the client disables only what is structurally undeployable and lets
  // the server refuse the rest VERBATIM (409 no-branch, 403, 503 no seat).
  const replay = row.row_class === 'replay';
  out.push({
    action: 'deploy-test-server', label: 'Deploy Test Server', icon: 'server',
    enabled: !replay,
    ...(replay ? { tip: 'Replay envs are disposable observation environments — they never deploy' } : {}),
  });
  return out;
}

/** One row of POST /api/releases/fleet-update (server FleetFrameworkUpdateRow). */
export interface FleetUpdateRow {
  releaseId: string;
  slug: string;
  installedSha: string | null;
  latestSha: string | null;
  compat: 'compatible' | 'incompatible' | 'unknown';
  result: string;
  reason: string;
  remedy: string | null;
}
export interface FleetUpdateResult {
  dry_run: boolean;
  mode: string;
  latest_sha: string | null;
  swept_at: string | null;
  rows: FleetUpdateRow[];
  counts: Record<string, number>;
  measured: string;
}

/** The update route's answer, as the client reads it. */
export interface UpdateFrameworkResponse {
  measured?: string;
  error?: string;
  noop?: boolean;
  /** 202 (migration 056): the install was accepted and runs BEHIND this
   * answer — the durable row state is what to watch, in every tab. */
  accepted?: boolean;
  update?: string;
  /** 202: which lane accepted — 'quiescent' (next quiet window) or absent /
   * 'immediate'. The third value this once named, 'hot', is RETIRED (#3972):
   * the route refuses `mode:'hot'` 400 by name (#2414 / #3402). */
  mode?: string;
  to_sha?: string | null;
  from_sha?: string | null;
  started_at?: string;
  needs_confirmation?: boolean;
  /** 202 with `mode:'quiescent'`: scheduled for the VM's next quiet window;
   * `message` is the sentence naming the live-turn count. */
  scheduled?: boolean;
  live_turns_now?: number | null;
  /** Everything the restart would end. */
  active_sessions?: number;
  /** The breakdown behind that total (panes die in the same restart). */
  active_agent_sessions?: number;
  active_panes?: number;
  message?: string;
  sessions_ended?: number | null;
}

export interface UpdateFrameworkOutcome {
  /** 'gate-refused' (#828) is a REFUSAL BY A GATE — nothing was fetched,
   * restarted or pinned — and is never rendered as a failure. Plain
   * 'refused' stays what it was: any other non-2xx answer. */
  kind: 'accepted' | 'done' | 'noop' | 'confirm' | 'refused' | 'gate-refused' | 'scheduled';
  /** The measured live-session count, when the server reported one — the
   * server's number, never one this client derived. */
  activeSessions: number | null;
  /** The sentence to put in front of the person. */
  text: string;
  /** Present only on 'confirm': the label of the button that does the ending. */
  confirmLabel?: string;
  /** Present only on 'gate-refused' (#828): the server's structured facts,
   * so the column can render the reason and offer the remedy verb. */
  cause?: { cause: string; vm_base: string | null; bundle_base: string; bundle_sha: string; remedy: string } | null;
}

/** What Update Framework does with ONE server answer (owner ruling
 * 2026-09-03: warn about live sessions, do not prevent).
 *
 * Exported as a pure decision because the wiring below is its only caller —
 * so measuring this function measures what the menu item actually does.
 *
 * THE 409 CONTROL. The update route answers 409 for several unrelated
 * reasons (no running VM to install onto, a replay row refuses the verb). Only
 * the one carrying `needs_confirmation` is a question. Every other 409 is a
 * refusal and renders as one — never as a "shall I end your sessions?"
 * dialog for a release that was never going to update anyway. */
export function updateFrameworkOutcome(
  name: string,
  status: number,
  data: UpdateFrameworkResponse | null,
): UpdateFrameworkOutcome {
  if (status === 409 && data?.needs_confirmation === true
      && typeof data.active_sessions === 'number') {
    const n = data.active_sessions;
    return {
      kind: 'confirm',
      activeSessions: n,
      // The server's own sentence, verbatim — it was written from the count
      // the guard measured. The fallback still names that same number.
      text: data.message
        ?? `${n} agent session${n === 1 ? '' : 's'} ${n === 1 ? 'is' : 'are'} running on this VM, `
           + `and updating ends ${n === 1 ? 'it' : 'them'}.`,
      // Only call them sessions when they all are. With panes in the count
      // the button stays true by not naming a noun it would get wrong —
      // the sentence above it has already been specific.
      confirmLabel: (data.active_panes ?? 0) > 0
        ? `End all ${n} and update`
        : `End ${n} session${n === 1 ? '' : 's'} and update`,
    };
  }
  if (status === 202 && data?.scheduled === true) {
    // The quiescent lane: nothing installs until the VM's Bridge reports a
    // quiet window; the column's durable 'scheduled' state carries the
    // wait, with the server's own live-turn sentence.
    const n = typeof data.live_turns_now === 'number' ? data.live_turns_now : null;
    return {
      kind: 'scheduled',
      activeSessions: n,
      text: data.message
        ?? `scheduled: will apply at the next quiet window (${n === null ? 'live turns unmeasured' : `${n} live turn${n === 1 ? '' : 's'}`} now)`,
    };
  }
  if (status === 202 && data?.accepted === true) {
    // The production lane since migration 056: the install runs behind
    // this answer and the column's durable 'updating' state carries it —
    // there is nothing to pop up (R14) and nothing to wait on here.
    return {
      kind: 'accepted',
      activeSessions: null,
      text: `${name}: update accepted — installing${data.to_sha ? ` ${String(data.to_sha).slice(0, 12)}` : ''}.`,
    };
  }
  if (status === 200) {
    if (data?.noop === true) {
      return { kind: 'noop', activeSessions: 0, text: `${name} was already current — nothing installed.` };
    }
    // A synchronous install verdict (a provisioner without the async
    // signal). Same rendering as noop: the pill settles to Current — the
    // sentence exists for the trail, not for a popup (R14).
    const ended = typeof data?.sessions_ended === 'number' ? data.sessions_ended : null;
    return {
      kind: 'done',
      activeSessions: ended,
      text:
        `${name} updated${ended ? `, ending ${ended} session${ended === 1 ? '' : 's'}` : ''}. `
        + `${data?.measured ?? ''}`.trimEnd(),
    };
  }
  if (status === 409 && (data as { refused?: boolean } | null)?.refused === true) {
    // #828 — the base-compat gate said no. The system worked: nothing was
    // fetched, restarted or pinned. It gets its OWN kind so the column can
    // paint 'Update refused' with the reason and the remedy, never the red
    // 'Update failed' dead end the owner measured on REL-096.
    const d = data as unknown as { error?: string; cause?: string; vm_base?: string | null; bundle_base?: string; bundle_sha?: string; remedy?: string };
    return {
      kind: 'gate-refused',
      activeSessions: null,
      text: d.error ?? 'The update was refused by a gate; nothing was installed.',
      cause: typeof d.cause === 'string' && typeof d.bundle_base === 'string' && typeof d.bundle_sha === 'string'
        ? { cause: d.cause, vm_base: d.vm_base ?? null, bundle_base: d.bundle_base, bundle_sha: d.bundle_sha, remedy: d.remedy ?? 'retry' }
        : null,
    };
  }
  return {
    kind: 'refused',
    activeSessions: null,
    text: `Update refused (${status}): ${data?.error ?? 'unstated'}`,
  };
}

/** The body of the SECOND call: the authorisation, carrying the number it
 * covers. The FIRST call sends `{}` — consent is never a default, and never
 * inferred from the fact that this is a retry. The count is a CEILING: if the
 * fleet grew past it the server asks again instead of sweeping the extras. */
export function confirmTerminateBody(acknowledged: number): Record<string, unknown> {
  return { confirm_terminate_sessions: true, acknowledged_sessions: acknowledged };
}

export interface HeaderMenuEntry {
  id: 'customize' | 'copy-column' | 'pin' | 'autosize' | 'reset' | 'clear-filters';
  label: string;
  icon: string;
  enabled: boolean;
  sep?: boolean;
  tip?: string;
}

/** The header context menu (owner 55.6 + the follow-up trio). Clear All
 * Filters is disabled — with the reason inline — when nothing is filtered,
 * so the menu never offers a no-op that looks like an action. */
export function headerMenuSpec(isPinned: boolean, anyFilters: boolean): HeaderMenuEntry[] {
  return [
    { id: 'customize', label: 'Customize Columns', icon: 'panels-top-left', enabled: true },
    { id: 'copy-column', label: 'Copy Column Values', icon: 'clipboard-list', enabled: true },
    { id: 'pin', label: isPinned ? 'Unpin Column' : 'Pin Column', icon: 'pin', enabled: true },
    { id: 'autosize', label: 'Auto-size Columns', icon: 'chevrons-right', enabled: true, sep: true },
    { id: 'reset', label: 'Reset Columns to Default', icon: 'list-restart', enabled: true },
    { id: 'clear-filters', label: 'Clear All Filters', icon: 'x', enabled: anyFilters,
      ...(anyFilters ? {} : { tip: 'No column filters are active' }) },
  ];
}

/** The display text for one cell — what the eye reads IS what the filter
 * lists, the copy lanes emit and doesFilterPass matches (one vocabulary).
 * Datetime and pill columns override the machine-y valueGetter text; every
 * other column (including ones other lanes add later) rides the defs
 * fallback automatically. */
export function gridDisplayValue(
  defs: ReadonlyArray<ColDef<ReleaseGridRow>>,
  colId: string,
  row: ReleaseGridRow,
): string {
  const dash = (s: string): string => (s === '—' ? '' : s);
  switch (colId) {
    case 'created': case 'createdAbs': return dash(fmtWhen(row.created_at));
    case 'lastCommit': case 'lastCommitAbs': return dash(fmtWhen(row.last_commit_at));
    case 'lastVmActive': case 'lastVmActiveAbs': return dash(fmtWhen(row.last_vm_activity_at));
    case 'lastDeploy': case 'lastDeployAbs': return dash(fmtWhen(row.last_deployed_at ?? null));
    case 'vmStartedAbs': return dash(fmtWhen(row.vm_started_at ?? null));
    case 'status': return statusPillText(row);
    case 'vmStatus': return vmStatusCell(row).text;
    default: return displayValueFromDefs(defs, colId, row);
  }
}

/** Item D (bridge-ui-polish) — what the release search matches: EVERY
 * column's display value, the same vocabulary the eye reads, the filters
 * list and Copy emits (gridDisplayValue). One vocabulary; a column another
 * lane adds later is searchable automatically via the defs fallback. */
export function releaseSearchHaystack(
  defs: ReadonlyArray<ColDef<ReleaseGridRow>>,
  row: ReleaseGridRow,
): string {
  return HOME_COLUMN_IDS.map((c) => gridDisplayValue(defs, c, row)).join(' ').toLowerCase();
}

export function statusPillClass(status: ReleaseGridRow['status']): string {
  return `hc-pill hc-pill-${status}`;
}

/** A cell that renders relative time and keeps counting.
 *
 * The text is painted once here so the cell is never briefly blank, and the
 * `data-rel-time` attribute hands it to the page-wide ticker
 * (startRelativeTimeTicker) — one interval for the whole page rather than one
 * per cell. `title` keeps the exact stamp, because relative time is friendlier
 * to read and useless for anything precise.
 */
function relTimeCell(iso: string | null, kind: 'plain' | 'vm' = 'plain'): HTMLElement {
  const el = document.createElement('span');
  el.dataset.relTime = iso ?? '';
  if (kind === 'vm') el.dataset.relKind = 'vm';
  el.textContent = kind === 'vm' ? formatVmActivity(iso) : formatRelativeLong(iso);
  const abs = absoluteStamp(iso);
  if (abs) el.dataset.tip = abs;
  return el;
}

/** The ABSOLUTE variant of a datetime cell (item 41): the exact stamp, no
 * ticker (a fixed instant never re-renders), an em dash for no value. The
 * relative variant's tooltip already carried this text; the column makes it
 * a first-class, sortable, exportable value. */
function absTimeCell(iso: string | null): HTMLElement {
  const el = document.createElement('span');
  el.textContent = absoluteStamp(iso) || '—';
  return el;
}

/** Icon + accessible label for the two row actions.
 *
 * Text-only buttons reading "abort"/"reopen" were the previous shape. Icons
 * carry the destructive/constructive distinction at a glance — and because an
 * icon alone is not a label, each keeps its word visible next to the glyph and
 * an aria-label besides.
 */
export const ACTION_ICON: Record<ReleaseAction, string> = {
  abort: 'ban',
  reopen: 'rotate-cw',
  fire: 'zap',   // it SPENDS — see the prompts map's `fire` entry
  down: 'trash-2', // TERMINATE + close: destruction, distinct from vm-stop's square and abort's slash
  'vm-start': 'play',   // StartInstances — the one triangle in the set
  'vm-stop': 'square',  // StopInstances — the universal stop glyph
  'vm-relaunch': 'refresh-cw', // snapshot → terminate → fresh instance (rotate-cw is reopen's)
  'vm-provision': 'plus', // give the release a machine where it has none — nothing to snapshot or terminate
};

/** The tooltip = the accessible name. Each one says WHICH thing the verb
 * acts on — the run or the machine — because the owner read one slash
 * glyph on every stopped row and had no way to tell "abort the release"
 * from "stop the VM" (2026-09-08). */
export const ACTION_TITLE: Record<ReleaseAction, string> = {
  abort: 'Abort this release — ends the RUN; its VM is stopped (not terminated) and the reaper terminates it after the retention window',
  reopen: 'Reopen this release (takes a slot)',
  fire: 'Fire the captured pending delivery — the parked replay fleet starts taking REAL agent turns',
  down: 'Down this replay — TERMINATES its VM and closes the row (a replay is disposable; this is not a stop)',
  'vm-start': 'Start this release\'s VM (EC2 StartInstances) — the machine only; the release stays as it is',
  'vm-stop': 'Stop this release\'s VM (EC2 StopInstances) — the machine only: it parks and frees its slot; the release is NOT aborted',
  'vm-relaunch': 'Relaunch this release\'s VM on a FRESH instance (current AMI + bundle) — snapshots the disks, TERMINATES this machine and ends its live fleet; the release stays as it is',
  'vm-provision': 'Give this release a machine (EC2 RunInstances on the current AMI) — it records none right now (or vm-reconcile found the recorded one gone), so nothing is snapshotted and nothing is terminated; the release stays as it is',
};
/** What the Status cell should SAY.
 *
 * The owner, watching their own reopen: "I would want to see a status of
 * shutting down and a status of starting." The terminal status is deliberately
 * not flipped until the gate passes, so the grid read 'aborted' for the ten
 * minutes a reopen spent starting and gating — accurate about the state
 * machine, useless as an answer to "is anything happening?".
 *
 * The transition WINS the label when present, because it is the more
 * informative of the two and it is transient by construction (the server
 * clears it in a finally). The terminal status is still what the pill is
 * COLOURED by, so a release never changes colour for a transient reason. */
/** The FORK badge's whole decision, as one pure function over the row's
 * server decoration (fork.ts decorateForkRows) — exported so home.test
 * drives every branch. Four shapes, each unmistakable at a glance:
 *   refused    the boot gate REFUSED/INCONCLUSIVE and the VM was reaped —
 *              red, the verdict named, the remedy in the title (the row
 *              used to read only "Ideating");
 *   verified   FORK VERIFIED (N/N) — green; the lineage line in the title;
 *   unverified a boot measured claims that miss, or no boot yet — amber,
 *              the count named; misses in the title;
 *   corpus-only  a fork NO marriage gate governs — neutral, lineage only and
 *              NO fraction: not 0/0 either, since an n/m whose m counts a
 *              gate that never applies is a false health signal whichever
 *              numerator it carries (#1781);
 *   plain      pre-decoration row (older server) — the lineage badge only.
 * A fork marker reads identically in every theme (fixed-color doctrine). */
export interface ForkBadgeModel {
  kind: 'refused' | 'verified' | 'unverified' | 'corpus-only' | 'plain';
  text: string;
  cls: string;
  title: string;
  /** "forked from <slug> @ <captured_at>" — the provenance line the card
   * and the title both carry. */
  lineage: string;
}

/** WS8 P3 (#2838): the checkpoint picker's DB column — the wire shape of
 * fork.ts's ForkReleaseDbSummary, read null-tolerantly. */
export interface CkptReleaseDb {
  status: string;
  mode: string;
  location?: string;
  schema_floor: number | null;
  table_count: number;
  row_count: number;
  bytes: number;
  excluded_count: number;
  reason: string | null;
  sidecar_in_store?: boolean | null;
}

/** 1.2 MB / 412 KB / 80 B — one decimal above KB, 1024-based. */
export function fmtDbBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '? B';
  if (n < 1024) return `${Math.round(n)} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}

/** The pre-WS8-A wording — a manifest with NO release_db block at all. */
export const DB_CELL_PRE_WS8A = 'DB: not recorded — manifest predates WS8-A (no release_db block)';

/** The ONE renderer for the picker's DB line. Every input renders a
 * sentence that starts `DB:` — a null block is the pre-WS8-A manifest, an
 * undefined block is a pre-P3 server, and every non-captured status names
 * its reason. Never blank, never a bare tick (issue #2838). */
export function forkReleaseDbCellText(rd: CkptReleaseDb | null | undefined): string {
  if (rd === undefined) return 'DB: not reported — this console predates the checkpoints DB surface';
  if (rd === null) return DB_CELL_PRE_WS8A;
  const why = rd.reason ? ` — ${rd.reason}` : ' — reason not named';
  switch (rd.status) {
    case 'captured': {
      const floor = rd.schema_floor === null ? 'floor unknown' : `floor ${rd.schema_floor}`;
      const excl = rd.excluded_count > 0 ? ` (${rd.excluded_count} excluded)` : '';
      return `DB: ${rd.table_count} table${rd.table_count === 1 ? '' : 's'} / ${rd.row_count} row${rd.row_count === 1 ? '' : 's'}` +
        ` / ${fmtDbBytes(rd.bytes)} @ ${floor}${excl}`;
    }
    case 'skipped': return `DB: skipped${why}`;
    case 'sidecar-missing': return `DB: sidecar missing${why}`;
    case 'over-budget': return `DB: over budget${why}`;
    case 'abandoned': return `DB: abandoned${why}`;
    default: return `DB: ${rd.status}${why}`;
  }
}

/** The hover — mode and location, so an operator can tell a console-taken
 * slice (P1, S3 sidecar) from a VM-taken one (WS8-A, inside package.tgz). */
export function forkReleaseDbCellTitle(rd: CkptReleaseDb | null | undefined): string {
  if (!rd) return forkReleaseDbCellText(rd);
  const where = rd.location === 's3-sidecar' ? 'S3 sidecar (db/ under the checkpoint prefix)' : 'inside package.tgz on the VM';
  return `${forkReleaseDbCellText(rd)}\nmode ${rd.mode}; CSVs ${where}` +
    (rd.sidecar_in_store === false ? '\nsidecar NOT in the store' : '');
}

/** What the fork column CANNOT answer, made answerable.
 *
 * Every fork consumer in this file reads CHILD -> PARENT
 * (`fork_of_release_id`): the "Forked From" column, `forkBadgeModel`, the
 * detail lineage line. DRIVEN at origin/main d323fa86e: `forked_into`,
 * `fork_children` and `forks_of` each read **0 files** across the repo
 * (control: `fork_of_release_id` reads 9 sites in this file alone), so
 * "has anything been forked OFF this release?" has no surface at all —
 * and the answer is a `.filter()` over an array the client already holds.
 *
 * THE DISCRIMINATOR. Naively that filter is the API-surface defect this
 * unit is about: `0` would be published for BOTH "nothing was ever forked
 * off this release" and "the children exist but are not in the rows this
 * client loaded" (a filtered grid, a paged fetch, a purged source). Those
 * are different answers and they must not render alike, so the reading
 * carries its own POPULATION — `scanned`, the number of rows the filter
 * actually ran over — and `complete`, whether the caller vouched that
 * those rows are the whole release list. `complete: false` renders "1 of
 * the 40 loaded", never a bare "1".
 *
 * Returns null when the question does not apply (no parent id), which is
 * the fourth state and is NOT zero.
 */
export interface ForkChildrenReading {
  /** Children found among `rows`. Never a guess: it is a filter result. */
  count: number;
  /** Their display names, shortest-id fallback, in the order given. */
  names: string[];
  /** How many rows the filter ran over — the population behind `count`. */
  scanned: number;
  /** True only when the caller states `rows` is the whole release list. */
  complete: boolean;
}

export function forkChildrenOf(
  rows: readonly Pick<ReleaseGridRow, 'id' | 'name' | 'rel_id' | 'fork_of_release_id'>[],
  parentId: string | null | undefined,
  opts?: { complete?: boolean },
): ForkChildrenReading | null {
  if (!parentId) return null;
  const kids = rows.filter((r) => r.fork_of_release_id === parentId);
  return {
    count: kids.length,
    names: kids.map((k) => k.name || k.rel_id || `${String(k.id).slice(0, 8)}\u2026`),
    scanned: rows.length,
    complete: opts?.complete === true,
  };
}

/** The cell text for `forkChildrenOf`, three-valued by construction.
 *
 * null (not applicable)      -> the em dash
 * count 0, complete          -> 'none'            (a MEASURED all-clear)
 * count 0, not complete      -> 'none of N loaded' (UNMEASURED beyond N)
 * count > 0                  -> the names, with the population when partial
 *
 * The two zero cases are the whole point: before this, both were nothing.
 */
export function forkChildrenCellText(reading: ForkChildrenReading | null): string {
  if (!reading) return '\u2014';
  const where = reading.complete ? '' : ` of ${reading.scanned} loaded`;
  if (reading.count === 0) return `none${where}`;
  const shown = reading.names.slice(0, 3).join(', ');
  const more = reading.names.length > 3 ? `, +${reading.names.length - 3} more` : '';
  return `${reading.count}${where}: ${shown}${more}`;
}

export function forkBadgeModel(
  row: Pick<ReleaseGridRow, 'fork_of_release_id' | 'fork_checkpoint_id' | 'fork_provenance' | 'fork_boot' | 'fork_verify'>,
  sourceName?: string | null,
): ForkBadgeModel | null {
  if (!row.fork_of_release_id) return null;
  const p = row.fork_provenance ?? null;
  const src = sourceName ?? p?.source_slug ?? `release ${row.fork_of_release_id.slice(0, 8)}…`;
  const ck = (p?.checkpoint_id ?? row.fork_checkpoint_id ?? '?').slice(0, 10);
  const lineage = `forked from ${src} @ ${p?.captured_at ?? `checkpoint ${ck}…`}`;
  const seat = p?.autostaff_on_boot;
  const seatLine = seat === true ? 'autostaff on boot: ON (by seat)'
    : seat === false ? 'autostaff on boot: OFF (by seat — born quiet)'
    : 'autostaff on boot: inherit the source fleet\'s posture at capture';
  if (row.fork_boot) {
    const v = row.fork_boot.verdict.toUpperCase();
    return {
      kind: 'refused', lineage,
      text: `FORK GATE ${v === 'INCONCLUSIVE' ? 'INCONCLUSIVE' : 'REFUSED'}`,
      cls: 'hc-badge-fork hc-badge-fork-bad',
      title: `${lineage}\nBoot gate ${v}: ${row.fork_boot.summary}\nRemedy: ${row.fork_boot.remedy}`,
    };
  }
  const fv = row.fork_verify ?? null;
  // The gate-applicability read comes FIRST of the verify branches, because
  // both of the others render a fraction. A `corpus-only` fork runs no
  // marriage gate, so there is no counter to show — and the count it would
  // have shown is the one the gate left behind during an earlier `full`
  // period (#1781: REL-098 read amber FORK 1/7 for two days after a
  // retry-boot mode change, on a release nothing was wrong with).
  // Strictly `=== false`: a pre-#1781 server omits the field, and absent
  // must keep meaning "gate applies" or every older payload loses its badge.
  if (fv && fv.gate_applies === false) {
    return {
      kind: 'corpus-only', lineage, text: 'FORK', cls: 'hc-badge-fork',
      title:
        `${lineage}\nCorpus-only fork: the corpus was restored, the fleet package was NOT — ` +
        `no fork boot leg runs and NO marriage gate applies, so there is no verification ` +
        `count for this release.\n${seatLine}`,
    };
  }
  if (fv?.verified) {
    return {
      kind: 'verified', lineage,
      text: `FORK ✓ ${fv.passed}/${fv.checked}`,
      cls: 'hc-badge-fork hc-badge-fork-ok',
      title: `${lineage}\n${fv.verdict}\n${seatLine}`,
    };
  }
  if (fv) {
    return {
      kind: 'unverified', lineage,
      text: `FORK ${fv.boot_ok === null ? '(unbooted)' : `${fv.passed}/${fv.checked}`}`,
      cls: 'hc-badge-fork hc-badge-fork-warn',
      title: `${lineage}\n${fv.verdict}\n${seatLine}`,
    };
  }
  return {
    kind: 'plain', lineage, text: 'FORK', cls: 'hc-badge-fork',
    title:
      `${lineage} — an exact clone of that moment, quiet at birth (D2): autostaff OFF and zero ` +
      `auto-resumed turns at boot; every session wakes at its settled boundary on first engagement. ` +
      `${seatLine}.`,
  };
}

export function statusPillText(
  row: Pick<ReleaseGridRow, 'status' | 'transition_state' | 'slot_queued_at' | 'transition_since' | 'transition_error'>,
  /** #3288: same optional seat as vmStatusCell — a pill whose transition has
   * outlived its lane's budget reads 'stale transition' rather than a
   * confident 'starting'. Absent budgets change nothing. */
  opts?: { budgets?: TransitionBudgets | null; now?: number },
): string {
  // #4246 P1 (owner scope 2026-09-18) — CORPUS FAILURE, first in precedence
  // (corpus-class error > transition > slot queue > status). ONE predicate,
  // vm-standing's isCorpusFailure; the VM cell and GET /api/releases/:id's
  // `corpus_failure` read the same one. Not a fifth status: the row still
  // IS 'ideating', and an accepted Start clears the error as it always has.
  if (isCorpusFailure(row)) return 'corpus failure';
  const t = row.transition_state;
  if (t && transitionStanding(row, opts?.budgets, opts?.now)?.stale) return 'stale transition';
  // The slot-queue wait (migration 068) is the same kind of fact as a
  // transition — what the release is doing NOW — and reads as one; a real
  // transition (a pipeline owning the row) still wins.
  if (!t) return row.slot_queued_at ? 'waiting for slot' : row.status;
  return t === 'shutting-down' ? 'shutting down'
    : t === 'starting' ? 'starting'
    : t === 'gating' ? 'verifying'
    : t;
}

/** #4199 P2 — the plan-kick CHIP beside the status pill, as a pure model
 * so a suite can drive it without a DOM. null = nothing to chip (kicked,
 * none, or a list from a console that predates the field). `retry` is true
 * ONLY when blocked — an owed release is still re-arming by itself and a
 * Retry there would be a second kicker on one release. The tip carries the
 * blocked row's remedy sentence VERBATIM (the same line the journal has). */
export function planKickChipModel(
  pk: PlanKickInfo | null | undefined,
): { text: string; cls: string; tip: string; retry: boolean } | null {
  if (!pk) return null;
  const cls = (pk.failure_class ?? 'unclassified (pre-#4199)');
  if (pk.state === 'owed') {
    return {
      text: `owed (${pk.prior_failed_kicks} failed)`,
      cls: 'hc-pill hc-pill-plankick hc-pill-plankick-owed',
      tip: `initial plan owed: ${pk.prior_failed_kicks} failed kick(s), last class ${cls} — the console re-arms it on the next boot or send (ceiling ${pk.ceiling} since the last retry)` +
        (pk.bridge_detail ? ` — last Bridge detail: ${pk.bridge_detail.slice(0, 240)}` : ''),
      retry: false,
    };
  }
  if (pk.state === 'blocked') {
    return {
      text: `blocked: ${cls}`,
      cls: 'hc-pill hc-pill-plankick hc-pill-plankick-blocked',
      tip: pk.remedy ?? `blocked after ${pk.prior_failed_kicks} failed kick(s), class ${cls}`,
      retry: true,
    };
  }
  return null;
}

/** POST /api/releases/:id/plan-kick/retry — the chip's Retry. Answers the
 * server's `plan_kick` (200 on a fired retry, 409 with the current state
 * when the release was not blocked) so the chip re-renders from the
 * answer, never from a guess. */
export async function postPlanKickRetry(
  releaseId: string,
  fetchJson: (method: string, url: string) => Promise<{ status: number; data: unknown }> = (m, u) => jsonFetch(m, u),
): Promise<{ status: number; plan_kick: PlanKickInfo | null; error: string | null; measured: string | null }> {
  const { status, data } = await fetchJson('POST', `/api/releases/${encodeURIComponent(releaseId)}/plan-kick/retry`);
  const d = (data ?? {}) as { plan_kick?: PlanKickInfo | null; error?: string; measured?: string };
  return { status, plan_kick: d.plan_kick ?? null, error: d.error ?? null, measured: d.measured ?? null };
}

/** Paint the chip (and its Retry, when blocked) into `holder`, replacing
 * whatever chip was there — the click re-paints from the POST's answer. */
export function renderPlanKickChip(holder: HTMLElement, row: Pick<ReleaseGridRow, 'id' | 'plan_kick'>): void {
  holder.querySelectorAll('.hc-plankick').forEach((n) => n.remove());
  const model = planKickChipModel(row.plan_kick);
  if (!model) return;
  const chip = document.createElement('span');
  chip.className = `${model.cls} hc-plankick`;
  chip.textContent = model.text;
  chip.dataset.tip = model.tip;
  holder.appendChild(chip);
  if (!model.retry) return;
  const btn = mkIconBtn({
    glyph: 'refresh-cw',
    label: 'Retry plan kick (after the record or the VM is fixed)',
    intent: 'neutral',
    size: 'sm',
    cls: 'hc-plankick hc-plankick-retry',
  });
  btn.addEventListener('click', async () => {
    setBtnState(btn, 'busy');
    const out = await postPlanKickRetry(row.id);
    setBtnState(btn, 'enabled');
    if (out.plan_kick) {
      row.plan_kick = out.plan_kick;
      renderPlanKickChip(holder, row);
      if (out.measured) {
        const painted = holder.querySelector<HTMLElement>('.hc-plankick');
        if (painted) painted.dataset.tip = `${out.measured} — ${painted.dataset.tip ?? ''}`;
      }
    } else {
      chip.dataset.tip = `retry answered ${out.status}${out.error ? `: ${out.error}` : ''} — ${model.tip}`;
    }
  });
  holder.appendChild(btn);
}

/** prime-carryretry — GET /api/releases serves `carry_hold` (null = none)
 * when a relaunch's carry RESTORE refused: the package is retained, the
 * pointer HELD, and no launch boots until an operator retries the boot from
 * that package. `card` is the server's one sentence; `retry` names the call. */
export interface CarryHoldInfo {
  manifest_key: string;
  reason: string;
  line?: string;
  at?: string;
  instance_id?: string | null;
  card: string;
  retry: { method: 'POST'; path: string; body: { fromCarry: string } };
}

/** The held-carry CHIP beside the status pill, as a pure model (the
 * plan-kick chip's shape). null = no hold. */
export function carryHoldChipModel(
  h: CarryHoldInfo | null | undefined,
): { text: string; cls: string; tip: string; retry: boolean } | null {
  if (!h || !h.manifest_key) return null;
  return {
    text: 'carry held',
    cls: 'hc-pill hc-pill-plankick hc-pill-plankick-blocked',
    tip: `${h.card} — no launch boots without it; Retry boots FROM that package (no capture, no snapshot, no terminate) once its cause is fixed`,
    retry: true,
  };
}

/** POST /api/releases/:id/vm/relaunch {fromCarry} — the chip's Retry. */
export async function postCarryRetry(
  releaseId: string,
  manifestKey: string,
  fetchJson: (method: string, url: string, body?: unknown) => Promise<{ status: number; data: unknown }> = (m, u, b) => jsonFetch(m, u, b),
): Promise<{ status: number; error: string | null; measured: string | null }> {
  const { status, data } = await fetchJson(
    'POST', `/api/releases/${encodeURIComponent(releaseId)}/vm/relaunch`, { fromCarry: manifestKey },
  );
  const d = (data ?? {}) as { error?: string; measured?: string };
  return { status, error: d.error ?? null, measured: d.measured ?? null };
}

/** Paint the held-carry chip (and its Retry) into `holder`, replacing any
 * earlier one. The answer is written onto the chip's tip, never guessed. */
export function renderCarryHoldChip(holder: HTMLElement, row: Pick<ReleaseGridRow, 'id' | 'carry_hold'>): void {
  holder.querySelectorAll('.hc-carryhold').forEach((n) => n.remove());
  const hold = row.carry_hold;
  const model = carryHoldChipModel(hold);
  if (!model || !hold) return;
  const chip = document.createElement('span');
  chip.className = `${model.cls} hc-carryhold`;
  chip.textContent = model.text;
  chip.dataset.tip = model.tip;
  holder.appendChild(chip);
  const btn = mkIconBtn({
    glyph: 'refresh-cw',
    label: 'Retry the boot from the retained carry package',
    intent: 'neutral',
    size: 'sm',
    cls: 'hc-carryhold hc-carryhold-retry',
  });
  btn.addEventListener('click', async () => {
    setBtnState(btn, 'busy');
    const out = await postCarryRetry(row.id, hold.manifest_key);
    setBtnState(btn, 'enabled');
    chip.dataset.tip = `retry answered ${out.status}${out.error ? `: ${out.error}` : out.measured ? `: ${out.measured}` : ''} — ${model.tip}`;
  });
  holder.appendChild(btn);
}

export function statusPillClassFor(
  row: Pick<ReleaseGridRow, 'status' | 'transition_state' | 'slot_queued_at' | 'transition_error'>,
): string {
  // #4246 P1: a corpus failure is a FAILURE pill (seat: src/client/styles.css
  // beside .hc-pill-busy), stacked on the status colour — the status did not
  // change, the last corpus upgrade on it did.
  if (isCorpusFailure(row)) return `${statusPillClass(row.status)} hc-pill-corpus-failure`;
  // Colour by the terminal status; add a busy modifier so the transient label
  // is visually distinguishable without inventing a fifth status colour.
  return row.transition_state || row.slot_queued_at
    ? `${statusPillClass(row.status)} hc-pill-busy`
    : statusPillClass(row.status);
}

/** GET /api/slots — the fields the widget reads (the route serves more). */
export interface SlotsSnapshot {
  used: number;
  total: number;
  queued?: number;
  holders?: Array<{ rel_id: string | null; slug: string; status: string; row_class: string; idle_min: number; live_sessions: number | null }>;
  queue?: Array<{ rel_id: string | null; slug: string; position: number; waited_s: number }>;
  next_expected_free?: string | null;
}

/** `5/12`, or `12/12 · 2 queued` when releases are waiting for a slot. */
export function slotsWidgetText(s: SlotsSnapshot): string {
  const q = s.queued ?? s.queue?.length ?? 0;
  return q > 0 ? `${s.used}/${s.total} · ${q} queued` : `${s.used}/${s.total}`;
}

/** The hover: who holds the slots (idle age, live sessions) and who waits. */
export function slotsWidgetTip(s: SlotsSnapshot): string {
  const idle = (m: number): string =>
    m >= 1440 ? `${Math.floor(m / 1440)}d` : m >= 60 ? `${Math.floor(m / 60)}h` : `${m}m`;
  const lines = [`VM slots: ${s.used} of ${s.total} held`];
  for (const h of s.holders ?? []) {
    lines.push(
      `  ${h.rel_id ?? h.slug} — ${h.status}${h.row_class !== 'release' ? ` [${h.row_class}]` : ''}, ` +
        `idle ${idle(h.idle_min)}, ${h.live_sessions === null ? 'sessions unmeasured' : `${h.live_sessions} live`}`,
    );
  }
  const q = s.queue ?? [];
  if (q.length) {
    lines.push(`Waiting for a slot (${q.length}):`);
    for (const e of q) lines.push(`  ${e.position}. ${e.rel_id ?? e.slug} — waited ${Math.floor(e.waited_s / 60)}m`);
    if (s.next_expected_free) lines.push(`Next slot expected to free ~${new Date(s.next_expected_free).toLocaleTimeString()} (idle-stop estimate)`);
  }
  return lines.join('\n');
}

export function buildColumnDefs(ctx: {
  me: Me | null;
  apex: string | null;
  costs: () => CostMap;
  /** #4267 — the EC2 half of the cost-summary answer. */
  ec2Costs?: () => Ec2CostMap;
  versions: () => FleetVersions;
  fleet: () => FleetMetricsMap;
  /** R37: the fleet-presence snapshot behind the Active-users column.
   * Optional so older callers and tests keep compiling; absent renders
   * every cell as an em dash (no data, never an invented empty). */
  presence?: () => FleetPresenceMap;
  onAction: (action: ReleaseAction, row: ReleaseGridRow) => void;
  onUpdateFramework: (row: ReleaseGridRow) => void;
  /** R16/R17: click a failed framework badge -> the stored measured
   * detail. Optional so older callers/tests keep compiling. */
  onShowUpdateFailure?: (row: ReleaseGridRow, detail: string | null) => void;
  /** Click an 'Update blocked' pill -> the pre-click refusal (both base
   * hashes) and the remedy. Optional so older callers/tests keep compiling. */
  onShowUpdateBlocked?: (row: ReleaseGridRow, blocked: { reason: string; remedy: string }) => void;
  /** 2026-09-08: click on a 'version unknown' pill -> one fresh sweep.
   * Optional so older callers/tests keep compiling. */
  onRereadFramework?: (row: ReleaseGridRow) => void;
  /** 2026-09-08: click on the RED disk chip -> the reclaim verb. */
  onReclaimDisk?: (row: ReleaseGridRow) => void;
  /** THIS TAB's transient framework state (row-state.ts). Optional; absent
   * = no local lane (tests drive frameworkCell's pure combinations). */
  fwLocal?: (releaseId: string) => FrameworkLocalState | null;
  /** Item 55: branch cell -> forge URL, from the config-seat template
   * (client-config). Null = no link (never a guessed URL). Optional so
   * older callers and tests keep compiling. */
  branchHref?: (branch: string | null | undefined) => string | null;
  /** FW5 T5.6: resolve a release id to its display identity for fork
   * lineage (the FORK badge + Forked From column). Null = not in the
   * loaded list — the short id renders instead. Optional (older callers). */
  releaseRef?: (id: string) => { name: string; slug: string } | null;
  /** The WHOLE loaded release list, for the fork INVERSE (`forkChildrenOf`).
   * `releaseRef` answers child -> parent; nothing answered parent ->
   * children, so "has anything been forked off this release?" had no
   * surface. Optional, and its absence is the reason the reading carries
   * `complete`: without this accessor the column renders the em dash rather
   * than a `0` that cannot tell "none" from "none loaded". */
  allReleaseRows?: () => readonly ReleaseGridRow[] | null;
  /** REL-096: the release's escalation scorecard entry (GET /api/scorecard)
   * for the Name cell's "N decisions waiting" badge. Null = not measured,
   * which paints nothing. Optional (older callers/tests). */
  scorecard?: (releaseId: string) => ReleaseScorecardEntry | null;
  /** #3288: the budgets GET /api/releases published with THIS load
   * (`transition_budgets`). Optional so older callers/tests keep compiling;
   * absent = no lane has a budget, so a transition renders its age and is
   * never called stale. */
  transitionBudgets?: () => TransitionBudgets | null;
  /** #3288: click a 'Stale transition (Ns) — click to reconcile' cell ->
   * POST /api/releases/:id/transition/reconcile. Optional; absent leaves the
   * cell rendering the sentence without a handler (it is still the honest
   * reading of the row). */
  onReconcileTransition?: (row: ReleaseGridRow) => void;
}): ColDef<ReleaseGridRow>[] {
  /** A branch cell: an anchor when the seat gives us a URL, plain text
   * otherwise — identical for source and merged-into (item 55.10). */
  const branchCell = (branch: string | null | undefined): HTMLElement | string => {
    const name = branch ?? '';
    if (!name) return '—';
    const href = ctx.branchHref?.(name) ?? null;
    if (!href) return name;
    const a = document.createElement('a');
    a.href = href;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.className = 'hc-branch-link';
    a.textContent = name;
    return a;
  };
  return [
    {
      colId: 'name', headerName: 'Name', field: 'name', flex: 2, minWidth: 160,
      cellRenderer: (p: { data?: ReleaseGridRow; value?: string }) => {
        const a = document.createElement('a');
        const row = p.data;
        const href = row ? releaseHref(row, ctx.apex) : null;
        if (row && href) {
          a.href = href;
          // Item 29 (owner): clicking a release opens its Helm in a NEW tab —
          // the home grid stays put behind it. A real anchor (not
          // window.open), so cmd-click / middle-click stay natural. The '#'
          // fallback below deliberately stays tabless: a dead link must not
          // spawn a blank duplicate of this page.
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
        } else {
          a.href = '#';
        }
        a.textContent = p.value ?? '';
        a.className = 'hc-rel-link';
        // Badges ACCUMULATE onto one wrapper (FW5: a fork can also be
        // branches-pending — the old per-branch `return wrap` shape could
        // only ever show one chip).
        const extras: HTMLElement[] = [];
        // Replay-VM increment: the row-level REPLAY badge — visually
        // unmistakable (hard-coded high-contrast, the Bridge banner's
        // doctrine applied to the grid; deliberately NOT theme tokens).
        // The in-page banner on the subdomain is the Bridge's own.
        if (row?.row_class === 'replay') {
          const b = document.createElement('span');
          b.className = 'hc-badge-replay';
          b.textContent = 'REPLAY';
          b.dataset.tip =
            `Replay env — checkpoint ${row.replay_checkpoint_id ?? '?'} on framework ` +
            `${(row.replay_target_sha ?? '').slice(0, 12) || '?'}. Disposable; ` +
            `TTL-reaped; fire is a deliberate action.`;
          extras.push(b);
        }
        // FW5 T5.6: the FORK badge + lineage (keyed on the 052 provenance
        // columns — row_class stays 'release', D7). Same fixed-color
        // doctrine as REPLAY: a fork marker must read identically in every
        // theme. The title carries the lineage line + the quiet-birth
        // contract (D2) — the boot posture the fork gate PROVED, not a
        // live autostaff reading.
        // The badge is forkBadgeModel's decision: lineage always; the
        // named boot-gate refusal (red) or the verify count (green/amber)
        // when the server decorated the row.
        const fork = row ? forkBadgeModel(row, ctx.releaseRef?.(row.fork_of_release_id ?? '')?.name ?? null) : null;
        if (fork) {
          const b = document.createElement('span');
          b.className = fork.cls;
          b.textContent = fork.text;
          b.dataset.tip = fork.title;
          extras.push(b);
        }
        if (row?.branches_pending) {
          const b = document.createElement('span');
          b.className = 'hc-branches-pending';
          b.textContent = ' branches: pending';
          extras.push(b);
        }
        // REL-096/097: the escalation scorecard beside the other badges —
        // "N decisions waiting" loud (a human is being asked), the open
        // count quiet; the tip carries decision/machinery/blocking counts.
        // Absent when not measured: an unreachable Bridge paints nothing.
        const esc = row ? escalationBadge(ctx.scorecard?.(row.id) ?? null) : null;
        if (esc) {
          const b = document.createElement('span');
          b.className = esc.loud ? 'hc-badge-esc hc-badge-esc-loud' : 'hc-badge-esc';
          b.textContent = esc.text;
          b.dataset.tip = esc.tip;
          extras.push(b);
        }
        if (extras.length === 0) return a;
        const wrap = document.createElement('span');
        wrap.appendChild(a);
        for (const b of extras) wrap.appendChild(b);
        return wrap;
      },
    },
    {
      // Item 41: the corpus register id (REL-230). NULL until the cutter
      // mints one — an em dash, never a fabricated id.
      colId: 'relId', headerName: 'ID', flex: 1, minWidth: 100,
      valueGetter: (p) => p.data?.rel_id ?? '—',
    },
    {
      // FW5 T5.6: fork lineage as a first-class column — the grid
      // machinery gives it filter/pin/copy for free. Source resolved to
      // its NAME when the release is in the loaded list; the short id is
      // the honest fallback (lineage survives an aborted/purged source —
      // migration 052's no-FK rule).
      colId: 'forkedFrom', headerName: 'Forked From', flex: 1, minWidth: 130,
      valueGetter: (p) => {
        const r = p.data;
        if (!r?.fork_of_release_id) return '—';
        const src = ctx.releaseRef?.(r.fork_of_release_id) ?? null;
        return `${src?.name ?? `${r.fork_of_release_id.slice(0, 8)}…`} @ ${(r.fork_checkpoint_id ?? '?').slice(0, 10)}…`;
      },
    },
    {
      // The INVERSE of the column above, which nothing served: forked FROM
      // reads child -> parent, and no surface read parent -> children.
      // Three-valued by construction (forkChildrenCellText): the em dash
      // when there is no list to filter, 'none' when the list is the whole
      // population, and 'none of N loaded' when it is not — because a bare
      // 0 cannot tell an all-clear from an unloaded child, and a fork
      // survives its source being purged (migration 052's no-FK rule).
      colId: 'forkedInto', headerName: 'Forked Into', flex: 1, minWidth: 130,
      valueGetter: (p) => {
        const r = p.data;
        const all = ctx.allReleaseRows?.() ?? null;
        if (!r || !all) return '—';
        return forkChildrenCellText(forkChildrenOf(all, r.id, { complete: true }));
      },
    },
    {
      colId: 'created', headerName: 'Created', flex: 1, minWidth: 130,
      // valueGetter stays the SORT/EXPORT value (an ISO string sorts
      // chronologically as text); the renderer is what the eye reads. Sorting
      // on "5 min ago" would order alphabetically — days before hours before
      // minutes — which looks plausible and is wrong.
      valueGetter: (p) => p.data?.created_at ?? '',
      cellRenderer: (p: { data?: ReleaseGridRow }) => relTimeCell(p.data?.created_at ?? null),
    },
    {
      // Item 41: the absolute variant — hidden by default, in the picker.
      colId: 'createdAbs', headerName: 'Created (date)', flex: 1, minWidth: 160, hide: true,
      valueGetter: (p) => p.data?.created_at ?? '',
      cellRenderer: (p: { data?: ReleaseGridRow }) => absTimeCell(p.data?.created_at ?? null),
    },
    {
      colId: 'owner', headerName: 'Owner', flex: 1, minWidth: 140,
      valueGetter: (p) => p.data?.owner_name || p.data?.owner_email || '—',
      // Item 55.9: the OIDC profile picture next to the name — the same
      // avatarNode the account menu and chat render (initials fallback on a
      // broken URL, referrerPolicy no-referrer).
      cellRenderer: (p: { data?: ReleaseGridRow; value?: string }) => {
        const holder = document.createElement('span');
        holder.className = 'hc-owner';
        const row = p.data;
        if (!row) return holder;
        holder.appendChild(avatarNode(row.owner_picture_url ?? null, row.owner_name, row.owner_email ?? ''));
        const t = document.createElement('span');
        t.className = 'hc-owner-name';
        t.textContent = String(p.value ?? '');
        holder.appendChild(t);
        return holder;
      },
    },
    {
      // R37-R40: who is actively IN the release — live sockets first, recent
      // visits behind them (the fleet-presence lane). PICTURES in the cell,
      // NAMES everywhere else: the hover tip carries the name (R38), the
      // filter offers each name as its own value via the machinery's
      // multi-value lane (R39 — wired in buildGrid's displayValues), and the
      // sort/copy vocabulary is the joined names below.
      colId: 'activeUsers', headerName: 'Active users', flex: 1, minWidth: 120,
      valueGetter: (p) =>
        activeUsersDisplays(p.data, ctx.presence?.() ?? null).join(', ') || '—',
      cellRenderer: (p: { data?: ReleaseGridRow }) => {
        const users = activeUsersFor(p.data, ctx.presence?.() ?? null);
        if (users.length === 0) return '—';
        const wrap = document.createElement('span');
        wrap.className = 'hc-au';
        const { shown, more } = activeUsersCellModel(users);
        shown.forEach((u, i) => {
          const a = document.createElement('span');
          a.className = u.lane === 'socket' ? 'hc-au-a hc-au-live' : 'hc-au-a';
          // Stagger seat for the entrance animation (styles.css hc-au-in).
          a.style.setProperty('--au-i', String(i));
          // R38 — the app-wide data-tip layer (installTooltips on #app),
          // never native title=.
          a.dataset.tip = presenceTip(u);
          a.appendChild(avatarNode(u.picture_url, u.name, u.email ?? ''));
          wrap.appendChild(a);
        });
        if (more.length > 0) {
          const m = document.createElement('span');
          m.className = 'hc-au-more';
          m.textContent = `+${more.length} more`;
          m.dataset.tip = more.map(activeUserName).join(', ');
          wrap.appendChild(m);
        }
        return wrap;
      },
    },
    {
      colId: 'sourceBranch', headerName: 'Source branch', flex: 1, minWidth: 130,
      valueGetter: (p) => p.data?.source_branch ?? '—',
      cellRenderer: (p: { data?: ReleaseGridRow }) => branchCell(p.data?.source_branch),
    },
    {
      colId: 'mergedIntoBranch', headerName: 'Merged into', flex: 1, minWidth: 120,
      valueGetter: (p) => p.data?.merged_into_branch ?? '—',
      cellRenderer: (p: { data?: ReleaseGridRow }) => branchCell(p.data?.merged_into_branch),
    },
    {
      colId: 'status', headerName: 'Status', flex: 1, minWidth: 110,
      // Sort/filter still key off the terminal status — a transient label must
      // not reshuffle the grid under the operator mid-transition.
      valueGetter: (p) => p.data?.status ?? '',
      cellRenderer: (p: { data?: ReleaseGridRow }) => {
        const el = document.createElement('span');
        if (p.data) {
          const budgets = ctx.transitionBudgets?.() ?? null;
          el.className = statusPillClassFor(p.data);
          el.textContent = statusPillText(p.data, { budgets });
          if (p.data.transition_state) {
            // #3288: a stale transition is NOT "in progress" — nothing is
            // working on it. The tip says which, and names the remedy.
            const st = transitionStanding(p.data, budgets);
            el.dataset.tip = st?.stale
              ? `${p.data.status} — ${st.tip}`
              : `${p.data.status} — ${statusPillText(p.data, { budgets })} (in progress)`;
          }
          // #4199 P2 — the plan-kick chip (owed / blocked + remedy + Retry)
          // rides beside the pill; the pill itself is untouched so nothing
          // that reads it by class or text changes.
          // prime-carryretry — the held-carry chip (card + Retry from the
          // package) rides the same wrap, beside the pill.
          if (planKickChipModel(p.data.plan_kick) || carryHoldChipModel(p.data.carry_hold)) {
            const wrap = document.createElement('span');
            wrap.className = 'hc-status-cell';
            wrap.appendChild(el);
            renderPlanKickChip(wrap, p.data);
            renderCarryHoldChip(wrap, p.data);
            return wrap;
          }
        }
        return el;
      },
    },
    {
      colId: 'fleet', headerName: 'Fleet', flex: 1, minWidth: 110, sortable: false,
      headerTooltip:
        'Live sessions and agent panes on this release’s VM, read from its own Helm server ' +
        '(the same numbers the in-release Helm shows). A third badge counts running WORKFLOW ' +
        'agents — SDK agents inside dynamic-workflow runs, kept apart from panes so the ' +
        'sessions list reconciles. An em-dash means not measured — ' +
        'never a zero, which would read as an idle fleet.',
      valueGetter: (p) => {
        const c = fleetCell(p.data, ctx.fleet(), Date.now());
        if (c.state !== 'live') return '—';
        // The workflow agents ride the exported value too, marked as their
        // own countable ('+8wf'), so an export reconciles against the cell.
        const wf = (c.wfAgents ?? 0) > 0 ? `+${c.wfAgents}wf` : '';
        return `${c.sessions}/${c.panes ?? 0}${wf}`;
      },
      cellRenderer: (p: { data?: ReleaseGridRow }) => {
        const c = fleetCell(p.data, ctx.fleet(), Date.now());
        const holder = document.createElement('span');
        holder.className = 'hc-fleet';
        if (c.state !== 'live') {
          // An unreadable Bridge still shows the corpus fact (it is off the
          // row) — a stale remote must not hide behind a dead heartbeat.
          holder.innerHTML = '—' + corpusChipHtml(c);
          if (c.tip) holder.dataset.tip = c.tip;
          return holder;
        }
        holder.dataset.tip = c.tip;
        const canReclaim =
          c.diskRed && !!ctx.onReclaimDisk && !!p.data && actionsVisible(p.data, ctx.me);
        holder.innerHTML =
          `<span class="hc-count">${fleetIcon('terminal')}${c.sessions}</span>` +
          `<span class="hc-count${(c.panes ?? 0) > 0 ? ' on' : ''}">${fleetIcon('crew')}${c.panes ?? 0}</span>` +
          // The third chip: workflow agents, only when measured AND nonzero —
          // its own icon and countable, never folded into sessions/panes
          // (owner ask 2026-09-04: "say 8 instead of 0", visibly workflow).
          ((c.wfAgents ?? 0) > 0
            ? `<span class="hc-count on">${fleetIcon('flow')}${c.wfAgents}</span>`
            : '') +
          // The disk chip paints ONLY when RED. A quiet row with 0 sessions
          // and 0 panes is the exact shape a wedged, disk-full VM wears, and
          // this is the only pixel that tells them apart.
          // `data-tip` + `aria-label`, never a native `title=`: the
          // primitive-catalog ratchet pins the count of live `title=` sites
          // (arm 9) and a browser default tooltip is not the console's
          // tooltip — installTooltips (pi-stack/src/client/tooltip.ts) owns
          // the styled, themed, screen-reader-visible one. Measured while
          // adding this chip: the ratchet said GREW 8 sites, pinned 7.
          (c.diskRed
            ? `<span class="hc-count hc-disk-red${canReclaim ? ' hc-disk-go' : ''}" `
              + `data-tip="${escapeAttr(c.diskTip)}" aria-label="${escapeAttr(c.diskTip)}"`
              // 2026-09-08: the chip IS the reclaim button for a viewer who
              // may act — the owner's report was a RED chip whose tip named
              // two shell commands and offered no way to run them.
              // `data-btn-role="chip"` is not decoration — it is the
              // declaration buttons-check reads. A `role="button"` on a
              // non-<button> is scanned as an off-system site (kind 'role')
              // and REFUSED unless it names a PR-3 role in the surrounding
              // lines; without it this site read:
              //   OFF-SYSTEM  helm-console/src/client/home.ts:2453  (role)
              //               not rendered by the icon-button primitive and
              //               not declaring a PR-3 role
              // A count chip that acts IS the chip family PR-3c owns (22
              // sanctioned chip deferrals across the five surfaces today), so
              // it defers rather than converting onto iconBtn — the reclaim
              // affordance is the chip itself, not a button beside it.
              // The deferral is not free: it moves bridge-home's DEFERRED_PIN
              // (helm-bridge/scripts/buttons-check.mjs) 14 -> 15, which is the
              // seat that stops "declare it a chip" from dodging the gate.
              + (canReclaim ? ' role="button" data-btn-role="chip" tabindex="0" data-act="reclaim-disk"' : '')
              + `>${fleetIcon('disk')}full</span>`
            : '') +
          ringHtml(c.completion, c.completionTip, 'release-completion', c.completionStale, c.completionAgeText) +
          corpusChipHtml(c);
        // DEAD-IDLE badge (REL-096): the Bridge measured this release as
        // having nothing in flight and nothing able to put anything in
        // flight. It rides the chip primitive (mkChip, pi-stack/src/client/
        // feedback-nav.ts) on the `error` intent ramp, solid tone: chips,
        // badges and status pills are ONE family on the --btn-* ramps, and
        // the primitive-catalog gate (arm HAND-ROLLED-CHIP, pinned at 122)
        // refused the first cut's hand-rolled `hc-badge-*` span BY NAME
        // (measured: 123 against 122) — a private fixed-colour ramp is
        // exactly the parallel intent space #470 closed for buttons. The
        // Bridge's measured line rides `data-tip` + `aria-label`
        // (installTooltips), never a native `title=` (arm 9's ratchet).
        // `hc-deadidle` is a hook only; it paints nothing.
        if (c.deadIdle) {
          holder.appendChild(mkChip({
            label: `DEAD-IDLE ${c.deadIdle.minutes}m`,
            intent: 'error',
            tone: 'solid',
            size: 'sm',
            cls: 'hc-deadidle',
            attrs: { 'data-tip': c.deadIdle.line, 'aria-label': `Dead-idle: ${c.deadIdle.line}` },
          }));
        }
        // Delegated, because the cell's body is innerHTML (AG Grid destroys
        // renderer DOM on refresh — a per-element listener would be rebound
        // on every paint and lost on every refresh).
        if (canReclaim) {
          const row = p.data!;
          const hit = (ev: Event): boolean => {
            const t = ev.target as HTMLElement | null;
            return !!t && typeof t.closest === 'function' && !!t.closest('[data-act="reclaim-disk"]');
          };
          holder.addEventListener('click', (ev) => {
            if (!hit(ev)) return;
            ev.stopPropagation();
            ctx.onReclaimDisk!(row);
          });
          holder.addEventListener('keydown', (ev) => {
            const ke = ev as KeyboardEvent;
            if ((ke.key !== 'Enter' && ke.key !== ' ') || !hit(ev)) return;
            ev.stopPropagation();
            ctx.onReclaimDisk!(row);
          });
        }
        return holder;
      },
    },
    {
      colId: 'throughput', headerName: 'Throughput', flex: 1, minWidth: 150, sortable: false,
      headerTooltip:
        'Token throughput over the last 30 minutes, summed across this release’s sessions — ' +
        'the same waveform, from the same live metrics ring, as the in-release Helm strip.',
      valueGetter: (p) => {
        const c = fleetCell(p.data, ctx.fleet(), Date.now());
        if (c.state !== 'live') return '—';
        // Sort/export on the PEAK, so rows order by how busy they actually are.
        return Math.round(c.points.reduce((m, q) => (q.v > m ? q.v : m), 0));
      },
      cellRenderer: (p: { data?: ReleaseGridRow; node?: { rowHeight?: number | null } }) => {
        const c = fleetCell(p.data, ctx.fleet(), Date.now());
        const holder = document.createElement('span');
        holder.className = 'hc-wave';
        if (c.state !== 'live') {
          holder.textContent = '—';
          if (c.tip) holder.dataset.tip = c.tip;
          return holder;
        }
        holder.dataset.tip = c.tip;
        // The Bridge's own renderer, byte-for-byte (ensure-seismo.mjs).
        // DRAWN at the row's own height (owner 2026-09-04: the waveform
        // spans the full row, vertically centered — the .hc-grid .hc-wave
        // rule stretches the holder). The height must be geometry, not CSS:
        // the svg ships preserveAspectRatio="none", so stretching the old
        // 22px drawing would scale every stroke unevenly. −6 = the row
        // border + the svg's own 1px CSS borders, with 2px so the rounded
        // corners never touch the row lines; 30 is gridThemeParams' pinned
        // rowHeight (the Helm's glance-grid density, theme-truth) for a
        // call that carries no row node (the unit tests' stub).
        const h = Math.max(22, Math.round((p.node?.rowHeight ?? 30) - 6));
        holder.innerHTML = seismoSvg(c.points, {
          windowMs: c.windowMs, now: Date.now(), width: 132, height: h, cls: 'hc-seismo',
          channel: 'release-throughput',
        });
        return holder;
      },
    },
    {
      colId: 'framework', headerName: 'Framework', flex: 1, minWidth: 150, sortable: false,
      headerTooltip:
        'Whether this release’s VM is running the newest published framework bundle. ' +
        '“Update Available” IS the action (R15): clicking it installs the newest bundle ' +
        'and pins it so the next reboot keeps it. Hover a badge for the running and ' +
        'latest versions and how far behind the VM is.',
      // The sort/filter/export value is the STATE, not the label, so a
      // '—' no-VM row and every labelled state stay distinct downstream.
      valueGetter: (p) =>
        frameworkCell(p.data, ctx.versions(), Date.now(),
          p.data ? ctx.fwLocal?.(p.data.id) ?? null : null).state,
      cellRenderer: (p: { data?: ReleaseGridRow }) => {
        const holder = document.createElement('span');
        holder.className = 'hc-fw';
        const local = p.data ? ctx.fwLocal?.(p.data.id) ?? null : null;
        const cell = frameworkCell(p.data, ctx.versions(), Date.now(), local);
        // #889: the em-dash branch that used to live here is GONE.
        // `frameworkCell` cannot return 'none' any more — every former path
        // to it now returns 'unknown' with a stated reason, which paints the
        // unknown pill below and carries its why in the tooltip. Five of the
        // owner's seven releases rendered through the deleted branch on
        // 2026-09-08, one of them 347 commits behind.
        if (cell.blocked) {
          // 'Update blocked': a drift row whose VM cannot take the bundle
          // (pre-click base-compat verdict). Clickable for the WHY and the
          // remedy — never for an install that would be refused by name.
          const b = document.createElement('button') as HTMLButtonElement;
          b.type = 'button';
          b.dataset.btnRole = 'chip';
          b.dataset.btn = 'warning';
          b.className = `${frameworkPillClass('drift')} hc-pill-blocked hc-fw-go`;
          b.textContent = cell.label;
          if (cell.tip) b.dataset.tip = cell.tip;
          b.setAttribute('aria-label', `Update blocked on ${p.data?.name ?? 'this release'}: incompatible base — show the reason and the remedy`);
          b.addEventListener('click', () => { if (p.data) ctx.onShowUpdateBlocked?.(p.data, cell.blocked!); });
          holder.appendChild(b);
          return holder;
        }
        // R15: THE STATUS IS THE AFFORDANCE — one pill, a real <button>
        // exactly when this viewer can act on it (drift: the click IS the
        // update, R12; failed: the click opens the stored detail, R17).
        // The separate Update button is gone (R14/R15), and so is the
        // "update complete" popup it fed — completion is the animated
        // settle back to Current (R13).
        const mayAct = !!(p.data && actionsVisible(p.data, ctx.me));
        // #828 — THE CHIP IS THE LANE. It was inert on 'failed' (it opened a
        // detail dialog) and absent on a refusal, so a refused row was a
        // permanent red dead end. Now: drift installs, failed RETRIES, and a
        // refusal either retries (a gate that a new bundle can clear) or
        // offers the verb that WOULD clear it (remedyAction — Relaunch VM).
        // #889: `canUpdate` is the cell's own verdict on the INSTALL, and it
        // is false on a behind-but-blocked row (stopped VM, base mismatch) —
        // those paint a plain pill carrying `refusal`, never a button that
        // can only fail.
        const actionable =
          (cell.state === 'drift' && cell.canUpdate && mayAct)
          || ((cell.state === 'failed' || cell.state === 'refused') && mayAct
              && (cell.canRetry === true || !!cell.remedyAction))
          // 2026-09-08: 'version unknown' is no dead end — the click re-reads.
          || (cell.state === 'unknown' && mayAct && !!ctx.onRereadFramework);
        const pill = document.createElement(actionable ? 'button' : 'span') as HTMLElement;
        // A clickable STATUS PILL, not an icon button: PR-3c's chip primitive
        // owns "the status is the affordance". Its intent is declared now so
        // the ramp is already correct when that primitive lands — a failed
        // update is the error intent by definition.
        if (actionable) {
          pill.dataset.btnRole = 'chip';
          // A refusal is NOT an error ramp: the gate worked. It rides the
          // warning intent — attention, not alarm (#828).
          pill.dataset.btn = cell.state === 'failed' ? 'error' : cell.state === 'refused' ? 'warning' : 'neutral';
        }
        pill.className = frameworkPillClass(cell.state) + (actionable ? ' hc-fw-go' : '');
        if (cell.state === 'updating' || cell.state === 'resuming') {
          // R12's loading indicator. Under prefers-reduced-motion the ring
          // is static (its class is registered in the guard) and the label
          // still says 'Updating…' — the state is never motion-only.
          const spin = document.createElement('span');
          spin.className = 'hc-fw-spin';
          spin.setAttribute('aria-hidden', 'true');
          const lbl = document.createElement('span');
          lbl.textContent = cell.label;
          pill.appendChild(spin);
          pill.appendChild(lbl);
          pill.setAttribute('aria-busy', 'true');
        } else {
          pill.textContent = cell.label;
        }
        if (cell.justUpdated) pill.classList.add('hc-fw-just-updated');
        if (cell.tip) pill.dataset.tip = cell.tip;
        // Drift's hover is the RICH five-line ledger (bold install line):
        // the key resolves through the console registry at hover time, and
        // the plain data-tip above stays on as the no-provider fallback
        // (the layer degrades to it by contract).
        // #889: only an ACTIONABLE drift row gets the rich hover — its last
        // line is "Click to install the newest bundle", which would be a lie
        // on a stopped or base-blocked row. Those keep the plain data-tip
        // above, whose last line is the refusal.
        if (cell.state === 'drift' && cell.canUpdate && p.data) pill.dataset.tipHtml = `fw:${p.data.id}`;
        if (actionable) {
          (pill as HTMLButtonElement).type = 'button';
          pill.setAttribute(
            'aria-label',
            cell.remedyAction === 'vm-relaunch'
              ? `Update refused on ${p.data!.name}: no published bundle fits this VM — relaunch it on a fresh instance`
              : cell.state === 'failed'
                ? `Update failed on ${p.data!.name} — retry the update`
                : cell.state === 'refused'
                  ? `Update refused on ${p.data!.name} — retry the update`
                  : cell.state === 'unknown'
                    ? `Re-read ${p.data!.name}'s framework version now`
                    : `Update ${p.data!.name} to the newest framework bundle now`,
          );
          pill.addEventListener('click', () => {
            // The remedy verb wins when a retry provably cannot clear the
            // state; 'unknown' re-reads (2026-09-08); everything else re-runs
            // the update (#828).
            if (cell.remedyAction) ctx.onAction(cell.remedyAction, p.data!);
            else if (cell.state === 'unknown') ctx.onRereadFramework?.(p.data!);
            else ctx.onUpdateFramework(p.data!);
          });
        }
        holder.appendChild(pill);
        return holder;
      },
    },
    {
      colId: 'lastCommit', headerName: 'Last commit', flex: 1, minWidth: 130,
      valueGetter: (p) => p.data?.last_commit_at ?? '',
      cellRenderer: (p: { data?: ReleaseGridRow }) => relTimeCell(p.data?.last_commit_at ?? null),
    },
    {
      colId: 'lastCommitAbs', headerName: 'Last commit (date)', flex: 1, minWidth: 160, hide: true,
      valueGetter: (p) => p.data?.last_commit_at ?? '',
      cellRenderer: (p: { data?: ReleaseGridRow }) => absTimeCell(p.data?.last_commit_at ?? null),
    },
    {
      // RENAMED from 'Last VM active'. The signal widened with it: it is no
      // longer only this release's own VM but the LATER of that and its RM
      // sandbox turns (recency.ts), so "active" would have understated what it
      // reports. A live machine reads "Just now".
      colId: 'lastVmActive', headerName: 'Last VM activity', flex: 1, minWidth: 140,
      headerTooltip:
        'When the release VM last ran. Release-VM traffic is flushed every 60 s, so a live VM reads “Just now”.',
      valueGetter: (p) => p.data?.last_vm_activity_at ?? '',
      cellRenderer: (p: { data?: ReleaseGridRow }) =>
        relTimeCell(p.data?.last_vm_activity_at ?? null, 'vm'),
    },
    {
      colId: 'lastVmActiveAbs', headerName: 'Last VM activity (date)', flex: 1, minWidth: 170, hide: true,
      valueGetter: (p) => p.data?.last_vm_activity_at ?? '',
      cellRenderer: (p: { data?: ReleaseGridRow }) => absTimeCell(p.data?.last_vm_activity_at ?? null),
    },
    {
      // Item 41: when the test server last actually CHANGED — the newest
      // SUCCESSFUL deployment's terminal instant (releases.list). Failed
      // attempts deployed nothing and do not move this.
      colId: 'lastDeploy', headerName: 'Last deploy', flex: 1, minWidth: 130,
      headerTooltip: 'When this release’s test server last received a bundle — the newest successful deployment’s finish time.',
      valueGetter: (p) => p.data?.last_deployed_at ?? '',
      cellRenderer: (p: { data?: ReleaseGridRow }) => relTimeCell(p.data?.last_deployed_at ?? null),
    },
    {
      colId: 'lastDeployAbs', headerName: 'Last deploy (date)', flex: 1, minWidth: 160, hide: true,
      valueGetter: (p) => p.data?.last_deployed_at ?? '',
      cellRenderer: (p: { data?: ReleaseGridRow }) => absTimeCell(p.data?.last_deployed_at ?? null),
    },
    {
      // Owner request 2026-09-03: the release VM's own status — the
      // test-server dot pattern (dot + word; color is never the only
      // signal). The whole decision is vmStatusCell; the renderer paints it.
      colId: 'vmStatus', headerName: 'VM', flex: 1, minWidth: 115,
      headerTooltip:
        'The release VM, from the row facts the console already tracks (recorded instance, ' +
        'stopped stamp, in-flight transition) — never an EC2 call from the grid.',
      // Sort/filter/export on the STATE so a 'No VM' row and a pre-051
      // 'Running' row are not conflated downstream.
      valueGetter: (p) => (p.data ? vmStatusCell(p.data).state : ''),
      cellRenderer: (p: { data?: ReleaseGridRow }) => {
        const holder = document.createElement('span');
        if (!p.data) return holder;
        const row = p.data;
        const c = vmStatusCell(row, { budgets: ctx.transitionBudgets?.() ?? null });
        holder.className = c.cls;
        holder.dataset.tip = c.tip;
        holder.innerHTML = `<span class="hc-ts-dot"></span>${c.text}`;
        // #3288: a stale transition is the ONE vm cell that is an action.
        if (c.action === 'reconcile') {
          holder.dataset.action = 'reconcile';
          holder.setAttribute('role', 'button');
          holder.setAttribute('tabindex', '0');
          holder.addEventListener('click', (ev: Event) => {
            ev.stopPropagation();
            ctx.onReconcileTransition?.(row);
          });
        }
        return holder;
      },
    },
    {
      // win-boot (#5512): which OS the release VM runs — the frozen seat as
      // the word, the box's own OS string in the tip (vmOsCell).
      colId: 'vmOs', headerName: 'OS', flex: 1, minWidth: 90,
      headerTooltip: 'The release VM\u2019s operating system, frozen when the release was cut (Linux or Windows); the tip carries the OS string the box itself reported.',
      valueGetter: (p) => (p.data ? vmOsCell(p.data).text : ''),
      cellRenderer: (p: { data?: ReleaseGridRow }) => {
        const el = document.createElement('span');
        if (!p.data) return el;
        const c = vmOsCell(p.data);
        el.textContent = c.text;
        el.dataset.tip = c.tip;
        return el;
      },
    },
    {
      // Owner request 2026-09-03: "VM uptime (counting upward)". The cell
      // carries data-uptime and the page-wide 1 s ticker
      // (startUptimeTicker) keeps it counting; baseline = the console's own
      // RunInstances/StartInstances stamp (migration 051, vm_started_at).
      colId: 'vmUptime', headerName: 'VM uptime', flex: 1, minWidth: 110,
      headerTooltip:
        'Time since the console asked EC2 to run this release\u2019s VM, counting upward ' +
        'live. An em dash names why there is nothing to count (no VM, stopped, or a start ' +
        'that predates the stamp) — never a guessed uptime.',
      // Sort/export on elapsed SECONDS (a number) so rows order by real age
      // while the eye reads the formatted counter.
      valueGetter: (p) => {
        const iso = p.data ? vmUptimeCell(p.data).iso : null;
        const d = iso ? parseStamp(iso) : null;
        return d ? Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000)) : null;
      },
      cellRenderer: (p: { data?: ReleaseGridRow }) => {
        const el = document.createElement('span');
        if (!p.data) return el;
        const u = vmUptimeCell(p.data);
        el.dataset.tip = u.tip;
        if (!u.iso) {
          el.textContent = '—';
          return el;
        }
        // Migration 055: a bounded baseline (backfilled LaunchTime = upper,
        // observed-running = lower) renders a DISTINCT qualifier. The
        // qualifier is a SIBLING of the ticking span — the page-wide ticker
        // rewrites [data-uptime] textContent wholesale every second, so a
        // prefix inside that element would survive exactly one tick.
        if (u.bound === 'upper' || u.bound === 'lower') {
          const q = document.createElement('span');
          q.className = 'hc-uptime-bound';
          q.textContent = u.bound === 'upper' ? '≤ ' : '≥ ';
          el.appendChild(q);
        }
        // Painted once here (never briefly blank), then driven by the
        // page-wide uptime ticker — the relTimeCell pattern at 1 s cadence.
        const tick = document.createElement('span');
        tick.dataset.uptime = u.iso;
        tick.textContent = formatUptime(u.iso);
        el.appendChild(tick);
        return el;
      },
    },
    {
      // The absolute variant (item 41 pattern): the exact start stamp,
      // hidden by default, in the picker.
      colId: 'vmStartedAbs', headerName: 'VM started (date)', flex: 1, minWidth: 160, hide: true,
      valueGetter: (p) => p.data?.vm_started_at ?? '',
      cellRenderer: (p: { data?: ReleaseGridRow }) => absTimeCell(p.data?.vm_started_at ?? null),
    },
    {
      // Item 41: status dot + word (red Offline / yellow Deploying / green
      // Live), with the open-in-new-tab button beside a LIVE status. The
      // whole decision is testServerCell — the renderer only paints it.
      colId: 'testServer', headerName: 'Test server', flex: 1, minWidth: 130,
      // Sort/filter/export on the STATE WORD, not a URL: 'deploying' rows
      // group together whatever their slugs say.
      valueGetter: (p) => (p.data ? testServerCell(p.data, ctx.apex).state : '—'),
      cellRenderer: (p: { data?: ReleaseGridRow }) => {
        const holder = document.createElement('span');
        if (!p.data) return holder;
        const cell = testServerCell(p.data, ctx.apex);
        holder.className = cell.cls;
        holder.dataset.tip = cell.tip;
        const dot = document.createElement('span');
        dot.className = 'hc-ts-dot';
        holder.appendChild(dot);
        const word = document.createElement('span');
        word.className = 'hc-ts-word';
        word.textContent = cell.text;
        holder.appendChild(word);
        if (cell.href) {
          // A real anchor (not window.open): cmd-click / middle-click stay
          // natural, and rel=noopener keeps the opened page powerless over
          // this one (the item-29 convention).
          const a = document.createElement('a');
          a.className = 'hc-ts-open';
          a.href = cell.href;
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          a.innerHTML = icon('arrow-up-right');
          a.dataset.tip = `Open ${p.data.slug}-test in a new tab`;
          a.setAttribute('aria-label', `Open the ${p.data.slug} test server in a new tab`);
          holder.appendChild(a);
        }
        return holder;
      },
    },
    {
      // #4267 - EC2 COST. Real-time instance-hours + EBS + snapshots from the
      // release record (P2, src/server/cost-ec2.ts) with CloudWatch egress
      // folded in on the SAME tick and FLAGGED approximate (P3). An
      // unmeasured cell NAMES its reason and is never $0.00: this column
      // exists because a stopped release whose volume is still accruing used
      // to show nothing at all.
      colId: 'ec2Cost', headerName: 'EC2 cost', flex: 1, minWidth: 110,
      // Lane prime-ec2cost2: this tooltip used to promise snapshots and data
      // transfer on every figure, while the live console read neither. It
      // now states the basis each figure is checked against.
      headerTooltip:
        'The machine half of this release\u2019s bill, from the release record right now: instance hours and ' +
        'the EBS volume. EBS snapshots and data transfer out are in a figure ONLY when this console reads them ' +
        '\u2014 a figure missing any component ends in + (a LOWER BOUND), and its tip names what it counts ' +
        'and what is EXCLUDED. A release whose start stamp is missing says so \u2014 it never shows $0.00 ' +
        'for something nobody measured.',
      valueGetter: (p: { data?: ReleaseGridRow }) => ec2CostCell(p.data?.id, ctx.ec2Costs?.() ?? null).label,
      cellRenderer: (p: { data?: ReleaseGridRow }) =>
        paintMoneyCell(ec2CostCell(p.data?.id, ctx.ec2Costs?.() ?? null), 'hc-cost-ec2'),
    },
    {
      colId: 'tokenCost', headerName: 'Token cost', flex: 1, minWidth: 100,
      headerTooltip:
        'The model half of this release\u2019s bill: the VM\u2019s cumulative token spend plus RM turns and ' +
        'workflow runs, over the whole fork lineage. A release with no figure says WHY \u2014 never a bare dash.',
      // The sort/export value is the LABEL (a money column sorts as text
      // today, as it always has); the state rides the renderer.
      valueGetter: (p) => costCellText(p.data, ctx.costs()),
      // #927: this column had NO renderer and therefore no tooltip at all,
      // so an em dash could not even be interrogated. Every state now carries
      // its reason on the shared data-tip layer.
      cellRenderer: (p: { data?: ReleaseGridRow }) => {
        const holder = document.createElement('span');
        holder.className = 'hc-cost';
        const cell = costCell(p.data, ctx.costs());
        const s = document.createElement('span');
        s.textContent = cell.label;
        // classList, not `className =`: #1816's truncation class rides
        // ALONGSIDE the state class rather than overwriting it, and a plain
        // measured cell still ends with an empty className exactly as before.
        if (cell.state !== 'measured') s.classList.add(`hc-cost-${cell.state}`);
        if (cell.truncated) s.classList.add('hc-cost-truncated');
        s.dataset.tip = cell.tip;
        holder.appendChild(s);
        // #4360: a billed cell's label IS the vendor figure (it replaced the
        // catalog one); the basis rides as data and a partial or unknown
        // coverage gets its own mark. No catalog figure is painted beside it.
        if (cell.vendor) {
          s.dataset.costBasis = cell.vendor.basis;
          s.dataset.costCoverage = cell.vendor.coverage;
          if (cell.vendor.coverageLabel) {
            const m = document.createElement('span');
            m.className = 'hc-cost-coverage';
            m.textContent = cell.vendor.coverageLabel;
            m.dataset.tip = cell.tip;
            holder.appendChild(m);
          }
        }
        // Owner ruling 2026-09-25 — the model key beside the model spend.
        const ak = agentKeyMark(p.data);
        if (ak) {
          const k = document.createElement('span');
          k.className = ak.cls;
          k.textContent = ak.text;
          k.dataset.tip = ak.tip;
          k.dataset.agentKey = String(p.data?.agent_key);
          holder.appendChild(k);
        }
        return holder;
      },
    },
    {
      // #4267 - TOTAL COST, and the rule it exists to enforce: **it never
      // sums a dash as zero.** One unmeasured half makes the whole total
      // unmeasured, naming WHICH half is missing. See totalCostCell in
      // cost-cell.ts: there is no `?? 0` and no filter-then-sum, because
      // dropping a null and adding up what is left IS summing it as zero one
      // indirection away.
      //
      // The colId is UNCHANGED on purpose. A saved column-state or a picker
      // entry that named `totalCost` still resolves; what moved is the
      // MEANING, and the column that shows what this one used to show is
      // `tokenCost` above, rendering the same `costCell` decision unchanged.
      colId: 'totalCost', headerName: 'Total cost', flex: 1, minWidth: 100,
      headerTooltip:
        'EC2 cost plus Token cost. A total is shown only when BOTH halves are measured \u2014 a partial sum ' +
        'presented as a total is a smaller number than the truth, wearing the truth\u2019s formatting.',
      valueGetter: (p: { data?: ReleaseGridRow }) =>
        releaseTotalCostCell(p.data, ctx.costs(), ctx.ec2Costs?.() ?? null).label,
      cellRenderer: (p: { data?: ReleaseGridRow }) =>
        paintMoneyCell(releaseTotalCostCell(p.data, ctx.costs(), ctx.ec2Costs?.() ?? null), 'hc-cost-total'),
    },
    {
      colId: 'actions', headerName: '', flex: 1, minWidth: 150, sortable: false,
      valueGetter: () => '',
      cellRenderer: (p: { data?: ReleaseGridRow }) => {
        const holder = document.createElement('span');
        // The users grid's action-cell idiom (users.ts): flex + height:100%
        // so the buttons center on the ROW axis — .ag-cell only centers
        // text (line-height), and a button taller than the type rides the
        // text baseline, visibly high of the row's middle.
        holder.className = 'hc-actions';
        const row = p.data;
        if (!row || !actionsVisible(row, ctx.me)) return holder; // no action cell
        for (const action of actionsFor(row)) {
          // Icon-only with the verb in the tooltip (Styles × Themes PR-2).
          // abort / down / fire are destructive — the last of those because
          // firing a replay spends real money (MUST-FIX above). The row
          // buttons open the confirm DIALOG, which is the ask, so they carry
          // an explicit noConfirm rather than arming twice.
          const destructive = actionIsDestructive(action);
          const btn = mkIconBtn({
            glyph: ACTION_ICON[action],
            label: ACTION_TITLE[action],
            intent: destructive ? 'destructive' : 'neutral',
            size: 'sm',
            cls: `hc-action hc-action-${action}`,
            ...(destructive ? { noConfirm: 'the click opens this verb\'s confirm dialog — that IS the ask' } : {}),
          });
          btn.addEventListener('click', () => ctx.onAction(action, row));
          holder.appendChild(btn);
        }
        return holder;
      },
    },
  ];
}

// ---------------------------------------------------------------------
// Mount
// ---------------------------------------------------------------------

async function jsonFetch<T>(method: string, url: string, body?: unknown): Promise<{ status: number; data: T | null }> {
  try {
    const res = await fetch(url, {
      method,
      headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    let data: T | null = null;
    try { data = (await res.json()) as T; } catch { /* tolerate non-JSON */ }
    return { status: res.status, data };
  } catch {
    return { status: 0, data: null };
  }
}

/** Batch cost fetch — the ONE agreed endpoint (WS7). Null-tolerant.
 *
 * #927: this used to keep ONLY the rows whose `total_usd` was a number, so a
 * release the route reported as `{total_usd: null, measured: false}` became
 * indistinguishable from one the route never mentioned. Both then rendered a
 * bare em dash. Every row the route names is kept now, with its own verdict,
 * and telling the states apart is `costCell`'s job. */
export async function fetchCostMap(
  // NO DEFAULT HERE: `fetchCostSummary` owns the one default fetcher, and a
  // second copy of it would be a second UN-EXTRACTED fetch site for the PAT
  // coverage sweep to account for — it extracts a LITERAL url, and the
  // default's helper call passes a variable. One default, one seat row.
  // (This comment deliberately does not spell that call out: the sweep scans
  // the raw source and does NOT strip comments, so writing it here would
  // itself be counted as a seventh site — measured, on this very comment.)
  fetcher?: (url: string) => Promise<{ status: number; data: unknown }>,
): Promise<CostMap> {
  return (await fetchCostSummary(fetcher)).costs;
}

/** #4267 — ONE read, BOTH halves. The route serves `releases` (token) and
 * `ec2` as two top-level maps; see server/costs.ts for why the EC2 figure is
 * NOT folded into `releases` (folding it would flip the Token cell of an
 * EC2-only release from "Awaiting first report" to "Unknown").
 *
 * `fetchCostMap` above stays, delegating, because it is this file's exported
 * surface and every caller and suite that names it still means the token
 * half. */
export async function fetchCostSummary(
  fetcher: (url: string) => Promise<{ status: number; data: unknown }> = async (u) => jsonFetch('GET', u),
): Promise<{ costs: CostMap; ec2: Ec2CostMap }> {
  const { status, data } = await fetcher('/api/releases/cost-summary');
  if (status !== 200 || data === null || typeof data !== 'object') return { costs: null, ec2: null };
  const map = new Map<string, CostEntry>();
  const rows = (data as {
    releases?: Record<string, {
      total_usd?: number | null; measured?: boolean; as_of?: string | null;
      // #1805 serves `truncated` here and #1844 serves
      // `truncated_release_ids` beside it, present only when the flag is
      // true. Both are read defensively, because the cell renders whichever
      // the payload actually carries and says which it got — an older
      // console sends neither.
      truncated?: boolean; truncated_release_ids?: unknown;
      // #4360 — read defensively; an unknown spelling is dropped, not coerced.
      vendor_usd?: unknown;
      vendor_coverage?: unknown; vendor_coverage_share?: unknown;
    }>;
  }).releases ?? {};
  for (const [id, v] of Object.entries(rows)) {
    if (!v || typeof v !== 'object') continue;
    const usd = typeof v.total_usd === 'number' && Number.isFinite(v.total_usd) ? v.total_usd : null;
    map.set(id, {
      total_usd: usd,
      // Trust the server's own verdict when it sends one; fall back to the
      // figure's presence for a console that predates the field.
      measured: typeof v.measured === 'boolean' ? v.measured : usd !== null,
      as_of: typeof v.as_of === 'string' ? v.as_of : null,
      // #1816: the marker was computed, transported and DROPPED HERE. A
      // console that predates #1805 sends neither field; absent reads false
      // and empty, which renders exactly as it did before.
      truncated: v.truncated === true,
      truncated_release_ids: Array.isArray(v.truncated_release_ids)
        ? v.truncated_release_ids.filter((x): x is string => typeof x === 'string')
        : [],
      vendor_usd: typeof v.vendor_usd === 'number' && Number.isFinite(v.vendor_usd) ? v.vendor_usd : null,
      ...(v.vendor_coverage === 'complete' || v.vendor_coverage === 'partial'
        || v.vendor_coverage === 'unknown' || v.vendor_coverage === 'none'
        ? { vendor_coverage: v.vendor_coverage }
        : {}),
      vendor_coverage_share: typeof v.vendor_coverage_share === 'number' && Number.isFinite(v.vendor_coverage_share)
        ? v.vendor_coverage_share
        : null,
    });
  }

  // #4267 — the EC2 half. ABSENT is a THIRD state and a different one from an
  // entry that refuses: a console that predates this wave sends no `ec2` key
  // at all, and `null` is what `ec2CostCell` renders as "the console has not
  // answered yet" rather than as a measured anything.
  const rawEc2 = (data as { ec2?: Record<string, Ec2CostEntry> }).ec2;
  let ec2: Ec2CostMap = null;
  if (rawEc2 && typeof rawEc2 === 'object') {
    ec2 = new Map<string, Ec2CostEntry>();
    for (const [id, v] of Object.entries(rawEc2)) {
      if (!v || typeof v !== 'object') continue;
      // Read defensively, and NEVER coerce: `Number(undefined)` is NaN and a
      // NaN dollar figure that reaches `toFixed` renders as the word 'NaN'
      // where an operator expects money.
      ec2.set(id, {
        ec2_usd: typeof v.ec2_usd === 'number' && Number.isFinite(v.ec2_usd) ? v.ec2_usd : null,
        ec2_measured: v.ec2_measured === true,
        ec2_reason: typeof v.ec2_reason === 'string' ? v.ec2_reason : null,
        ec2_remedy: typeof v.ec2_remedy === 'string' ? v.ec2_remedy : null,
        ec2_refusal_kind: typeof v.ec2_refusal_kind === 'string' ? v.ec2_refusal_kind : null,
        ec2_approximate: v.ec2_approximate === true,
        ec2_approximate_note: typeof v.ec2_approximate_note === 'string' ? v.ec2_approximate_note : null,
        ec2_lower_bound: v.ec2_lower_bound === true,
        ec2_lower_bound_note: typeof v.ec2_lower_bound_note === 'string' ? v.ec2_lower_bound_note : null,
        ec2_as_of: typeof v.ec2_as_of === 'string' ? v.ec2_as_of : null,
        // Lane prime-ec2cost2: the figure's BASIS. An entry that is not an
        // object with a string key is DROPPED, never coerced.
        ec2_components: Array.isArray(v.ec2_components)
          ? (v.ec2_components as unknown[])
            .filter((c): c is Record<string, unknown> => !!c && typeof c === 'object'
              && typeof (c as { key?: unknown }).key === 'string')
            .map((c) => ({
              key: String(c.key),
              usd: typeof c.usd === 'number' && Number.isFinite(c.usd) ? c.usd : null,
              measured: c.measured === true,
              reason: typeof c.reason === 'string' ? c.reason : null,
            }))
          : null,
      });
    }
  }
  return { costs: map, ec2 };
}

export function mountHome(root: HTMLElement): () => void {
  // The ONE shell (shared-shell, R63): brand/nav/appearance/account live in
  // shell.ts now — this page owns only its OWN state, in the pagebar row
  // below the top strip (R64): the "Releases" title (R65), the slot count
  // (R66) and the fleet drift pill (R67). The Columns and Create controls
  // #372 parked here moved DOWN by the grid (R26/R30, grid-toolbar.ts) —
  // ids carried forward BY NAME (#hcColsBtn keeps R27's panels-top-left
  // glyph, #hcCreate keeps the id its wiring and the e2e specs bind) —
  // joined by Reset (R28) and Refresh (R32), all icon buttons (R31) with
  // data-tip tooltips (R33) matching WaaG in the Helm.
  const shell = mountShell(root, 'releases');
  shell.pagebar.innerHTML = `
      <h1 class="hc-pagetitle">Releases</h1>
      <div class="hc-slots" id="hcSlots" data-tip="active releases / slot total">–/–</div>
      <button class="hc-driftpill" id="hcDrift" type="button" data-btn-role="chip" data-btn="error" hidden></button>
      <button class="hc-driftpill hc-fleetupd" id="hcFleetUpdate" type="button" data-btn-role="chip" data-btn="neutral" hidden
        data-tip="Preview, then update every running release that is idle and compatible — one audited pass; busy and incompatible VMs are listed with their reason, never forced">Update all…</button>
      <div class="hc-spring"></div>`;
  shell.main.innerHTML = `
      <section class="hc-recents" id="hcRecents" hidden></section>
      ${renderGridToolbar()}
      <div id="hcGrid" class="hc-grid"></div>
      <div id="hcCardList" class="hc-cardlist"></div>
      <div class="hc-listmore-wrap">${iconBtn({ glyph: 'chevron-down', label: 'Show more', size: 'sm', id: 'hcListMore', cls: 'hc-listmore', attrs: { hidden: true } })}</div>`;
  // Item D — the shared search box, leftmost in the toolbar row directly
  // above the grid (the same position every grid page mounts it).
  const homeSearch = mkSearchBox({
    id: 'hcHomeSearch',
    placeholder: 'Search releases',
    label: 'Search releases',
    onInput: (v) => {
      searchQ = v;
      paintRows();
    },
  });
  (root.querySelector('#hcGridBar') as HTMLElement).prepend(homeSearch.el);

  let me: Me | null = null;
  let apex: string | null = null;
  let costs: CostMap = null;
  /** #4267 — the EC2 half of the same one read. null = not answered yet. */
  let ec2Costs: Ec2CostMap = null;
  let versions: FleetVersions = null;
  let fleet: FleetMetricsMap = null;
  /** The escalation scorecard sweep (REL-096) — cached server-side like
   * the fleet lane; null until it answers, and a null paints no badge. */
  let scorecard: ScorecardMap = null;
  let presence: FleetPresenceMap = null;
  /** All releases as the server sent them, plus whether the finished tail is
   * expanded. rowData is DERIVED from these two — never the reverse, so a
   * background refresh cannot silently collapse the user's choice. */
  let allRows: ReleaseGridRow[] = [];
  /** #3288 — the transition budgets THIS load was served with. Null until
   * the first answer, and null on a console that publishes none: the grid
   * then renders a transition's age and never calls it stale. Read through a
   * getter by the column defs, so a later load is picked up without
   * rebuilding them (the costs/versions/fleet idiom above). */
  let txBudgets: TransitionBudgets | null = null;
  /** The recents strip's rows as the server sent them (pinned first). The
   * strip re-renders from these whenever the cost map or the fleet sweep
   * lands — the cards would otherwise paint '—' forever (the fetch order is
   * grid first, cost/fleet later, by design). */
  let recentRows: RecentRelease[] = [];
  /** False until the strip's FIRST paint — the one render that gets the
   * entrance stagger. Every later paint is a reenter (suppressed entrance,
   * FLIP where the order can change). */
  let recentsPainted = false;
  /** Which cards have shown a MEASURED live/cost value at least once — the
   * '—' -> data edge is the only moment the field-fresh animation fires,
   * never the routine 30 s repaint. */
  const liveMeasured = new Map<string, { fleet: boolean; cost: boolean }>();
  let showTerminal = false;
  /** Item D — the release search's query. Filtering happens in paintRows
   * over the display vocabulary (releaseSearchHaystack); rowData stays
   * DERIVED from allRows + this + the expansion, never mutated in place. */
  let searchQ = '';
  let gridApi: GridApi<ReleaseGridRow> | null = null;
  /** The defs the LIVE grid was built with — the column picker's row source
   * (labels + default visibility), captured by buildGrid at mount. */
  let currentDefs: ColDef<ReleaseGridRow>[] = [];
  /** Item 55.8 — pins. null = shipped defaults (DEFAULT_HOME_PINS); an
   * array = the user's explicit list ([] = deliberately unpinned). Persists
   * in the SAME ui-state blob as the column filters. */
  let pins: string[] | null = null;
  /** Item 55.4 — the sheets-style column filters (colId -> state). */
  let colFilters: ColumnFilterMap = {};
  /** Item 55.10 — the branch-URL template from /api/client-config
   * ({branch} placeholder); null = no links, plain text. */
  let branchTpl: string | null = null;

  const slotsEl = root.querySelector('#hcSlots') as HTMLElement;
  const modal = root.querySelector('#hcModal') as HTMLElement;
  const recentsEl = root.querySelector('#hcRecents') as HTMLElement;
  const driftEl = root.querySelector('#hcDrift') as HTMLButtonElement;
  const fleetUpdEl = root.querySelector('#hcFleetUpdate') as HTMLButtonElement;
  const listMoreEl = root.querySelector('#hcListMore') as HTMLButtonElement;
  const cardListEl = root.querySelector('#hcCardList') as HTMLElement;

  // Every listener/timer this mount installs, torn down together. mountHome
  // runs again on each hash route change, so anything left behind accumulates
  // one copy per navigation — a document click handler that fires N times and
  // an interval walking a detached DOM.
  const teardowns: (() => void)[] = [];
  // The drift chip's rich tooltip provider (owner follow-up): resolves the
  // row + snapshot at HOVER time, so the ledger is always the current
  // sweep's — never a stale string captured at render.
  teardowns.push(registerRichTip('fw', (id) => {
    const entry = versions?.releases?.[id];
    if (!entry || !versions) return null;
    return frameworkDriftTipHtml(entry, versions, Date.now());
  }));

  // Item 55.5's fixed viewport (body.hc-fixed) is the SHELL's now — R61 made
  // it every page's behaviour, so the class rides shell.teardown() below,
  // not a mount-local push here.
  teardowns.push(shell.teardown);
  // A dialog left open across a route change would leak the engine's
  // document keydown listener — tear it down instantly on unmount.
  teardowns.push(() => destroyDialog(modal));

  // The phone "card / list per row" fallback (owner ruling 2026-09-06): below
  // 640px the ag-grid gives way to a stacked key:value card per release
  // (card-list.ts), the desktop grid staying byte-identical above it. The
  // switch itself is CSS; this only re-paints when the breakpoint is crossed
  // so the card DOM is never built on a desktop.
  const phone = watchPhone(() => paintRows());
  teardowns.push(phone.teardown);

  // --- Framework column: the transient lane (R12/R13/R16/R18) -----------
  // row-state.ts is the foundation's keyed TTL store, built ONCE for
  // exactly this: refreshCells destroys renderer DOM, so 'updating'/
  // 'failed'/the settle live here and renderers re-read them per paint.
  // Its TTL expiry notification is ALSO the repaint scheduler for R18's
  // auto-clear — no bespoke client timer.
  // #1089: 'deferred' rides the same store — the durable lane's note folds
  // in exactly like 'scheduled', with the server-computed `until` as its TTL.
  const fwStore = createRowStore<'updating' | 'resuming' | 'failed' | 'refused' | 'updated' | 'scheduled' | 'deferred'>();
  /** R13's settle window: how long the Current pill wears its arrival. */
  const FW_SETTLE_MS = 2600;
  /** Local-only refusals (4xx, network) clear themselves — R18's local
   * arm; server-stored failures ride the server's own `until`. */
  const FW_LOCAL_FAILED_TTL_MS = 60_000;
  const fwLocal = (id: string): FrameworkLocalState | null => {
    const e = fwStore.entry(id);
    if (!e) return null;
    if (e.state === 'updated') return { state: 'updated' };
    const d = (e.data ?? {}) as { detail?: string | null };
    return { state: e.state, at: e.at, detail: d.detail ?? null };
  };
  teardowns.push(
    fwStore.subscribe(() => {
      gridApi?.refreshCells({ columns: ['framework'], force: true });
      ensureFwFastPoll();
    }),
  );
  let fwPollTimer: number | null = null;
  /** While anything is 'updating', re-read fleet-versions every 5 s (a
   * cached snapshot + one SELECT server-side — no SSM cost) so the flip to
   * Current or failed lands while the person watches (R13). The 120 s
   * baseline poll is untouched. */
  function ensureFwFastPoll(): void {
    const busy = [...fwStore.snapshot().keys()].some((id) => fwStore.get(id) === 'updating' || fwStore.get(id) === 'resuming');
    if (busy && fwPollTimer === null) {
      fwPollTimer = window.setInterval(() => void loadVersions(), 5_000);
    } else if (!busy && fwPollTimer !== null) {
      window.clearInterval(fwPollTimer);
      fwPollTimer = null;
    }
  }
  teardowns.push(() => {
    if (fwPollTimer !== null) window.clearInterval(fwPollTimer);
    fwPollTimer = null;
  });
  /** Fold the durable update lane into the transient store on every read.
   * Server entries carry the server-computed `until` as their TTL; a local
   * click-sourced entry keeps its own life until the server confirms,
   * contradicts, or completes it — and a durable 'updating' that
   * DISAPPEARS is a completed install: the settle state animates the pill
   * back to Current (R13) instead of any popup (R14). */
  function reconcileFwStore(): void {
    const upd = versions?.updates ?? {};
    const now = Date.now();
    for (const [id, u] of Object.entries(upd)) {
      const cur = fwStore.entry(id);
      const curSrc = ((cur?.data ?? {}) as { src?: string }).src;
      // A fresh local refusal outranks a server row from before the retry.
      if (cur && curSrc === 'local' && cur.state === 'failed') continue;
      fwStore.set(id, u.state, {
        ttlMs: Math.max(1000, new Date(u.until).getTime() - now),
        data: { src: 'server', detail: u.error },
      });
    }
    for (const [id, e] of fwStore.snapshot()) {
      if (upd[id]) continue;
      if (((e.data ?? {}) as { src?: string }).src !== 'server') continue;
      if (e.state === 'updating' || e.state === 'resuming') {
        fwStore.set(id, 'updated', { ttlMs: FW_SETTLE_MS, data: { src: 'server' } });
      } else {
        fwStore.clear(id);
      }
    }
  }

  mountCtxMenu();

  /** The rows a filter's value list counts over — what the user could see
   * with no column filters (the current expansion of the terminal tail). */
  function candidateRows(): ReleaseGridRow[] {
    const { primary, rest } = partitionReleases(allRows);
    return showTerminal ? [...primary, ...rest] : primary;
  }

  async function refreshSlots(): Promise<void> {
    const { status, data } = await jsonFetch<SlotsSnapshot>('GET', '/api/slots');
    if (status !== 200 || !data) {
      slotsEl.textContent = '–/–';
      slotsEl.dataset.tip = 'active releases / slot total';
      return;
    }
    slotsEl.textContent = slotsWidgetText(data);
    slotsEl.dataset.tip = slotsWidgetTip(data);
  }

  async function loadRows(): Promise<void> {
    const { data } = await jsonFetch<{ releases: ReleaseGridRow[]; transition_budgets?: TransitionBudgets }>(
      'GET', '/api/releases');
    // #3288: the budget seat rides the SAME answer as the rows it dates, so
    // the grid can never compare a row against a budget from another load.
    txBudgets = data?.transition_budgets ?? null;
    // NO client-side sort: the server already orders by last_activity_at
    // (recency.ts), the same expression the in-release switcher uses. Re-sorting
    // here would be a second recency definition — the exact thing that lane was
    // consolidated to prevent — and the two would drift. partitionReleases
    // below only SPLITS that order, it never reorders within a part.
    allRows = data?.releases ?? [];
    paintRows();
  }

  /** Feed the grid from allRows + the current expansion, and label the toggle
   * with the MEASURED size of the tail it hides. */
  /** The phone card for one release: a curated ~6-field transpose of the
   * 20-column row (rel_id/title/status/owner/age), tappable to the release.
   * Text-only values — the display vocabulary, not the grid's rich cells. */
  function releaseCard(r: ReleaseGridRow): CardSpec {
    const badge = document.createElement('span');
    badge.className = statusPillClassFor(r);
    badge.textContent = statusPillText(r);
    // Fork rows carry their provenance line + the verify/boot-gate verdict
    // on the card too (the grid badge's title is unreachable on a phone).
    const fork = forkBadgeModel(r, allRows.find((x) => x.id === r.fork_of_release_id)?.name ?? null);
    const forkFields = fork
      ? [
          { key: 'Fork', value: `${fork.text} — ${fork.lineage}` },
          ...(fork.kind === 'refused' && r.fork_boot
            ? [{ key: 'Boot gate', value: `${r.fork_boot.verdict.toUpperCase()} — ${r.fork_boot.remedy}` }]
            : fork.kind !== 'plain' && r.fork_verify
              ? [{ key: 'Fork verify', value: r.fork_verify.verdict }]
              : []),
        ]
      : [];
    return {
      title: r.name,
      href: releaseHref(r, apex),
      badge,
      data: { rel: r.id },
      fields: [
        { key: 'Release', value: r.rel_id || r.slug || '' },
        { key: 'Owner', value: r.owner_name || r.owner_email || '' },
        { key: 'Created', value: fmtAgo(r.created_at) },
        { key: 'Last activity', value: fmtAgo(r.last_activity_at) },
        { key: 'Source', value: r.source_branch || '' },
        { key: 'OS', value: vmOsCell(r).text },
        ...forkFields,
      ],
    };
  }

  function paintRows(): void {
    const q = searchQ.trim().toLowerCase();
    const source = q
      ? allRows.filter((r) => releaseSearchHaystack(currentDefs, r).includes(q))
      : allRows;
    const { primary, rest } = partitionReleases(source);
    const shown = showTerminal ? [...primary, ...rest] : primary;
    gridApi?.setGridOption('rowData', shown);
    // The phone card column mirrors the grid's rows; built only on a phone
    // (cleared otherwise so a resize back to desktop leaves nothing behind).
    if (phone.isPhone()) {
      renderCardList(cardListEl, shown.map(releaseCard), {
        empty: q ? 'No releases match your search.' : 'No releases yet.',
      });
    } else {
      cardListEl.replaceChildren();
    }
    if (rest.length === 0) {
      listMoreEl.hidden = true;
      return;
    }
    listMoreEl.hidden = false;
    // Icon-only: the measured tail size moved from the face into the tooltip,
    // and the chevron itself carries the direction.
    setBtnGlyph(listMoreEl, showTerminal ? 'chevron-up' : 'chevron-down');
    setBtnLabel(listMoreEl, showTerminal
      ? 'Show less'
      : `Show more (${rest.length} completed or aborted)`);
    listMoreEl.setAttribute('aria-expanded', showTerminal ? 'true' : 'false');
  }

  /** One card's live half — the SAME join the ctx menu does (data-rel ->
   * grid row by id) feeding the SAME stores the grid cells read. Also
   * computes the '—' -> data freshness edge per card. */
  function cardLiveResolver(id: string): CardLive {
    const base = recentsCardLive(allRows.find((r) => r.id === id), fleet, costs, id);
    const prev = liveMeasured.get(id) ?? { fleet: false, cost: false };
    // #927: the freshness edge asked "is the label an em dash?", which no
    // label is any more. It asks the DECISION instead — a card flashes fresh
    // when a real figure arrives, not when the text stops being a dash.
    const costState = costCell(allRows.find((r) => r.id === id), costs).state;
    const nowMeasured = {
      fleet: base.html !== null,
      cost: costState === 'measured' || costState === 'zero',
    };
    liveMeasured.set(id, { fleet: prev.fleet || nowMeasured.fleet, cost: prev.cost || nowMeasured.cost });
    // REL-096: the same scorecard the grid's Name badge reads — a card and
    // its row can never disagree about how many decisions are waiting.
    const esc = escalationBadge(scorecard?.releases[id] ?? null);
    // Run position (aisdlc-docs § 30): the Bridge's own line, folded into
    // the SAME fleet sweep the live cell reads — one more read per row,
    // painted verbatim, or nothing.
    const rp = runPositionCard(fleet, id);
    return {
      ...base,
      liveFresh: nowMeasured.fleet && !prev.fleet,
      costFresh: nowMeasured.cost && !prev.cost,
      escText: esc?.text ?? null,
      escTip: esc?.tip,
      escLoud: esc?.loud ?? false,
      rpStep: rp?.step ?? null,
      rpState: rp?.state ?? null,
      rpTone: rp?.tone,
      rpText: rp?.text ?? null,
      rpTip: rp?.tip,
    };
  }

  /** Paint the strip from recentRows. `flip` wraps the re-render in the
   * shared FLIP helper (motion.ts — ONE implementation, R48) so surviving
   * cards travel to their new slots instead of teleporting; a card new to
   * the strip enters via hc-card-new, and reduced motion degrades to an
   * instant reorder inside withFlip itself. */
  function paintRecents(o: { reenter: boolean; flip: boolean }): void {
    const render = (): void =>
      renderRecents(recentsEl, recentRows, apex, { live: cardLiveResolver, reenter: o.reenter });
    if (o.flip) {
      withFlip(recentsEl, render, { keyAttr: 'data-rel', enterClass: 'hc-card-new', moveClass: 'hc-card-flip' });
    } else {
      render();
    }
  }

  /** The recents strip. Independent of the grid load and failure-tolerant:
   * a recents outage must never take the releases list down with it. */
  async function loadRecents(): Promise<void> {
    recentRows = await fetchRecents((u) => jsonFetch('GET', u));
    paintRecents({ reenter: recentsPainted, flip: recentsPainted });
    recentsPainted = true;
  }

  /** Is this release currently pinned on MY shelf? recentRows is complete
   * for pins (the server widens the limit so every pin rides the payload). */
  function isPinnedRecent(id: string): boolean {
    return recentRows.some((r) => r.id === id && r.pinned === true);
  }

  /** R41/R46 — toggle the Jump Back In pin (ONE verb for both surfaces,
   * ruling Q5). The server is the ordering authority, so this re-fetches and
   * re-renders rather than splicing locally; the FLIP pass is what animates
   * the OTHER cards reordering around the toggled one (R48), and the
   * hc-pin-just class is the toggled card's own pin/unpin beat (R47). */
  async function onPinRecent(releaseId: string): Promise<void> {
    const cur = isPinnedRecent(releaseId);
    const { status, data } = await jsonFetch<{ error?: string }>(
      cur ? 'DELETE' : 'POST', `/api/recents/${releaseId}/pin`,
    );
    if (status !== 200) {
      showNotice(`${cur ? 'Unpin' : 'Pin'} refused (${status}): ${data?.error ?? 'unstated'}`);
      return;
    }
    recentRows = await fetchRecents((u) => jsonFetch('GET', u));
    paintRecents({ reenter: true, flip: true });
    recentsPainted = true;
    const el = recentsEl.querySelector<HTMLElement>(`.hc-card[data-rel="${releaseId}"]`);
    if (el) {
      el.classList.add('hc-pin-just');
      el.addEventListener('animationend', () => el.classList.remove('hc-pin-just'), { once: true });
    }
  }

  /** #927 — the cost lane, now a LANE and not a one-shot. Same shape as
   * loadVersions: fetch, replace the store, refresh only its own column, and
   * repaint the cards that carry the same total (R45). A failed fetch leaves
   * `costs` null, which every cell renders as a NAMED unknown rather than as
   * a stale figure or a blank. */
  async function loadCosts(): Promise<void> {
    const summary = await fetchCostSummary();
    costs = summary.costs;
    ec2Costs = summary.ec2;
    gridApi?.refreshCells({ columns: ['ec2Cost', 'tokenCost', 'totalCost'], force: true });
    if (recentsPainted) paintRecents({ reenter: true, flip: false });
  }

  /** The drift lane. Server-side cached, so this is one cheap read per
   * client — never a per-row fan-out to the fleet. */
  async function loadVersions(refresh = false): Promise<void> {
    const { status, data } = await jsonFetch<FleetVersionSnapshot>(
      'GET', `/api/releases/fleet-versions${refresh ? '?refresh=1' : ''}`,
    );
    versions = status === 200 && data ? data : null;
    // Durable update-lane truth in, completed installs out (the settle).
    reconcileFwStore();
    gridApi?.refreshCells({ columns: ['framework'], force: true });
    paintDrift();
    paintFleetUpdate();
  }

  /** The fleet-level alert. No summary => the control stays hidden, so a
   * current fleet shows NOTHING rather than a reassuring zero. */
  function paintDrift(): void {
    const d = driftSummary(versions);
    if (!d) {
      driftEl.hidden = true;
      driftEl.textContent = '';
      return;
    }
    driftEl.hidden = false;
    driftEl.textContent = d.label;
    driftEl.dataset.tip = d.tip;
    driftEl.dataset.count = String(d.count);
  }
  /** The fleet-update control rides the drift pill's visibility: shown
   * exactly when something is behind (a current fleet shows nothing). */
  function paintFleetUpdate(): void {
    const d = driftSummary(versions);
    fleetUpdEl.hidden = !d;
  }

  /** THE FLEET UPDATE LANE (POST /api/releases/fleet-update). Two calls:
   * a DRY RUN first — the per-release plan from the sweep's own facts,
   * nothing touched — shown in the confirm; then the real pass, whose
   * per-release verdict rows (updated / updating / refused-with-reason /
   * skipped-busy / skipped-locked / current / unknown) render verbatim.
   * A fleet pass never authorises ending anyone's live sessions: busy VMs
   * come back skipped-busy with the count, and the row's own pill (which
   * asks) or Update at Quiet Window is the lane for those. */
  const fleetRowLine = (r: FleetUpdateRow): string =>
    `${r.slug}: ${r.result.toUpperCase()}${r.installedSha ? ` (${r.installedSha.slice(0, 12)})` : ''} — ${r.reason}` +
    (r.remedy && r.remedy !== 'none' ? ` [remedy: ${r.remedy}]` : '');
  async function onFleetUpdate(): Promise<void> {
    fleetUpdEl.classList.add('hc-busy');
    const plan = await jsonFetch<FleetUpdateResult>('POST', '/api/releases/fleet-update', { dry_run: true, mode: 'immediate' });
    fleetUpdEl.classList.remove('hc-busy');
    if (plan.status !== 200 || !plan.data) {
      showNotice(`Fleet update preview refused (${plan.status}): ${(plan.data as { error?: string } | null)?.error ?? 'unstated'}`);
      return;
    }
    const rows = plan.data.rows;
    const would = rows.filter((r) => r.result === 'would-update').length;
    const text = [
      plan.data.measured,
      '',
      ...rows.map(fleetRowLine),
      '',
      would > 0
        ? `${would} release${would === 1 ? '' : 's'} would be updated now (idle + compatible). Busy VMs are skipped — use the row's own Update (it asks) or Update at Quiet Window for those.`
        : 'Nothing would be updated now — every behind release is blocked, busy, locked or unmeasured (reasons above).',
    ].join('\n');
    if (would === 0) {
      showNotice(`Fleet update — dry run\n\n${text}`);
      return;
    }
    confirmDialog(text, () => {
      void (async () => {
        fleetUpdEl.classList.add('hc-busy');
        const run = await jsonFetch<FleetUpdateResult>('POST', '/api/releases/fleet-update', { dry_run: false, mode: 'immediate' });
        fleetUpdEl.classList.remove('hc-busy');
        if (run.status !== 200 || !run.data) {
          showNotice(`Fleet update refused (${run.status}): ${(run.data as { error?: string } | null)?.error ?? 'unstated'}`);
          return;
        }
        showNotice([run.data.measured, '', ...run.data.rows.map(fleetRowLine)].join('\n'));
        await loadVersions();
      })();
    }, `Update ${would} now`, { title: 'Update the fleet?', danger: false });
  }

  /** Jump to the first drifted release. Drift is computed only for releases
   * with a RUNNING VM (active or ideating) and those are always in the
   * default set, so this never has to expand the terminal tail — but it does
   * scroll the virtualised grid, which is what makes a below-the-fold alert
   * actionable at all. */
  function jumpToDrift(): void {
    const d = driftSummary(versions);
    if (!d || !gridApi) return;
    const want = new Set(d.releaseIds);
    let idx = -1;
    gridApi.forEachNode((node) => {
      if (idx < 0 && node.data && want.has(node.data.id) && typeof node.rowIndex === 'number') {
        idx = node.rowIndex;
      }
    });
    if (idx >= 0) gridApi.ensureIndexVisible(idx, 'middle');
  }

  /** Live fleet lane. Server-side cached, so this is one cheap read per
   * client — never a per-row fan-out to the VMs. */
  async function loadFleet(): Promise<void> {
    const { status, data } = await jsonFetch<FleetMetricsSnapshot>(
      'GET', '/api/releases/fleet-metrics',
    );
    fleet = status === 200 && data ? data : null;
    gridApi?.refreshCells({ columns: ['fleet', 'throughput'], force: true });
    // The cards read the SAME sweep (R44): without this repaint the strip
    // would show '—' forever — renderRecents ran before this lane landed.
    // reenter suppresses the entrance replay; no FLIP (the order is stable
    // on a metrics tick — only the values move).
    if (recentsPainted) paintRecents({ reenter: true, flip: false });
  }

  /** Escalation scorecard lane (REL-096/097). One cached console-side read
   * (the sweep asks each running VM's Bridge inbox); the Name cell's badge
   * and the card's "N decisions waiting" line both repaint from it. A
   * failed read leaves null — no badge, never a fabricated "0 open". */
  async function loadScorecard(): Promise<void> {
    const { status, data } = await jsonFetch<ScorecardSnapshot>('GET', '/api/scorecard');
    scorecard = status === 200 && data ? data : null;
    gridApi?.refreshCells({ columns: ['name'], force: true });
    if (recentsPainted) paintRecents({ reenter: true, flip: false });
  }

  /** Active-users lane (R37). Server-side cached like the fleet lane —
   * sockets + recent visits composed in one console-side sweep, one cheap
   * read per client. A failed read leaves null and the cells honest ('—'),
   * never a fabricated empty release. */
  async function loadPresence(): Promise<void> {
    const { status, data } = await jsonFetch<FleetPresenceSnapshot>(
      'GET', '/api/releases/fleet-presence',
    );
    presence = status === 200 && data ? data : null;
    gridApi?.refreshCells({ columns: ['activeUsers'], force: true });
  }

  /** Update Framework, two-phase (owner rulings 2026-09-03 + the Bridge-UI
   * ruling 2026-09-04, R12-R18). Clicking the Update Available pill IS the
   * update (R15): the column flips to 'Updating…' AT THE CLICK (R12), the
   * POST rides behind it, and the 202's durable row state takes over on the
   * next read so every tab shows the same install. Completion is the
   * animated settle back to Current (R13) — the "update complete" popup
   * this handler used to show is REMOVED (R14). Refusals and failures show
   * IN THE COLUMN with click-for-details (R16/R17) and clear themselves
   * (R18); nothing here pops a notice.
   *
   * There is deliberately NO unconditional "are you sure". An idle VM
   * updates on one call. A VM with live agent sessions comes back 409 with
   * the MEASURED count, and only then does a person get asked, with that
   * number in the sentence and on the button. (The ask still rides the
   * legacy confirmDialog — the plan's shared-file rule has dialog-system
   * migrate this call site signature-preserving.) The confirmation carries
   * the number as a ceiling, so a fleet that grew while the dialog was open
   * is reported rather than swept: the second 409 lands as a failed badge
   * naming the new count and changes nothing on the VM. */
  async function onUpdateFramework(row: ReleaseGridRow): Promise<void> {
    const post = (body: Record<string, unknown>) =>
      jsonFetch<UpdateFrameworkResponse>('POST', `/api/releases/${row.id}/update-framework`, body);
    const localFail = (detail: string) =>
      fwStore.set(row.id, 'failed', {
        ttlMs: FW_LOCAL_FAILED_TTL_MS, data: { src: 'local', detail },
      });
    const settle = () =>
      fwStore.set(row.id, 'updated', { ttlMs: FW_SETTLE_MS, data: { src: 'local' } });

    // R12: the state flips at the CLICK, not at the answer.
    fwStore.set(row.id, 'updating', { data: { src: 'local' } });

    // Phase 1: no consent field at all — the immediate lane and its 409
    // ask. The hot lane that used to ride `{mode:'hot'}` here is RETIRED
    // (#3402): the Bridge stopped advertising `capabilities.resume` in
    // #2414, so its one precondition can never be met again.
    const first = await post({});
    const step = updateFrameworkOutcome(row.name, first.status, first.data);
    if (step.kind === 'accepted') {
      // The durable 'updating' row exists server-side now; the next read
      // adopts it (and starts the fast poll that will see the verdict).
      await loadVersions();
      return;
    }
    if (step.kind === 'noop' || step.kind === 'done') {
      settle();
      await loadVersions();
      return;
    }
    if (step.kind === 'gate-refused') {
      // #828: the gate refused — nothing was touched. Painted as REFUSED in
      // this tab too (the durable row carries the same verdict on the next
      // read, with the structured cause), never as a failure.
      fwStore.set(row.id, 'refused', {
        ttlMs: FW_LOCAL_FAILED_TTL_MS, data: { src: 'local', detail: step.text },
      });
      await loadVersions();
      return;
    }
    if (step.kind === 'refused') {
      // Column-initiated, so column-rendered (R16) — never a popup.
      localFail(step.text);
      await loadVersions();
      return;
    }

    // 409 needs_confirmation: not updating (yet) — the question, with the
    // measured count, goes in front of the person.
    fwStore.clear(row.id);
    const acknowledged = step.activeSessions ?? 0;
    const cell = frameworkCell(row, versions);
    confirmDialog(
      `${step.text}\n\n${cell.tip}\n\nThe release is pinned to the new sha, so the next ` +
        `reboot keeps it.`,
      () => {
        void (async () => {
          fwStore.set(row.id, 'updating', { data: { src: 'local' } });
          // Phase 2: the authorisation, naming what it covers.
          const again = await post(confirmTerminateBody(acknowledged));
          const done = updateFrameworkOutcome(row.name, again.status, again.data);
          if (done.kind === 'accepted') {
            await loadVersions();
            return;
          }
          if (done.kind === 'noop' || done.kind === 'done') {
            settle();
            await loadVersions();
            return;
          }
          if (done.kind === 'confirm') {
            // The fleet grew while the dialog was open: report the new
            // number in the column rather than looping the prompt.
            localFail(
              `Update held: ${done.activeSessions} session(s) are running now, more than the ` +
                `${acknowledged} you confirmed. Nothing changed on the VM — click Update ` +
                `Available again to end ${done.activeSessions}.`,
            );
          } else {
            localFail(done.text);
          }
          await loadVersions();
        })();
      },
      step.confirmLabel,
      // R5 layout via dialog-system's engine (its 3-arg contract + additive
      // opts): the title names the decision, and ending live sessions is
      // destructive, so the primary carries the danger emphasis.
      { title: `Update '${row.name}' with sessions running?`, danger: true },
    );
  }

  /** The quiescent lane (framework auto-update, 2026-09-06): schedule the
   * update for the VM's next quiet window instead of asking to end live
   * sessions. The 202 carries the server's sentence — "scheduled: will
   * apply at the next quiet window (N live turns now)" — which the column
   * renders as Scheduled until the durable row state takes over. */
  async function onUpdateFrameworkQuiescent(row: ReleaseGridRow): Promise<void> {
    const r = await jsonFetch<UpdateFrameworkResponse>(
      'POST', `/api/releases/${row.id}/update-framework`, { mode: 'quiescent' },
    );
    const step = updateFrameworkOutcome(row.name, r.status, r.data);
    if (step.kind === 'scheduled') {
      fwStore.set(row.id, 'scheduled', { data: { src: 'local', detail: step.text } });
    } else if (step.kind === 'noop' || step.kind === 'done') {
      fwStore.set(row.id, 'updated', { ttlMs: FW_SETTLE_MS, data: { src: 'local' } });
    } else {
      fwStore.set(row.id, 'failed', { ttlMs: FW_LOCAL_FAILED_TTL_MS, data: { src: 'local', detail: step.text } });
    }
    await loadVersions();
  }

  /** R17 + #889: the failed badge's click — the STORED measured sentence
   * (server framework_update_error, or this tab's own refusal) AND the
   * retry.
   *
   * The owner's standing request (2026-09-08) is that clicking "Update
   * Failed" attempts the update again. It is offered as a CONFIRMATION, not
   * a bare re-fire, because the stored sentence is often the reason a retry
   * is pointless — daag-full-run's failure that night was a base mismatch
   * (`vm=e5f5db79 bundle=26a9fb52`), and re-firing it could only fail the
   * same way. So the person reads what failed, THEN decides. When the sweep
   * has already measured a standing objection the cell's `refusal` is shown
   * beside it and the confirm is not offered at all. */
  function onShowUpdateFailure(row: ReleaseGridRow, detail: string | null): void {
    const stored =
      detail ??
      versions?.updates?.[row.id]?.error ??
      'No stored detail — the record expired (the badge clears itself, R18); the next sweep re-measures the VM.';
    const entry = versions?.releases?.[row.id];
    const standing = entry?.baseBlock ?? null;
    const head = `Framework update failed on '${row.name}':\n\n${stored}`;
    if (standing) {
      showNotice(
        `${head}\n\nThis will fail again until it is fixed:\n${standing}`,
      );
      return;
    }
    if (!actionsVisible(row, me)) {
      showNotice(`${head}\n\n(Release owner or admin only — you cannot retry this update.)`);
      return;
    }
    confirmDialog(
      `${head}\n\nTry the update again now?`,
      () => { void onUpdateFramework(row); },
      'Try again',
      { title: `Retry framework update on '${row.name}'?` },
    );
  }

  /** 2026-09-08: the way out of 'version unknown' — one fresh sweep, then
   * the row's own verdict, in words, whichever way it went. */
  async function onRereadFramework(row: ReleaseGridRow): Promise<void> {
    await loadVersions(true);
    const c = frameworkCell(row, versions, Date.now(), fwStore.get(row.id) as FrameworkLocalState | null);
    const lines = c.tip.split('\n');
    showNotice(
      `Framework re-read for '${row.name}': ${c.label}` +
        (c.state === 'unknown'
          ? `\n\n${lines.slice(0, 2).join('\n')}\n\nStill unreadable. If the disk chip on this row is RED, the box cannot answer SSM at all — reclaim (or grow) the volume first.`
          : `\n\n${lines.slice(0, 3).join('\n')}`),
    );
  }

  /** 2026-09-08: the RED disk chip's verb — the framework's own sweep, run
   * on the box now; its measured line comes back verbatim, and so does
   * every refusal (unreachable box, a framework predating the script). */
  async function onReclaimDisk(row: ReleaseGridRow): Promise<void> {
    confirmDialog(
      `Runs the framework's disk reclaim on ${row.name}'s VM now: gc-parked-lanes.sh --apply ` +
        '(archived parked lanes only — the snapshot proof gates every removal) and ' +
        'checkpoint-fleet.py prune (the ring policy decides what stays). Nothing else is touched. ' +
        'Takes up to a few minutes; the measured line is shown when it returns.',
      () => {
        void (async () => {
          const { status, data } = await jsonFetch<{ measured?: string; rc?: number | null; still_red?: boolean; error?: string }>(
            'POST', `/api/releases/${row.id}/reclaim-disk`, {},
          );
          if (status >= 200 && status < 300) {
            showNotice(
              `Reclaim on '${row.name}' — ${data?.still_red ? 'STILL RED after collecting; a human must look' : 'done'}:\n\n${data?.measured ?? 'no measured line'}`,
            );
          } else {
            showNotice(`Reclaim refused (${status}): ${data?.error ?? 'unstated'}`);
          }
          await loadFleet();
        })();
      },
    );
  }

  /** Item 55.1 — Deploy Test Server from the row menu: the EXISTING
   * deploy-now lane (POST /api/releases/:id/deploy-now). The server's
   * mayRedeploy gate decides; every refusal renders VERBATIM (the
   * endpoint's refusals are measured sentences — no code branch yet, no
   * pipeline seat, forge head unreadable). */
  async function onDeployTestServer(row: ReleaseGridRow): Promise<void> {
    confirmDialog(
      'Builds the head of its code branch and ships it to the test server — ' +
        "the same lane as the deployments panel's Deploy now.",
      () => {
        void (async () => {
          const { status, data } = await jsonFetch<{ id?: string; error?: string }>(
            'POST', `/api/releases/${row.id}/deploy-now`, {},
          );
          if (status >= 200 && status < 300) {
            showNotice(`Deploy started for ${row.name}${data?.id ? ` — deployment ${data.id}` : ''}.`);
          } else {
            showNotice(`Deploy refused (${status}): ${data?.error ?? 'unstated'}`);
          }
          await loadRows();
        })();
      },
      'Deploy now',
      { title: `Deploy '${row.name}' to its test server?` },
    );
  }

  /** FW5 T5.6 — the checkpoint-picker wire shape (fork.ts ForkCheckpointRow). */
  interface CkptRow {
    checkpoint_id: string;
    captured_at: string | null;
    trigger: string | null;
    pinned: boolean;
    class: string | null;
    fork_grade: boolean;
    fleet_state?: string;
    corpus_only_eligible?: boolean;
    corpus_reachable?: boolean | null;
    framework_sha: string | null;
    fleet: { sessions: number | null; trees: number | null };
    fork_eligible: boolean;
    reason: string | null;
    quiesce: string | null;
    /** WS8 P3 (#2838): the DB half (fork.ts ForkReleaseDbSummary). null =
     * pre-WS8-A manifest; ABSENT = a pre-P3 server. Both render by name. */
    release_db?: CkptReleaseDb | null;
  }

  /** The §11 fidelity contract, short form — owner-mandated, UI-visible on
   * the create flow (never doc-buried). Rendered as a disclosure panel. */
  const FORK_IS: string[] = [
    'Every captured transcript, byte-exact to its settled boundary (Bridge sessions AND pi-team panes), with attachments.',
    'The corpus and every captured code tree at the RECORDED shas, with uncommitted dirt — married to the transcripts by a measured gate before the fork goes Active.',
    'Coordination state, decision surfaces and session rosters, restored.',
    'Quiet at birth: full history present, ZERO auto-resumed turns, autostaff off until you flip it.',
  ];
  const FORK_IS_NOT: string[] = [
    'Not a process clone — sessions wake at their last settled boundary; work a turn had half-done does not exist in the fork.',
    'Not a RAM clone — broker rosters and unacked deliveries are reconstructed or named as gaps.',
    'Not a credential clone — accounts and seats are recomposed; a changed seat is a NAMED diff, never silent.',
    'Not a wall-clock clone — timers, TTLs and date-derived behavior run at fork time.',
    'Not an infra clone — new VM, new subdomain, new release id; RM chat history is copied up to the fork moment.',
    'Not a future guarantee — agents are stochastic; divergence after the fork is the point (A/B on framework shas). The gate certifies the STARTING state, nothing after it.',
  ];

  /** FW5 T5.6 — Fork Release: checkpoint picker -> name -> create. The
   * listing is the T5.2 endpoint; refusals render VERBATIM (a 502 store
   * failure must never read as "no checkpoints" — fork.ts doctrine). */
  function openForkPicker(row: ReleaseGridRow): void {
    openDialog(modal, (body, close) => {
      body.innerHTML = `
      <h2 class="hc-dialog-title"></h2>
      <p class="hc-status hc-fork-status">Loading the fork library…</p>
      <form class="hc-create-form" hidden>
        <div class="hc-fork-list" role="radiogroup"></div>
        <label>Fork name <input name="name" required /></label>
        <label>Target framework sha <input name="target_framework_sha"
          placeholder="capture-time sha (default) — a different sha is the A/B lever" /></label>
        <div class="hc-fork-ctl"><span class="hc-roleopt-name">Fork mode</span><span data-mount="mode"></span></div>
        <div class="hc-fork-ctl"><span class="hc-roleopt-name">Plan-first mode</span><span data-mount="plan_first"></span></div>
        <p class="hc-status hc-fork-preflight" aria-live="polite"></p>
        <details class="hc-fork-contract">
          <summary>What a fork is — and is not</summary>
          <p class="hc-fork-contract-head">A fork IS:</p><ul data-k="is"></ul>
          <p class="hc-fork-contract-head">A fork is NOT:</p><ul data-k="not"></ul>
        </details>
        <p class="hc-dialog-msg"></p>
        <div class="hc-dialog-actions">
          ${iconBtn({ glyph: 'x', label: 'Cancel', size: 'sm', attrs: { 'data-act': 'no' } })}
          ${iconBtn({ glyph: 'check', label: 'Create fork', intent: 'additive', ctx: 'fork-release-dialog', size: 'sm', type: 'submit' })}
        </div>
      </form>`;
      (body.querySelector('.hc-dialog-title') as HTMLElement).textContent = `Fork Release — ${row.name}`;
      for (const [k, lines] of [['is', FORK_IS], ['not', FORK_IS_NOT]] as const) {
        const ul = body.querySelector(`ul[data-k="${k}"]`) as HTMLElement;
        for (const line of lines) {
          const li = document.createElement('li');
          li.textContent = line;
          ul.appendChild(li);
        }
      }
      body.querySelector('[data-act="no"]')!.addEventListener('click', close);
      const status = body.querySelector('.hc-fork-status') as HTMLElement;
      const form = body.querySelector('form') as HTMLFormElement;
      const list = body.querySelector('.hc-fork-list') as HTMLElement;
      const msg = body.querySelector('.hc-dialog-msg') as HTMLElement;
      (form.elements.namedItem('name') as HTMLInputElement).value = `${row.name} fork`;
      let chosen: string | null = null;
      // The two pickers ride the design system's fc select (#481 catalog:
      // a native <select> paints the OS list, not the theme — the
      // primitive-catalog guard refuses one by name). Their values are
      // read off the controls, never off FormData (a button-based control
      // has no form field).
      const MODE_OPTS = [
        { value: 'full', label: 'Full — restore fleet state from the package, marriage gate before Active' },
        { value: 'corpus-only', label: 'Corpus-only — cut branches only, fresh Bridge; leads re-staff from corpus state' },
      ];
      const modeSel = mkFcSelect({
        id: 'hcForkMode', label: 'Fork mode', cls: 'hc-select',
        options: MODE_OPTS, value: 'full', onPick: () => schedulePreflight(),
      });
      body.querySelector('[data-mount="mode"]')!.appendChild(modeSel.el);
      const planSel = mkFcSelect({
        id: 'hcForkPlanFirst', label: 'Plan-first mode', cls: 'hc-select',
        options: [
          { value: '', label: 'inherit (the captured / template seat)' },
          { value: 'true', label: 'ON — explicit seat write at boot (logged)' },
          { value: 'false', label: 'OFF — explicit seat write at boot (logged)' },
        ],
        value: '', onPick: () => schedulePreflight(),
      });
      body.querySelector('[data-mount="plan_first"]')!.appendChild(planSel.el);
      // ONE composer for the preflight and the create — the two must send
      // the same body, or the preflight certifies a different fork.
      const forkBody = (): Record<string, unknown> => {
        const fd = new FormData(form);
        return {
          name: fd.get('name'),
          checkpoint_id: chosen,
          ...(fd.get('target_framework_sha') ? { target_framework_sha: fd.get('target_framework_sha') } : {}),
          ...(modeSel.value === 'corpus-only' ? { corpus_only: true } : {}),
          ...(planSel.value ? { planning: { plan_first_enabled: planSel.value === 'true' } } : {}),
          // The launch-preflight acknowledgement (#547) rides the SAME
          // composer, so the dry-run certifies the body the create sends.
          ...ackBody(launchPf.vm(), launchPf.acknowledged()),
        };
      };

      // The shell renders NOW (owner report 2026-09-05: a slow library scan
      // left this dialog as a bare "Loading…" line for ~30 s): the form is
      // visible immediately — the name is typeable while the list loads —
      // with skeleton rows where the checkpoints will land and Create
      // disabled until they do.
      const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
      submitBtn.disabled = true;
      for (let i = 0; i < 3; i++) {
        const sk = document.createElement('div');
        sk.className = 'hc-ckpt-skel';
        list.appendChild(sk);
      }
      form.hidden = false;

      // The launch preflight over the snapshot the fork INHERITS (owner
      // batch 2026-09-06): FAIL blocks Create fork, WARN needs the "launch
      // anyway" tick, the acknowledgement rides the POST as preflight_ack.
      // Runs the moment the dialog opens (its inputs are the source row,
      // not the form), beside the library load; Create waits for BOTH.
      let libraryReady = false;
      // The fork dry-run's verdict (#529) is the THIRD term of the same
      // gate — before this rebase the two lanes each owned submitBtn and
      // the last writer won: judge() re-ENABLED Create after a dry-run
      // refusal, and the dry-run re-enabled it over an unacknowledged WARN.
      let dryRunOk = false;
      const judge = (): void => {
        const gate = ackVm(launchPf.vm(), launchPf.acknowledged());
        submitBtn.disabled = !(libraryReady && dryRunOk && gate.canSubmit);
      };
      const launchPf = mountPreflight(document, { verb: 'Create fork', onChange: judge });
      form.insertBefore(launchPf.el, msg);
      void (async () => {
        launchPf.set(null, 'running');
        let tplId = row.config_template_id;
        if (tplId === undefined) {
          const r = await jsonFetch<{ config_template_id?: string | null }>('GET', `/api/releases/${row.id}`);
          tplId = r.data?.config_template_id ?? null;
        }
        if (!tplId) {
          launchPf.set(null, 'idle', 'No config template on the source release — nothing to preflight (the fork is created unseeded).');
          return;
        }
        const nameNow = (form.elements.namedItem('name') as HTMLInputElement).value;
        const r = await jsonFetch<PreflightDto & { error?: string }>(
          'POST', `/api/config-templates/${tplId}/preflight`,
          { fork_of_release_id: row.id, inherited: true, lane: 'fork', name: nameNow },
        );
        if (r.status === 200 && r.data && Array.isArray(r.data.checks)) launchPf.set(r.data);
        else launchPf.set(null, 'error', r.data?.error ? `Preflight unavailable: ${r.data.error}` : undefined);
      })();

      void (async () => {
        const { status: st, data } = await jsonFetch<{
          scanned?: number; eligible?: number; checkpoints?: CkptRow[]; error?: string;
          checkpoint_class?: string | null; checkpoint_period_min?: number | null;
        }>('GET', `/api/releases/${row.id}/checkpoints`);
        list.textContent = ''; // the skeletons go, whatever the outcome
        if (st !== 200 || !data) {
          // VERBATIM refusal — 501 no provisioner, 502 store failure. Never
          // rendered as an empty library.
          status.textContent = `Fork library unavailable (${st}): ${data?.error ?? 'no response'}`;
          form.hidden = true;
          return;
        }
        const rows = data.checkpoints ?? [];
        if (rows.length === 0) {
          form.hidden = true;
          // Name the RELEASE'S OWN capture class when the last verified
          // boot measured it (owner report 2026-09-05: "scanned 0" on
          // every release, cause nameable only by journal archaeology).
          // No measured class (older release / no verified boot) keeps
          // the generic hint — never a guessed grade. ("Snapshot This
          // Moment" is #450's rename of the pin verb — merge-resolved.)
          const klass = data.checkpoint_class ?? null;
          const period = data.checkpoint_period_min ?? null;
          if (klass === 'fork') {
            status.textContent =
              `No checkpoints in the fork library yet (scanned 0). This release captures at FORK ` +
              `grade every ${period ?? 30} min — the first fork point lands within one cadence of ` +
              'boot, or Snapshot This Moment creates one now.';
          } else if (klass === 'disabled') {
            status.textContent =
              'No checkpoints in the fork library yet (scanned 0). This release\'s periodic ' +
              'checkpoint lane is DISABLED (checkpoint-policy.json) — nothing will ever land here ' +
              'until it is re-enabled at fork grade, or Snapshot This Moment captures one now.';
          } else if (klass) {
            status.textContent =
              `No checkpoints in the fork library yet (scanned 0). This release captures at ` +
              `${klass.toUpperCase()} grade${period ? ` every ${period} min` : ''} — such captures ` +
              'cannot fork and never reach the library. Fork points need checkpoint-policy.json ' +
              'class_periodic "fork" on the release checkout, or Snapshot This Moment.';
          } else {
            status.textContent =
              'No checkpoints in the fork library yet (scanned 0). The periodic fork-grade tick ' +
              '(checkpoint-policy.json class_periodic: "fork") and Snapshot This Moment create fork points.';
          }
          return;
        }
        status.textContent =
          `${data.eligible ?? 0} full-fork-eligible of ${data.scanned ?? rows.length} checkpoint(s)` +
          ` (${rows.filter((c) => c.corpus_only_eligible).length} corpus-only) — newest first. ` +
          'A full fork needs an uploaded fork-grade package; corpus-only forks cut from the recorded shas alone.';
        const byId = new Map(rows.map((c) => [c.checkpoint_id, c]));
        // The mode follows the pick: a row without fleet state can only be
        // a corpus-only fork, and the select says so instead of letting the
        // create refuse it after the fact.
        const syncMode = (): void => {
          const c = chosen ? byId.get(chosen) : null;
          if (!c) return;
          const fullOk = c.fork_eligible && c.fork_grade;
          modeSel.setOptions(MODE_OPTS.map((o) => (o.value === 'full' ? { ...o, disabled: !fullOk } : o)));
          if (!fullOk) modeSel.value = 'corpus-only';
          modeSel.set();
        };
        for (const c of rows) {
          const fullOk = c.fork_eligible && c.fork_grade;
          const eligible = fullOk || !!c.corpus_only_eligible;
          const opt = document.createElement('label');
          opt.className = `hc-roleopt hc-ckpt${eligible ? '' : ' hc-ckpt-off'}`;
          const radio = document.createElement('input');
          radio.type = 'radio';
          radio.name = 'ckpt';
          radio.value = c.checkpoint_id;
          radio.disabled = !eligible;
          radio.addEventListener('change', () => { chosen = c.checkpoint_id; syncMode(); });
          const text = document.createElement('span');
          const l1 = document.createElement('span');
          l1.className = 'hc-roleopt-name';
          const when = c.captured_at ? new Date(c.captured_at).toLocaleString() : '(no timestamp)';
          l1.textContent = when;
          for (const [show, cls, label, tip] of [
            [c.pinned, 'hc-ckpt-b-pinned', 'PINNED', 'Pinned — kept indefinitely'],
            [c.fork_grade, 'hc-ckpt-b-grade', 'FORK-GRADE', 'Full-fidelity capture (all transcripts, uploads, seats, dirt)'],
            [!c.fork_grade, 'hc-ckpt-b-light', (c.class ?? 'light').toUpperCase(), 'Not fork-grade — cannot fork'],
            [c.quiesce === 'partial', 'hc-ckpt-b-partial', 'QUIESCE: PARTIAL', 'The pin barrier timed out or was pierced — stragglers are named in the manifest (D5)'],
            [c.quiesce === 'full', 'hc-ckpt-b-full', 'QUIESCED', 'Captured at a fleet-wide settled boundary'],
            [c.fork_grade && !c.fork_eligible && !!c.corpus_only_eligible, 'hc-ckpt-b-light', 'CORPUS-ONLY',
              `Fleet state ${c.fleet_state ?? 'unavailable'} — no package in the store; a corpus-only fork cuts from the recorded shas with a fresh Bridge`],
          ] as const) {
            if (!show) continue;
            const b = document.createElement('span');
            b.className = `hc-ckpt-b ${cls}`;
            b.textContent = label;
            b.dataset.tip = tip;
            l1.appendChild(b);
          }
          const l2 = document.createElement('span');
          l2.className = 'hc-roleopt-blurb';
          l2.textContent =
            `${c.trigger ?? 'unknown trigger'} · ${c.fleet.sessions ?? '?'} sessions, ${c.fleet.trees ?? '?'} trees` +
            ` · framework ${(c.framework_sha ?? '').slice(0, 12) || '?'}` +
            (eligible
              ? (fullOk ? '' : ` · fleet state ${c.fleet_state ?? 'unavailable'} — corpus-only`)
              : ` · not eligible — ${c.reason ?? (c.fork_grade ? 'unnamed' : 'not fork-grade')}`);
          if (!eligible) opt.dataset.tip = c.reason ?? 'not fork-grade (offsets-only captures cannot fork)';
          // WS8 P3 (#2838): the DB column — never blank, never a bare tick.
          const l3 = document.createElement('span');
          l3.className = 'hc-roleopt-blurb hc-ckpt-db';
          l3.textContent = forkReleaseDbCellText(c.release_db);
          l3.dataset.tip = forkReleaseDbCellTitle(c.release_db);
          text.appendChild(l1);
          text.appendChild(l2);
          text.appendChild(l3);
          opt.appendChild(radio);
          opt.appendChild(text);
          list.appendChild(opt);
        }
        // Default: the newest eligible checkpoint (rows arrive newest-first).
        const first = list.querySelector<HTMLInputElement>('input[name="ckpt"]:not(:disabled)');
        if (first) {
          first.checked = true;
          chosen = first.value;
          syncMode();
        }
        libraryReady = true;
        judge();
        // Gap 3 (2026-09-06): Create stays disabled until the server's
        // PREFLIGHT (POST …/fork?dry_run=1 — every refusal the create lane
        // would raise, zero rows written) answers ok for the CURRENT body;
        // any change re-runs it. A refusal renders verbatim, naming the
        // check that refused, before a row can exist. It is the THIRD term
        // in judge()'s gate, beside the library and the launch preflight.
        void preflight();
      })();

      const preflightLine = body.querySelector('.hc-fork-preflight') as HTMLElement;
      let preflightSeq = 0;
      async function preflight(): Promise<void> {
        const seq = ++preflightSeq;
        dryRunOk = false;
        judge();
        if (!chosen) {
          preflightLine.textContent = 'Pick a fork-eligible checkpoint to preflight.';
          return;
        }
        preflightLine.textContent = 'Preflighting… (the create lane\'s every refusal, zero rows written)';
        const { status: st, data } = await jsonFetch<{
          ok?: boolean; error?: string;
          checks?: Array<{ check: string; ok: boolean | null; detail: string }>;
          would_create?: { fork_mode?: string; framework_sha?: string; framework_source?: string;
            planning_override?: Record<string, unknown> | null; config_template?: { name?: string | null; source?: string } };
        }>('POST', `/api/releases/${row.id}/fork?dry_run=1`, forkBody());
        if (seq !== preflightSeq) return; // a newer preflight superseded this one
        const checks = data?.checks ?? [];
        const passed = checks.filter((c) => c.ok === true).length;
        const skipped = checks.filter((c) => c.ok === null).map((c) => c.check);
        if (st === 200 && data?.ok) {
          const w = data.would_create ?? {};
          preflightLine.textContent =
            `Preflight OK — ${passed} check(s) passed` +
            (skipped.length ? `, ${skipped.length} not probed here (${skipped.join(', ')})` : '') +
            `. Mode ${w.fork_mode ?? 'full'}; framework ${(w.framework_sha ?? '').slice(0, 8)} (${w.framework_source ?? '?'}); ` +
            `template ${w.config_template?.name ?? 'none'} (${w.config_template?.source ?? '?'})` +
            (w.planning_override ? `; planning override ${JSON.stringify(w.planning_override)} applied at boot` : '') + '.';
          dryRunOk = true;
          judge();
          return;
        }
        const refused = checks.find((c) => c.ok === false);
        preflightLine.textContent =
          `Preflight refused (${st}${refused ? `, ${refused.check}` : ''}): ${data?.error ?? 'no response'}`;
        dryRunOk = false;
        judge();
      }
      let preflightTimer: number | null = null;
      const schedulePreflight = (): void => {
        if (preflightTimer !== null) window.clearTimeout(preflightTimer);
        preflightTimer = window.setTimeout(() => { preflightTimer = null; void preflight(); }, 350);
      };
      form.addEventListener('input', (ev) => {
        if ((ev.target as HTMLElement | null)?.getAttribute('name') === 'ckpt') return; // radios below
        schedulePreflight();
      });
      list.addEventListener('change', () => { void preflight(); });

      form.addEventListener('submit', (ev) => {
        ev.preventDefault();
        msg.textContent = '';
        if (!chosen) {
          showDialogMsg(msg, 'Pick a fork-eligible checkpoint first.');
          return;
        }
        const gate = ackVm(launchPf.vm(), launchPf.acknowledged());
        if (!gate.canSubmit) {
          showDialogMsg(msg, gate.reason ?? 'Preflight refused.');
          return;
        }
        const submit = form.querySelector('button[type="submit"]') as HTMLButtonElement;
        submit.disabled = true;
        submit.classList.add('hc-btn-busy');
        void (async () => {
          const { status: st, data } = await jsonFetch<{
            slug?: string;
            error?: string;
            refusal?: string;
            preflight?: PreflightDto;
            fork?: {
              checkpoint_id?: string; code_sha?: string; corpus_sha?: string; framework_sha?: string;
              rm_messages_copied?: number; rm_sessions_copied?: number; rm_cutoff?: string;
              fork_mode?: string; overridden?: string[];
            };
          }>('POST', `/api/releases/${row.id}/fork`, forkBody());
          submit.disabled = false;
          submit.classList.remove('hc-btn-busy');
          if (st === 409 && (data?.refusal === 'preflight-failed' || data?.refusal === 'preflight-warn') && data?.preflight) {
            // The server re-judged and refused: paint ITS result, re-gate.
            launchPf.set(data.preflight);
            showDialogMsg(msg, data.error ?? 'preflight refused');
            judge();
            return;
          }
          if (st === 201) {
            close();
            const f = data?.fork ?? {};
            showNotice(
              `Fork '${data?.slug}' created from checkpoint ${(f.checkpoint_id ?? '').slice(0, 10)}… — ` +
                `branches cut at code ${(f.code_sha ?? '').slice(0, 8)} / corpus ${(f.corpus_sha ?? '').slice(0, 8)}, ` +
                `framework ${(f.framework_sha ?? '').slice(0, 8)}; RM history copied: ` +
                `${f.rm_messages_copied ?? 0} message(s) up to ${f.rm_cutoff ?? '?'}` +
                (f.overridden?.length ? `; overridden: ${f.overridden.join(', ')}` : '') + '. ' +
                (f.fork_mode === 'corpus-only'
                  ? 'CORPUS-ONLY: no fleet state is restored — a fresh Bridge boots over the cut branches and leads re-staff from corpus state.'
                  : 'The fork boots QUIET (autostaff off) and goes Active only after the marriage gate passes.'),
            );
            await Promise.all([loadRows(), refreshSlots()]);
            return;
          }
          showDialogMsg(msg, data?.error ?? `refused (${st})`);
        })();
      });
    }, { className: 'hc-fork-dialog' });
  }

  /** FW5 T5.6 — Snapshot This Moment: kick the quiesce+capture lane, then poll
   * the job until it settles; the measured result (full|partial with
   * stragglers NAMED — D5) renders verbatim. */
  async function onPinMoment(row: ReleaseGridRow): Promise<void> {
    confirmDialog(
      'The fleet is HELD at a settled turn boundary (bounded, 120s), a fork-grade checkpoint ' +
        'is captured and uploaded, then the fleet is released. Human actions still win during ' +
        'the hold (the capture goes partial, stragglers named).',
      () => {
        void (async () => {
          const kick = await jsonFetch<{ pin?: { phase?: string }; error?: string }>(
            'POST', `/api/releases/${row.id}/pin-moment`, {},
          );
          if (kick.status !== 202) {
            showNotice(`Snapshot refused (${kick.status}): ${kick.data?.error ?? 'unstated'}`);
            return;
          }
          showNotice(`Snapshotting '${row.name}' — holding the fleet at a settled boundary…`);
          const t0 = Date.now();
          while (Date.now() - t0 < 10 * 60_000) {
            await new Promise((r) => setTimeout(r, 2000));
            const st = await jsonFetch<{
              pin?: {
                phase?: string;
                result?: {
                  ok?: boolean; quiesce_mode?: string | null; stragglers?: string[];
                  checkpoint_id?: string | null; error?: string | null;
                } | null;
              };
            }>('GET', `/api/releases/${row.id}/pin-moment`);
            const job = st.data?.pin;
            if (!job || job.phase === 'running') continue;
            const r = job.result ?? {};
            if (job.phase === 'done') {
              showNotice(
                `Snapshot captured. Quiesce ${r.quiesce_mode ?? 'unknown'}` +
                  `${r.stragglers?.length ? ` — stragglers: ${r.stragglers.join(', ')}` : ''}; ` +
                  `checkpoint ${r.checkpoint_id ?? '(id unreported)'} is in the fork library, pinned.`,
              );
            } else {
              showNotice(`Snapshot FAILED: ${r.error ?? 'unstated'} (fleet release runs on the VM regardless).`);
            }
            return;
          }
          showNotice('Snapshot still running after 10 minutes — check the fork library and the console journal.');
        })();
      },
      'Snapshot this moment',
      { title: `Snapshot this moment on '${row.name}'?` },
    );
  }

  /** `yesLabel` names the action on the button when "Confirm" would be too
   * vague to consent to — a destructive step says what it destroys. Set as
   * textContent, not interpolated into the markup.
   *
   * The 3-arg signature is a CONTRACT (framework-column's 409 flow calls it
   * exactly so — collision map §5); the trailing options bag is additive.
   * With no explicit title the verb becomes the title line, so a legacy call
   * still renders the R5 layout (title / consequence body / verb primary).
   * Content owners (abort-flow, framework-column) restyle their sentences —
   * and set `danger` where their verb destroys — on top of this engine. */
  function confirmDialog(
    text: string,
    onYes: () => void,
    yesLabel = 'Confirm',
    opts: { title?: string; danger?: boolean } = {},
  ): void {
    dlgConfirm(modal, {
      title: opts.title ?? (yesLabel === 'Confirm' ? 'Please confirm' : `${yesLabel}?`),
      body: text,
      confirmLabel: yesLabel,
      danger: opts.danger === true,
      onConfirm: onYes,
    });
  }

  async function onAction(action: ReleaseAction, row: ReleaseGridRow): Promise<void> {
    // The R5 layout per verb: title names the decision, body names the
    // CONSEQUENCE, the primary is the verb, and destructive verbs (abort
    // stops the VM; down TERMINATES it) carry the danger emphasis. The
    // abort dialog's further content/flow is abort-flow's seam — these
    // sentences are the migration floor, not that seam's ceiling.
    const prompts: Record<ReleaseAction, { title: string; body: string; verb: string; danger: boolean }> = {
      abort: {
        title: `Abort release '${row.name}'?`,
        body: 'This aborts the release and stops its VM (if any); the reaper terminates the VM after the retention window.',
        verb: 'Abort release', danger: true,
      },
      reopen: {
        title: `Reopen release '${row.name}'?`,
        body: 'This takes a fleet slot (governor-checked at click time).',
        verb: 'Reopen release', danger: false,
      },
      fire: {
        // MUST-FIX (WF-A off-system #3): this read `danger: false` — styled and
        // gated as a routine action — while its OWN body says the fleet starts
        // taking real agent turns and SPENDING. Money leaving the account on a
        // click is the destructive class by any reading, so the flag now
        // agrees with the sentence beside it.
        title: `Fire replay '${row.name}'?`,
        body: 'The parked fleet starts taking REAL agent turns — and spending — from the checkpoint moment.',
        verb: 'Fire replay', danger: true,
      },
      down: {
        title: `Down replay '${row.name}'?`,
        body: 'Its VM is TERMINATED (not stopped) and the row closes — replays are disposable. ' +
          'If EC2 no longer has the instance, nothing is terminated and the row still closes.',
        verb: 'Down replay', danger: true,
      },
      'vm-start': {
        title: `Start the VM of '${row.name}'?`,
        body: `EC2 StartInstances on ${row.vm_instance_id ?? 'the recorded instance'} — the machine only. ` +
          'The release stays as it is; the console waits for the Bridge to answer (billing resumes).',
        verb: 'Start VM', danger: false,
      },
      'vm-stop': {
        title: `Stop the VM of '${row.name}'?`,
        body: `EC2 StopInstances on ${row.vm_instance_id ?? 'the recorded instance'} — the machine only. ` +
          'Every live agent turn on it ends now; the release is NOT aborted and the slot is freed. Start VM brings it back.',
        verb: 'Stop VM', danger: true,
      },
      'vm-relaunch': {
        title: `Relaunch the VM of '${row.name}' on a fresh instance?`,
        body: `This ENDS the live fleet on ${row.vm_instance_id ?? 'the recorded instance'}: forensic snapshots of its disks are taken first, ` +
          'then the instance is TERMINATED and a fresh one is provisioned on the current AMI with the current framework bundle. ' +
          'The corpus branch and S3 checkpoints survive; on-VM session state, live agent turns and the test-server deployment on that machine do not ' +
          '(the snapshots keep the disks for forensics). The release stays as it is.',
        verb: 'Relaunch VM', danger: true,
      },
      'vm-provision': {
        title: `Provision a VM for '${row.name}'?`,
        body: 'This release records no usable machine right now — EC2 RunInstances on the CURRENT AMI ' +
          'gives it one. Nothing is snapshotted and nothing is terminated (there is nothing recorded to ' +
          'take); the release stays as it is.',
        verb: 'Provision VM', danger: false,
      },
    };
    // Replay verbs ride their own routes (the release lifecycle refuses
    // replay rows by name, and vice versa is enforced server-side too); the
    // VM verbs ride the lifecycle-NEUTRAL machine routes (any row class).
    const path = action === 'fire' || action === 'down'
      ? `/api/releases/${row.id}/replay/${action}`
      : action === 'vm-start'
        ? `/api/releases/${row.id}/vm/resume`
        : action === 'vm-stop'
          ? `/api/releases/${row.id}/vm/stop`
          : action === 'vm-relaunch'
            ? `/api/releases/${row.id}/vm/relaunch`
            : action === 'vm-provision'
              ? `/api/releases/${row.id}/vm/provision`
              : `/api/releases/${row.id}/${action}`;
    confirmDialog(
      prompts[action].body,
      () => {
        void (async () => {
          const { status, data } = await jsonFetch<Record<string, unknown>>(
            'POST', path,
          );
          if (action === 'fire' && status === 200) {
            const tail = String((data as { tail?: string } | null)?.tail ?? '');
            showNotice(`Fired — the replay fleet is live. Measured tail: ${tail.slice(-300) || '(none)'}`);
          } else if (action === 'vm-relaunch' && (status === 200 || status === 202)) {
            // #4449 — the response's carry line + survive note ride the notice.
            showNotice(relaunchNoticeText(status, data as RelaunchNoticeData | null));
          } else if (VM_ACTIONS.has(action) && status === 200) {
            // The machine routes answer their own measured line — show it,
            // it names what happened (stopped 1 / already stopped 0 / the
            // Bridge answered in Ns).
            const measured = String((data as { measured?: string } | null)?.measured ?? '');
            showNotice(`${ACTION_VERB[action]}: ${measured || 'done'}`);
          } else if (action === 'down' && status === 200 && (data as { gone?: boolean } | null)?.gone === true) {
            showNotice(`Down: the recorded instance was already gone from EC2 — nothing terminated, row closed. ${String((data as { fleet_down?: string }).fleet_down ?? '')}`);
          } else if (status === 202) {
            // Accepted, but gated: the server has not flipped the row yet and
            // will only do so if the gate passes. Say that, rather than
            // silently reloading a grid that still shows the old status —
            // and never mistake it for a refusal (the pre-202 handler treated
            // every non-200 as one).
            const tr = (data as { transition?: { gate?: string; stays?: string } } | null)?.transition;
            showNotice(
              `${ACTION_VERB[action]} accepted — waiting on the ` +
                `${tr?.gate ?? 'boot gate'}. The release stays '${tr?.stays ?? row.status}' until it passes.`,
            );
          } else if (status !== 200) {
            if (action === 'reopen') {
              showNotice(reopenRefusalMessage(status, (data ?? {}) as { used?: number; total?: number; detail?: string; error?: string }));
            } else {
              // The NAMED card (title + consequence), never the CLI dump.
              const card = actionFailureNotice(action, status, data as Parameters<typeof actionFailureNotice>[2]);
              dlgNotice(modal, { title: card.title, body: card.body });
            }
          }
          await Promise.all([loadRows(), refreshSlots()]);
        })();
      },
      prompts[action].verb,
      { title: prompts[action].title, danger: prompts[action].danger },
    );
  }

  /** Outcome notices are single-acknowledgment dialogs whose whole content
   * is one measured sentence — they render body-only (no filler "Notice"
   * title line; real titles are the content owners' sentences to write). */
  /** #3288 — the stale-transition cell's verb.
   *
   * POST /api/releases/:id/transition/reconcile completes the stranded gate
   * (a running recorded VM's boot report is RE-READ and its verdict written)
   * or clears the transition when the VM is gone. It launches, stops,
   * terminates and snapshots NOTHING, which is why there is no confirmation
   * dialog in front of it — there is nothing to authorise and nothing to
   * spend. A refusal is shown by name; either way the rows are re-read, so
   * the cell answers with the row rather than with a popup. */
  async function onReconcileTransition(row: ReleaseGridRow): Promise<void> {
    const { status, data } = await jsonFetch<{ ok?: boolean; lane?: string; measured?: string; error?: string }>(
      'POST', `/api/releases/${row.id}/transition/reconcile`, {});
    if (status !== 200) {
      showNotice(
        `Reconcile refused on '${row.name}' — ${data?.error ?? `the console answered ${status}`}`,
      );
    }
    await loadRows();
  }

  function showNotice(text: string): void {
    dlgNotice(modal, { body: text });
  }

  /** The Create-release form now lives in create-dialog.ts (owner batch
   * 2026-09-05, docs/create-release-browsers.md) so the suite drives the
   * REAL dialog without mounting this grid page; this page only wires the
   * host, the apex read, and the post-create reload. */
  root.querySelector('#hcCreate')!.addEventListener('click', () =>
    openCreateWired(modal, {
      apex: () => apex,
      onCreated: async () => { await Promise.all([loadRows(), refreshSlots()]); },
    }));
  // R32 (console half): ONE user verb re-runs every named loader the page
  // already polls — the same functions the timers call, never a parallel
  // fetch lane. The spin class rides the in-flight window (registered in
  // the reduced-motion guard); a click while spinning is refused — the spin
  // IS the acknowledgment, and doubled loaders would race their paints.
  const refreshBtn = root.querySelector('#hcRefresh') as HTMLButtonElement | null;
  refreshBtn?.addEventListener('click', () => {
    if (refreshBtn.classList.contains('hc-spinning')) return;
    refreshBtn.classList.add('hc-spinning');
    void Promise.all([loadRows(), refreshSlots(), loadFleet(), loadScorecard(), loadVersions(), loadRecents()])
      .finally(() => refreshBtn.classList.remove('hc-spinning'));
  });
  driftEl.addEventListener('click', jumpToDrift);
  fleetUpdEl.addEventListener('click', () => void onFleetUpdate());
  listMoreEl.addEventListener('click', () => {
    showTerminal = !showTerminal;
    paintRows();
  });

  // Grid-state persistence: ui_state(grid_preset,'home'). ONE blob carries
  // columns + pins + colFilters (item 55.8's "pins persist the same way the
  // filters persist" — literally the same seat write). Legacy seats were a
  // bare column-state array; parseGridSeatBlob reads both. 404-tolerant.
  let saveTimer: number | null = null;
  function persistGridState(): void {
    if (!gridApi) return;
    if (saveTimer) window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(() => {
      const blob: Record<string, unknown> = { columns: gridApi!.getColumnState() };
      if (pins !== null) blob.pins = pins; // absent = shipped defaults
      if (anyColumnFilterActive(colFilters)) blob.colFilters = colFilters;
      void jsonFetch('PUT', '/api/ui-state/grid-preset/home', { state: blob });
    }, 750);
  }

  void (async () => {
    // ONE /api/me per mount: the shell fetched it for the account circle and
    // the nav reveal (both its chrome now); this page reuses the answer. The
    // 401 -> /auth/login redirect is the shell's too — bail out rather than
    // racing grid fetches against a navigation that is already underway.
    const who = await shell.whoami;
    me = who.me;
    if (who.status === 401) return;
    // A Create button that 403s is worse than no button — the server still
    // refuses either way. (The admin NAV entries are the shell's half.)
    const createBtn = root.querySelector('#hcCreate') as HTMLElement | null;
    if (createBtn && !canCreate(me)) createBtn.setAttribute('hidden', '');
    // The apex comes from the SERVER (/api/me `apex` = its domains[0]),
    // never from our own hostname: see consoleApex.
    apex = consoleApex(me, location.hostname);

    const gridEl = root.querySelector('#hcGrid') as HTMLElement;

    /** Build the grid.
     *
     * `themeQuartz.withParams(...)` is still evaluated once, at construction —
     * but gridThemeParams() now hands it `var(--hc-*)` STRINGS, so the palette
     * resolves at PAINT time and a theme change re-skins the grid through the
     * same inline custom properties as the rest of the page. The
     * destroy-and-rebuild-on-theme-change this function existed to serve is
     * GONE (design-system-foundation); buildGrid runs once per mount.
     */
    function buildGrid(): void {
      currentDefs = buildColumnDefs({
        me,
        apex,
        costs: () => costs,
        ec2Costs: () => ec2Costs,
        versions: () => versions,
        fleet: () => fleet,
        presence: () => presence,
        scorecard: (id) => scorecard?.releases[id] ?? null,
        transitionBudgets: () => txBudgets,
        onReconcileTransition: (row) => void onReconcileTransition(row),
        onAction: (a, row) => void onAction(a, row),
        onUpdateFramework: (row) => void onUpdateFramework(row),
        onShowUpdateFailure: (row, detail) => onShowUpdateFailure(row, detail),
        onShowUpdateBlocked: (row, blocked) =>
          showNotice(`Framework update blocked on '${row.name}' — no in-place update can succeed:\n\n${blocked.reason}\n\nRemedy: ${blocked.remedy}`),
        onRereadFramework: (row) => void onRereadFramework(row),
        onReclaimDisk: (row) => void onReclaimDisk(row),
        fwLocal: (id) => fwLocal(id),
        branchHref: (branch) =>
          branch && branchTpl ? branchTpl.replace('{branch}', encodeURIComponent(String(branch))) : null,
        // FW5 T5.6: fork lineage resolves against the loaded list — the
        // short id is the honest fallback for a purged/unloaded source.
        releaseRef: (id) => {
          const src = allRows.find((r) => r.id === id);
          return src ? { name: src.name, slug: src.slug } : null;
        },
        // The inverse accessor. `allRows` IS the whole loaded release list
        // here, which is why the column passes `complete: true`.
        allReleaseRows: () => allRows,
      });
      // Item 55 machinery — filter/pin/copy over WHATEVER defs exist, so a
      // column another lane adds participates with zero wiring.
      const machineryDefs = withGridMachinery(currentDefs, {
        defaultPins: DEFAULT_HOME_PINS,
        unfiltered: ['actions'],
        getFilter: (c) => colFilters[c],
        setFilter: (c, s) => {
          if (s === null) delete colFilters[c];
          else colFilters[c] = s;
        },
        displayValue: (c, r) => gridDisplayValue(currentDefs, c, r as ReleaseGridRow),
        // R39: the Active-users column is MULTI-valued — the filter offers
        // each user's NAME as its own value (the machinery flattens these
        // for the list and judges rows entity-by-entity), while the cell
        // renders pictures. Every other column stays single-valued.
        displayValues: (c, r) =>
          c === 'activeUsers'
            ? activeUsersDisplays(r as ReleaseGridRow, presence)
            : null,
        rows: () => candidateRows(),
        onFilterWrite: () => persistGridState(),
      });
      gridApi = createGrid<ReleaseGridRow>(gridEl, {
        // Derived from the per-user appearance seat (appearance.ts), not the
        // five hexes this used to hardcode — the grid is most of the page, so
        // a themed console with a dark grid would look broken.
        theme: themeQuartz.withParams(gridThemeParams()),
        columnDefs: machineryDefs,
        rowData: [],
        // ON, to match the rest of the screen. The requirement is that
        // everything here animates like the release UI, and the grid is most
        // of the page — rows that jump between sorts while the cards above
        // them ease in is the one place the inconsistency would be obvious.
        // AG Grid owns this transition, so it is not tunable to our motion
        // tokens; it is close enough in duration not to read as a different
        // language, and it is suppressed for reduced-motion users by the
        // browser rather than by us.
        animateRows: true,
        onColumnMoved: persistGridState,
        onColumnResized: persistGridState,
        onColumnVisible: persistGridState,
      });
      // Owner item 67: the transcript's rail treatment on every AG grid
      // (shared pi-stack layer — always-visible rails, pinned-aware
      // horizontal span, no overscroll bounce). Self-detaches on
      // gridPreDestroyed, so rebuilds through buildGrid stay clean.
      if (gridApi) attachGridScrollRails(gridEl, gridApi);
    }

    buildGrid();

    // ---- item 55 verbs the menus drive ---------------------------------

    /** Pin exactly the effective set, everything else unpinned — one
     * authoritative pass (SET semantics; the pinned block keeps the grid's
     * own column order). */
    function applyPins(): void {
      const ids = currentDefs.map((d) => d.colId).filter((c): c is string => !!c);
      gridApi?.applyColumnState({
        state: effectivePins(pins, DEFAULT_HOME_PINS, ids).map((colId) => ({ colId, pinned: 'left' as const })),
        defaultState: { pinned: null },
      });
    }

    /** Displayed rows in PAINT order (after AG sort + filters) — what Copy
     * Column Values emits is what the screen shows. */
    function displayedRows(): ReleaseGridRow[] {
      const out: ReleaseGridRow[] = [];
      const n = gridApi?.getDisplayedRowCount() ?? 0;
      for (let i = 0; i < n; i++) {
        const d = gridApi?.getDisplayedRowAtIndex(i)?.data;
        if (d) out.push(d);
      }
      return out;
    }

    function clearAllFilters(): void {
      colFilters = {};
      void gridApi?.setFilterModel(null); // comps mirror via setModel(null)
      gridApi?.refreshHeader();
      persistGridState();
    }

    function resetColumnsToDefault(): void {
      pins = null;
      gridApi?.resetColumnState(); // machinery defs carry the default pins
      applyPins(); // explicit second pass — measured, not assumed
      gridApi?.refreshHeader();
      // The persisted columns/pins are CLEARED, not overwritten with a copy
      // of the defaults. Filters stay — this is a columns-only action.
      if (saveTimer) {
        window.clearTimeout(saveTimer);
        saveTimer = null;
      }
      const keep = anyColumnFilterActive(colFilters) ? { colFilters } : null;
      void jsonFetch('PUT', '/api/ui-state/grid-preset/home', { state: keep });
    }

    function runRowAction(action: RowMenuAction, row: ReleaseGridRow): void {
      if (action === 'open') {
        const href = releaseHref(row, apex);
        if (href) window.open(href, '_blank', 'noopener,noreferrer');
      } else if (action === 'update-framework') {
        void onUpdateFramework(row);
      } else if (action === 'update-framework-quiescent') {
        void onUpdateFrameworkQuiescent(row);
      } else if (action === 'reread-framework') {
        void onRereadFramework(row);
      } else if (action === 'reclaim-disk') {
        void onReclaimDisk(row);
      } else if (action === 'deploy-test-server') {
        void onDeployTestServer(row);
      } else if (action === 'fork') {
        openForkPicker(row);
      } else if (action === 'pin-recent') {
        void onPinRecent(row.id);
      } else if (action === 'pin-moment') {
        void onPinMoment(row);
      } else {
        void onAction(action, row);
      }
    }

    /** Item 55.1/55.7 — the row menu (cellCol non-null adds Copy Value). */
    function openRowMenu(row: ReleaseGridRow, x: number, y: number, cellCol: string | null): void {
      const items: CtxMenuItem[] = buildRowMenuSpec(row, me, versions, apex, isPinnedRecent(row.id), Date.now(), fleet).map((s) => ({
        label: s.label,
        icon: s.icon,
        disabled: !s.enabled,
        ...(s.note ? { note: s.note } : {}),
        ...(s.tip ? { tip: s.tip } : {}),
        ...(s.danger ? { danger: true } : {}),
        run: () => runRowAction(s.action, row),
      }));
      if (cellCol) {
        items.push({ label: '—', icon: '', sep: true });
        items.push({
          label: 'Copy Value',
          icon: 'copy',
          run: () => void copyPlainText(gridDisplayValue(currentDefs, cellCol, row)),
        });
      }
      openCtxMenu(x, y, items);
    }

    /** Item 55.6 + the follow-up trio — the header menu. */
    function openHeaderMenu(colId: string, x: number, y: number): void {
      const ids = currentDefs.map((d) => d.colId).filter((c): c is string => !!c);
      const eff = effectivePins(pins, DEFAULT_HOME_PINS, ids);
      const runs: Record<HeaderMenuEntry['id'], () => void> = {
        customize: openColumnsPopover,
        'copy-column': () =>
          void copyPlainText(columnCopyText(displayedRows().map((r) => gridDisplayValue(currentDefs, colId, r)))),
        pin: () => {
          pins = togglePin(eff, colId);
          applyPins();
          persistGridState();
        },
        autosize: () => {
          gridApi?.autoSizeAllColumns();
          persistGridState(); // AG's autosize source is not a user column event
        },
        reset: resetColumnsToDefault,
        'clear-filters': clearAllFilters,
      };
      const items: CtxMenuItem[] = [];
      for (const s of headerMenuSpec(eff.includes(colId), anyColumnFilterActive(colFilters))) {
        if (s.sep) items.push({ label: '—', icon: '', sep: true });
        items.push({
          label: s.label,
          icon: s.icon,
          disabled: !s.enabled,
          ...(s.tip ? { tip: s.tip } : {}),
          run: runs[s.id],
        });
      }
      openCtxMenu(x, y, items);
    }

    // ONE delegated listener on the grid HOST (rebuild-proof: the host
    // outlives every theme-change grid instance; the closure reads the
    // current gridApi). Header cells and data cells both carry col-id.
    const onGridCtx = (e: MouseEvent): boolean => {
      const t = e.target as HTMLElement;
      const head = t.closest<HTMLElement>('.ag-header-cell[col-id]');
      if (head) {
        const colId = head.getAttribute('col-id') ?? '';
        if (!colId) return false;
        openHeaderMenu(colId, e.clientX, e.clientY);
        return true;
      }
      const rowEl = t.closest<HTMLElement>('[row-index]');
      if (!rowEl) return false;
      const idx = Number(rowEl.getAttribute('row-index'));
      if (!Number.isFinite(idx)) return false;
      const data = gridApi?.getDisplayedRowAtIndex(idx)?.data;
      if (!data) return false;
      const cellCol = t.closest<HTMLElement>('.ag-cell[col-id]')?.getAttribute('col-id') ?? null;
      openRowMenu(data, e.clientX, e.clientY, cellCol);
      return true;
    };
    // Through the ONE menu primitive (PR-3b): it owns the listener and the
    // editable-text ruling. The rows are still built here (they resolve
    // through AG's displayed-row index), which is what the boolean declares.
    teardowns.push(attachCtxHandler(gridEl, onGridCtx));

    // Item 55.1 — the SAME menu from a recently-accessed-VM card. The card
    // names its release (data-rel); the grid row is the menu's substrate.
    const onRecentsCtx = (e: MouseEvent): boolean => {
      const card = (e.target as HTMLElement).closest<HTMLElement>('.hc-card[data-rel]');
      if (!card) return false;
      const id = card.getAttribute('data-rel') ?? '';
      const row = allRows.find((r) => r.id === id);
      if (row) {
        openRowMenu(row, e.clientX, e.clientY, null);
        return true;
      }
      // The card's release is not in the loaded list — offer what the card
      // itself knows and disable the rest WITH the reason.
      const href = card.getAttribute('href');
      const gone = 'Release not in the loaded list';
      const ghostPinned = isPinnedRecent(id);
      openCtxMenu(e.clientX, e.clientY, [
        {
          label: 'Open Release', icon: 'arrow-up-right',
          ...(href && href !== '#' ? { run: () => window.open(href, '_blank', 'noopener,noreferrer') } : { disabled: true }),
        },
        // The pin verb stays LIVE on a ghost card: it needs only the id the
        // card itself carries — R46's card surface must not die with the
        // row join.
        {
          label: ghostPinned ? 'Unpin from Jump Back In' : 'Pin to Jump Back In',
          icon: 'pin',
          run: () => void onPinRecent(id),
        },
        { label: 'Update Framework', icon: 'circle-arrow-up', disabled: true, tip: gone },
        { label: 'Fork Release', icon: 'git-branch', disabled: true, tip: forkCapability(me, null).tip },
        { label: 'Snapshot This Moment', icon: 'camera', disabled: true, tip: pinCapability(me, null).tip },
        { label: 'Abort', icon: 'x', disabled: true, danger: true, tip: gone },
        { label: 'Deploy Test Server', icon: 'server', disabled: true, tip: gone },
      ]);
      return true;
    };
    teardowns.push(attachCtxHandler(recentsEl, onRecentsCtx));

    // Restore the persisted preset (404-tolerant). Legacy seats are a bare
    // column-state array; the machinery blob wraps it (parseGridSeatBlob).
    const preset = await jsonFetch<{ state?: unknown }>('GET', '/api/ui-state/grid-preset/home');
    if (preset.status === 200 && preset.data?.state !== undefined && preset.data.state !== null) {
      const blob = parseGridSeatBlob(preset.data.state);
      if (Array.isArray(blob.columns) && blob.columns.length) {
        // Reconciled, never applied raw: a preset predating a column would
        // otherwise exile that column past the right edge, where AG Grid
        // virtualises it out of the DOM and it is invisible forever.
        const state = reconcileColumnState(blob.columns as Array<{ colId?: string }>);
        if (state.length) gridApi!.applyColumnState({ state: state as never, applyOrder: true });
      }
      pins = blob.pins ?? null;
      if (blob.colFilters) colFilters = { ...blob.colFilters };
    }
    // Pins AFTER the columns blob: a legacy blob carries pinned:null on
    // every column and would strip the shipped defaults; the pins field
    // (or its absence = defaults) is the one authority.
    applyPins();
    // Seed AG's mirrored filter models so the header funnels tell the truth
    // after a restore (setModel writes the same state back — idempotent).
    const activeFilterCols = Object.keys(colFilters);
    for (const c of activeFilterCols) {
      void gridApi!.setColumnFilterModel(c, colFilters[c] as never).catch(() => {});
    }
    if (activeFilterCols.length) gridApi!.onFilterChanged();

    // Item 41: the column picker. One checkbox per column, resolved by
    // columnPickerEntries (live state first, ColDef default otherwise — the
    // absolute datetime variants ship hidden and are switched on here).
    // Visibility flows through the grid itself (setColumnsVisible), so
    // onColumnVisible persists it into the SAME ui_state preset every other
    // column change rides — no second persistence lane.
    const colsBtn = root.querySelector('#hcColsBtn') as HTMLButtonElement | null;
    const colsMenu = root.querySelector('#hcColsMenu') as HTMLElement | null;
    // The picker is the SHARED primitive (grid-toolbar.ts mountColumnPicker):
    // one row per column, a viewport-bound single column, dismiss wiring —
    // the same copy the admin grids mount.
    const picker = colsBtn && colsMenu
      ? mountColumnPicker({
        btn: colsBtn,
        menu: colsMenu,
        entries: () => {
          if (!gridApi) return [];
          const state = gridApi.getColumnState() as Array<{ colId?: string; hide?: boolean | null }>;
          const defs = currentDefs as Array<{ colId?: string; headerName?: string; hide?: boolean }>;
          return columnPickerEntries(defs, state);
        },
        onToggle: (colId, on) => { gridApi?.setColumnsVisible([colId], on); },
      })
      : null;
    if (picker) teardowns.push(() => picker.destroy());

    // R28 (ruling Q2's dispatched default): the toolbar Reset is the FULL
    // clear WaaG's #glReset performs — columns + pins + colFilters AND the
    // server seat — composed from the two existing verbs IN THIS ORDER:
    // clearAllFilters arms the debounced seat write; resetColumnsToDefault
    // then CANCELS that timer and, with the filters now empty, PUTs the
    // cleared seat (state: null) — one write, never a race of two. The
    // header menu's narrower entries (columns-only reset, clear-filters)
    // stay what they are; this button is the both-at-once verb.
    const resetBtn = root.querySelector('#hcReset') as HTMLButtonElement | null;
    resetBtn?.addEventListener('click', () => {
      clearAllFilters();
      resetColumnsToDefault();
    });

    /** Customize Columns from the header context menu — the SAME picker the
     * header button owns. Deferred one tick: the menu item's click is still
     * bubbling to the picker's own document dismiss handler, which would
     * close what was just opened. */
    function openColumnsPopover(): void {
      window.setTimeout(() => { picker?.open(); }, 0);
    }

    // Item 55.10 — the branch-URL template rides /api/client-config (the
    // config-seat lane; null on a seatless dev install = plain text).
    async function loadClientConfig(): Promise<void> {
      const cc = await jsonFetch<{ branch_url_template?: string | null }>('GET', '/api/client-config');
      branchTpl = cc.status === 200 ? (cc.data?.branch_url_template ?? null) : null;
      if (branchTpl) gridApi?.refreshCells({ columns: ['sourceBranch', 'mergedIntoBranch'], force: true });
    }
    void loadClientConfig();

    // The seam the appearance module used to name here — destroy the grid and
    // rebuild it with fresh params on every theme change — is RETIRED
    // (design-system-foundation): gridThemeParams() hands AG Grid var()
    // strings, so a theme flip re-skins the grid at paint time with no
    // rebuild, no column-state carry, and no re-feeding of rows.

    await Promise.all([loadRows(), refreshSlots(), loadRecents()]);

    // Cost lane: batch fetch AFTER the grid is up; refresh cells only.
    await loadCosts();

    // Live relative time. ONE interval for the whole page — the cards and every
    // grid cell carry data-rel-time and are repainted together, so "5 min ago"
    // keeps counting without a timer per cell.
    teardowns.push(startRelativeTimeTicker(root));
    // VM uptime counts upward live (1 s cadence — see UPTIME_TICK_MS's
    // rationale); same teardown discipline as the relative-time ticker.
    teardowns.push(startUptimeTicker(root));

    // Drift lane, same shape as the cost lane: fetch after the grid is up,
    // refresh only its own column.
    await loadVersions();
    await loadFleet();
    await loadPresence();

    // EVERY poller is registered for teardown. mountHome runs again on each
    // hash navigation, and an interval the router cannot clear survives it —
    // so the page accumulates one more copy of each poll per navigation.
    const slotTimer = window.setInterval(() => void refreshSlots(), 60_000);
    teardowns.push(() => window.clearInterval(slotTimer));
    // The Bridge samples every 10 s and the console sweep runs on its own
    // cadence; 30 s here keeps the waveform moving without out-polling the
    // data behind it.
    const fleetTimer = window.setInterval(() => void loadFleet(), 30_000);
    teardowns.push(() => window.clearInterval(fleetTimer));
    // Escalations move on the minute scale; the fleet cadence is plenty and
    // the server sweep behind it is cached (one Bridge read per VM per TTL).
    const scorecardTimer = window.setInterval(() => void loadScorecard(), 30_000);
    teardowns.push(() => window.clearInterval(scorecardTimer));
    // Presence moves at human speed; the fleet cadence is plenty, and the
    // server sweep behind it is local reads only (no VM fan-out).
    const presenceTimer = window.setInterval(() => void loadPresence(), 30_000);
    teardowns.push(() => window.clearInterval(presenceTimer));
    // Slower than slots on purpose: behind this read is one SSM round trip
    // per active VM, and a published bundle does not appear every minute.
    const driftTimer = window.setInterval(() => void loadVersions(), 120_000);
    teardowns.push(() => window.clearInterval(driftTimer));
    // #927 — THE COST LANE HAD NO POLLER, and it was the only live lane
    // without one. Measured on prod 2026-09-09: daag-plan-first's VM started
    // at 04:30:47Z and its first billable record reached the console at
    // 05:05:25Z, 34m38s later. A tab opened inside that window held a cost
    // map with no key for that release and kept showing nothing for the rest
    // of the session, while the uptime cell beside it ticked every second.
    // A once-fetched map behind a column that claims to be a live total is
    // the same lie as a blank: it looks current and is not.
    //
    // 60 s, and the cadence is measured rather than guessed: behind the
    // endpoint are two aggregates over 1226 cost-event rows and 19 cumulative
    // snapshots (the whole fleet, 110 releases, read 2026-09-09) — far
    // cheaper than the fleet lane already polling at 30 s. A VM pushes its
    // cumulative snapshot about every 15 min, but event rows land
    // continuously, so a minute keeps the figure honest without out-polling
    // anything behind it.
    const costTimer = window.setInterval(() => void loadCosts(), 60_000);
    teardowns.push(() => window.clearInterval(costTimer));
  })();

  // Handed back so the router can tear this mount down. Returned rather than
  // wired to an event: mountHome does not own the routing, and a mount that
  // cleans up only on a specific event cannot be reused by a caller that
  // unmounts for a different reason.
  return () => {
    for (const fn of teardowns.splice(0)) {
      try { fn(); } catch { /* one bad teardown must not strand the rest */ }
    }
  };
}
